import React, { useState, useEffect, useMemo, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";

// API
import ItemService from "../../../../services/customer_appCommonService";

import {
  //eslint-disable-next-line
  addequipmentRentExtForm,
  clearEquipmentRentNumbers,
  getEquipmentRentFormDetail,
  //eslint-disable-next-line
  setEquipmentRentDocumentNumber,
  //eslint-disable-next-line
  setEquipmentRentExtDocumentNumber,
} from "../../../../redux/feature/customer_app/customerSlice";

// Components
import Layout from "../../../../Layout/Layout";
import { InputField } from "../../../../Components/formElements/InputField";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import { ComboboxField } from "../../../../Components/formElements/ComboboxField";
import Loader from "../../../../Components/Loader";
import MaterialSelectionModalV5 from "./MaterialSearchModal";
import FailureModal from "../../../../Components/Modal/supplier_app/FailureModal";
import Constants from "../../../../common/CustomerConstants";
import ERFExtensionAccessoriesSection from "./ERFExtensionAccessoriesSection";
// Validations
import {
  getTodaysData,
  validateRequired,
  validateContactNumber,
} from "../../../../common/Validations";

// Constants & Utils
import {
  ERF_TYPES,
  PRIVILEGE_SETTINGS,
  DEFAULT_PRIVILEGE_SETTINGS,
  PAYMENT_MODES,
  PAYMENT_MODE_RULES,
} from "./ERFConfigFileConstant";

import {
  SALE_POINT_OPTIONS,
  APPROVED_BY_OPTIONS_LEVEL_ONE,
  APPROVED_BY_OPTIONS_LEVEL_TWO,
  ORDER_TYPE_OPTIONS,
  SOURCE_OF_CALL_OPTIONS,
  ID_PROOF_OPTIONS,
  DOCTOR_TYPE_OPTIONS,
  // HOSPITAL_OPTIONS,
  STATE_OPTIONS,
  IS_DIABETIC_OPTIONS,
  // MOCK_DOCTORS,
} from "./constants";

import { message, calculateDays, calculateEndDate } from "./utils";
import UpdateRemarksModal from "../Common/UpdateRemarksModal";
// import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import CommonItemLevelDatatableModal from "../../../../Components/Modal/supplier_app/CommonItemLevelDatatableModal";
import ErfExtenssionModal from "./ErfExtenssionModal";
import ERFAutocompleteSearch from "./ERFAutocompleteSearch";
import { Modal } from "react-bootstrap";
import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import CustomerConstants from "../../../../common/CustomerConstants";
import AttendanceDetailsModal from "./AttendanceDetailsModal";
import ERFExtensionPaymentSection from "./ERFExtensionPaymentSection";
// import CustomerConstants from "../../../../common/CustomerConstants";
import ERFCreditNoteSection from "./ERFCreditNoteSection";
import ERFPaymentSection from "./ERFPaymentSection";
import ERFTallyStatusSection from "./ERFTallyStatusSection";
import ERFTravelSection from "./ERFTravelSection";

const ERFExtensionDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    // Cleanup function - runs when component unmounts
    return () => {
      dispatch(clearEquipmentRentNumbers());
    };
  }, [dispatch]);
  // Redux Data
  const userData = useSelector((state) => state?.user?.data);
  const currentUserId = userData.userId || "M000";
  const currentUserName =
    userData?.firstName || userData?.name || userData?.userId || "";
  const documentNumber = useSelector(
    (state) => state?.customer?.equipmentRentForm?.documentNumber,
  );
  const equipmentNr = useSelector(
    (state) => state?.customer?.equipmentRentExtForm?.documentNumber,
  );
  // eslint-disable-next-line
  const [financeExectiveUserRoleData, setFinanceExecutiveUserRoleData] =
    useState();

  const [isSalesAdmin, setIsSalesAdmin] = useState(false);

  // eslint-disable-next-line
  const isStoreUser = useMemo(() => {
    return userData?.roles?.some((r) => r.roleId === "STORE") || false;
  }, [userData]);

  const isPCDUser = useMemo(() => {
    return userData?.roles?.some((r) => r.roleId === "SALES-PCD") || false;
  }, [userData]);

  useEffect(() => {
    if (userData?.roles) {
      const isAllowed = userData.roles.some((r) => r.roleId === "SALES-ADMIN");
      setIsSalesAdmin(isAllowed);
    }
  }, [userData]);

  useEffect(() => {
    if (userData?.roles) {
      const allowedRoles = ["FI-EXECUTIVE"];
      const isAllowed = userData?.roles.some((r) =>
        allowedRoles.includes(r.roleId),
      );
      setFinanceExecutiveUserRoleData(isAllowed);
    }
  }, [userData]);

  const getErfTypeByName = (name) => {
    return ERF_TYPES.find((type) => type.name === name);
  };

  const normalizeStatus = (status) =>
    String(status || "Open").toUpperCase() === "CLOSED" ? "Closed" : "Open";

  const isCreditNoted = (item) =>
    String(item?.creditNoteStatus || "").toLowerCase() === "stockin";

  const convertToNumbers = (obj) => {
    const converted = { ...obj };

    const numericFields = [
      "orderedQuantity",
      "sellingPrice",
      "rentPerDay",
      "rent7Days",
      "rent15Days",
      "rent30Days",
      "days",
      "netValue",
      "grossValue",
      "taxPercentage",
      "taxAmount",
      "actualSecurityAmt",
      "securityAmt",
      "masterMRP",
      "masterSellingPrice",
      "masterUnitPrice",
      "refundAmt",
      "refundDays",
      "closedGrossValue",
      "closedNetValue",
      "closedTaxAmount",
      "closedSellingPrice",
    ];

    numericFields.forEach((field) => {
      if (converted[field] !== undefined && converted[field] !== "") {
        converted[field] = parseFloat(converted[field]) || 0;
      }
    });

    return converted;
  };

  // Returns YYYY-MM-DD that is one calendar day after the input date.
  // Used for extension fromDate calculation — the new period starts on the
  // day AFTER the previous period's toDate so the patient is not billed twice
  // for the overlap day.
  const addOneDay = (dateStr) => {
    if (!dateStr) return dateStr;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().split("T")[0];
  };

  const computeClosureTotals = (rentList, nursingList, saleList = []) => {
    const fmt = (n) => parseFloat(n.toFixed(2));

    const sumItems = (items) =>
      items
        .filter(
          (i) => !i.isTotal && !i.movedToChildExtension && !isCreditNoted(i),
        )
        .reduce(
          // (acc, item) => {
          //   const isClosed = normalizeStatus(item.extensionStatus) === "Closed";

          //   if (isClosed) {
          //     acc.net +=
          //       parseFloat(item.closedNetValue ?? item.netValue ?? 0) || 0;
          //     acc.tax +=
          //       parseFloat(item.closedTaxAmount ?? item.taxAmount ?? 0) || 0;
          //     acc.gross +=
          //       parseFloat(item.closedGrossValue ?? item.grossValue ?? 0) || 0;
          //   } else {
          //     acc.net += parseFloat(item.netValue || 0);
          //     acc.tax += parseFloat(item.taxAmount || 0);
          //     acc.gross += parseFloat(item.grossValue || 0);
          //   }
          //   return acc;
          // },
          (acc, item) => {
            acc.net += parseFloat(item.netValue || 0);
            acc.tax += parseFloat(item.taxAmount || 0);
            acc.gross += parseFloat(item.grossValue || 0);
            return acc;
          },
          { net: 0, tax: 0, gross: 0 },
        );

    const rent = sumItems(rentList);
    const nursing = sumItems(nursingList);
    const sale = sumItems(saleList);
    const totalGross = rent.gross + nursing.gross + sale.gross;
    const totalGrossRounded = Math.round(totalGross);

    return {
      rentNetTotal: fmt(rent.net),
      rentTaxTotal: fmt(rent.tax),
      rentGrossTotal: fmt(rent.gross),
      nursingNetTotal: fmt(nursing.net),
      nursingTaxTotal: fmt(nursing.tax),
      nursingGrossTotal: fmt(nursing.gross),
      // lineItemNetTotal: fmt(rent.net + nursing.net),
      // lineItemTaxTotal: fmt(rent.tax + nursing.tax),
      saleNetTotal: fmt(sale.net),
      saleTaxTotal: fmt(sale.tax),
      saleGrossTotal: fmt(sale.gross),
      lineItemNetTotal: fmt(rent.net + nursing.net + sale.net),
      lineItemTaxTotal: fmt(rent.tax + nursing.tax + sale.tax),
      lineItemGrossTotal: fmt(totalGross),
      lineItemGrossRounded: totalGrossRounded,
      roundOffAmount: fmt(totalGrossRounded - totalGross),
    };
  };

  // UI State
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line
  const [edit, setEdit] = useState(true); // true = View Mode, false = Edit Mode
  const [disabled, setDisabled] = useState(true); // Controls input fields
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [getDropdownData, setDropdownData] = useState("");
  const [activeItemType, setActiveItemType] = useState("Rent");
  const [getModalShowExtenssionModal, setModalShowExtenssionModal] =
    useState(false);
  const [getExtenssionData, setExtenssionData] = useState(false);
  // eslint-disable-next-line
  const [selectedNurse, setSelectedNurse] = useState(null);
  // eslint-disable-next-line
  const [erfNurseList, setErfNurseList] = useState([]); // Dynamic Nurse List
  // eslint-disable-next-line
  const [isLoadingErfNurse, setIsLoadingErfNurse] = useState(false);
  // eslint-disable-next-line
  const [companyList, setCompanyList] = useState([]);
  // eslint-disable-next-line
  const [patientList, setPatientList] = useState([]); // Dynamic Patient List
  // Modal State
  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getErrorFields, setErrorFields] = useState([]);
  const [failureModalData] = useState({
    title: "Validation Error",
    ErrorMsg: "Please check the required fields.",
  });
  // Data State
  const [hospitalList, setHospitalList] = useState([]); // Dynamic Hospital List
  //eslint-disable-next-line
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [rentItems, setRentItems] = useState([]);
  const [saleItems, setSaleItems] = useState([]); // reused for Service Items
  const [validation, setValidation] = useState({});
  const [doctorsList, setDoctorsList] = useState([]);
  //eslint-disable-next-line
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(false);
  //eslint-disable-next-line
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const [selectedERF, setSelectedERF] = useState(null);
  const [isERFLoaded, setIsERFLoaded] = useState(false);
  const [baseStartingDate, setBaseStartingDate] = useState(""); // Original fromDate
  const [baseEndingDate, setBaseEndingDate] = useState(""); // Original toDate
  const [rentTotalAmount, setRentTotalAmount] = useState(0);
  //eslint-disable-next-line
  const [extensionHistory, setExtensionHistory] = useState({}); // Track history per line item
  const [showExtensionHistoryModal, setShowExtensionHistoryModal] =
    useState(false);
  // eslint-disable-next-line
  const [viewingRow, setViewingRow] = useState(null);
  const [selectedItemHistory, setSelectedItemHistory] = useState([]);
  const [selectedItemCode, setSelectedItemCode] = useState("");
  const [getItemLevelData, setItemLevelData] = useState([]);
  const [getItemLevelColumns, setItemLevelColumns] = useState([]);
  const [getModalTitle, setModalTitle] = useState("");
  const [itemLevelModalShow, setItemLevelModalShow] = useState(false);
  const [updateModalShow, setUpdateModalShow] = useState(false);
  // Accordion Toggles
  const [isCardVisible, setCardVisible] = useState(true);
  const [isCardVisible2, setCardVisible2] = useState(true);
  const [isCardVisible3, setCardVisible3] = useState(true);
  const [isCardVisible4, setCardVisible4] = useState(true);
  const [isCardVisible5, setCardVisible5] = useState(true);
  const [visible, setVisible] = useState({
    PAY: true,
    SEC: true,
    SMOD: true,
  });
  const [erfForType, setErfForType] = useState("Patient"); // "Patient" or "Hospital"
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [erfHospitalList, setErfHospitalList] = useState([]);
  const [isErfLoadingHospitals, setErfIsLoadingHospitals] = useState(false);
  // replecement Logic 123

  const [replacementNurseMap, setReplacementNurseMap] = useState({});

  const [backendRentItems, setBackendRentItems] = useState([]);
  const [backendNursingItems, setBackendNursingItems] = useState([]);
  const [backendAccessoryItems, setBackendAccessoryItems] = useState([]);

  const [isPCDEditing, setIsPCDEditing] = useState(false);

  const [restoreSelection, setRestoreSelection] = useState(new Set());

  const [restoreConfirmDialog, setRestoreConfirmDialog] = useState({
    show: false,
    data: null,
  });
  const [singleRestoreDialog, setSingleRestoreDialog] = useState({
    show: false,
    data: null,
  });
  const [travelHistory, setTravelHistory] = useState([]);

  const getNurseDetailsForDate = (dateStr) => {
    try {
      // Check replacement nurse map FIRST
      const hasReplacement =
        replacementNurseMap &&
        Object.keys(replacementNurseMap).length > 0 &&
        replacementNurseMap[dateStr];

      if (hasReplacement) {
        return replacementNurseMap[dateStr];
      }

      // Fall back to original nurse
      return {
        nurseName: formInputs?.nurseName || "—",
        mobileNr: formInputs?.nurseMobileNr || "—",
        userId: formInputs?.nurseUserId || "—",
        AssociateNumber: formInputs?.nurseAssociateNumber || "—",
        email: formInputs?.nurseEmail || "—",
        address: formInputs?.nurseAddress || "—",
      };
    } catch (error) {
      return {
        nurseName: formInputs?.nurseName || "—",
        mobileNr: formInputs?.nurseMobileNr || "—",
        userId: formInputs?.nurseUserId || "—",
      };
    }
  };
  const [selectedExtensionItems, setSelectedExtensionItems] = useState(() => {
    const initial = {};
    if (rentItems && rentItems.length > 0) {
      rentItems.forEach((item) => {
        if (!item.isTotal) {
          initial[item.local_id] = "Yes";
        }
      });
    }
    return initial;
  });

  const [accessoryItems, setAccessoryItems] = useState([]);

  // ----------Approval Process-----------------
  const APPROVAL_REQUIRED_CASES = [
    "SPECIAL CASE",
    "DISCOUNT",
    "SPECIAL CASE+DISCOUNT",
  ];

  const pcCallStatusOptions = [
    { id: 1, name: "Extension", code: "EXT", description: "Extension" },
    { id: 2, name: "Closed", code: "CD", description: "Closed" },
    { id: 3, name: "Partial Ext", code: "PEXT", description: "Partial Ext" },
    { id: 4, name: "Follow Up", code: "FWP", description: "Follow Up" },
    // { id: 5, name: "In Transit", code: "ITS", description: "In Transit" },
    {
      id: 6,
      name: "Early Closure",
      code: "ELCS",
      description: "Early Closure",
    },
    {
      id: 7,
      name: "Partial Early Closure",
      code: "PELCS",
      description: "Partial Early Closure",
    },
  ];
  const refundStatuses = [
    {
      id: 2,
      name: "Payment Not Recived",
      code: "PNR",
      description: "Payment Not Recived",
    },
    {
      id: 3,
      name: "Payment Recieved",
      code: "PR",
      description: "Payment Recieved",
    },
  ];

  // Main Form Data
  const [formInputs, setFormInputs] = useState({
    // Order Details
    erfType: "Select",
    erfTypeCode: "",
    erfTypeDescription: "",
    orderDate: "",
    salePoint: userData?.organisationName || "",
    costCenter: userData?.firstName || "",
    privilegeCase: "Select",
    billingName: "Patient",
    orderType: "Select",
    sourceOfCall: "Select",

    // Doctor Details
    doctorName: "",
    drType: "Select",
    drCode: "",
    hospital: "",

    // Patient Details
    patientName: "",
    contactNumber: "",
    age: "",
    weight: "",
    gender: "",
    gstNo: "",
    panNo: "",
    isDiabetic: "No",
    aadharNo: "",
    patientChallenges: "",

    // Caregiver Details
    caregiverName: "",
    caregiverRelation: "",
    idProof: "Select",
    idProofNumber: "",
    emailId: "",

    // Address
    billingAddress: "",
    billingState: "Select",
    billingPinCode: "",
    deliveryAddress: "",
    deliveryState: "Select",
    deliveryPinCode: "",

    // Payment Details (Left)
    cartage: "",
    installationCharges: "",
    receivedAmount: "",

    // Payment Breakup (Right)
    paymentMode: "Select",
    cashAmount: "",
    balanceAmtBreakup: "0",
    chequeNo: "",
    cardTransNo: "",
    bankName: "",
    receiptNo: "",

    // Security Details (Left)
    standardSecurityAmt: 0,
    receivedSecurityAmt: "",

    // Security Breakup (Right)
    securityPincode: "",
    securityAmtMode: "Select",
    securityCashAmt: "",
    balanceSecurityBreakup: "0",
    securityChequeNo: "",
    securityCardTransNo: "",
    securityBankName: "",

    // Security Mode
    securityMode: "Select",
    securityModeCash: "",
    securityModeBal: "0",
    remarks: "",

    pcCallStatus: "",
    pcCallStatusCode: "",
    pcCallStatusDesc: "",
    pcCallStatusSecondary: "",
    earlyClosureDate: "",
    earlyClosureTime: "",

    serviceAction: "",
  });

  // console.log("FormInputs", formInputs);
  const [childLifecycleStatus, setChildLifecycleStatus] = useState(null);

  useEffect(() => {
    const childNr = formInputs.childExtensionNr;
    if (!childNr) {
      setChildLifecycleStatus(null);
      return;
    }

    // Children created via PEXT/EXT are always extension documents.
    // ERFExtDetail is the query for extension records.
    const isExtensionDoc =
      childNr.startsWith("EX") || childNr.includes("ERFEXT");
    const queryName = isExtensionDoc ? "ERFExtDetail" : "ERFDetail";

    let cancelled = false;
    (async () => {
      try {
        const res = await ItemService.generateQuery({
          queryName,
          documentNumber: childNr,
          timeZone: "Asia/Calcutta",
        });
        if (cancelled) return;
        const child =
          (Array.isArray(res?.data) && res.data[0]) ||
          (Array.isArray(res?.data?.data) && res.data.data[0]) ||
          null;
        setChildLifecycleStatus(child?.extensionLifecycleStatus || "ACTIVE");
      } catch {
        if (!cancelled) setChildLifecycleStatus(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formInputs.childExtensionNr]);

  const childIsLeaf = childLifecycleStatus === "ACTIVE";
  // eslint-disable-next-line
  const childIsExtended = childLifecycleStatus === "EXTENDED";

  // Derive all UI states from just these 2 fields
  const lifecycleStatus = formInputs.extensionLifecycleStatus;
  const isActive = !lifecycleStatus || lifecycleStatus === "ACTIVE";
  const isExtended = lifecycleStatus === "EXTENDED";

  // eslint-disable-next-line
  const isTerminal = lifecycleStatus === "CLOSED";

  const [isLatestExtension, setIsLatestExtension] = useState(true); // default true
  const [wantToRefund, setWantToRefund] = useState(false);

  const canEditOnThisRecord =
    isLatestExtension ||
    (formInputs.extensionLifecycleStatus === "EXTENDED" && childIsLeaf);

  const canLoggedInUserClearRefundIndicator =
    isSalesAdmin || isPCDUser || financeExectiveUserRoleData;

  const disableCreateExtension =
    !["EXT", "PEXT"].includes(formInputs.pcCallStatusCode) ||
    (!formInputs.days && !formInputs.endingDate) ||
    !isLatestExtension;

  // Show child record link when extended
  const showChildLink = isExtended && formInputs.childExtensionNr;

  const handlePCCallStatusChange = (name, value, code, description) => {
    if (!code) {
      return;
    }
    if (code === formInputs.pcCallStatusCode) {
      return;
    }

    setRestoreSelection(new Set());

    const now = new Date();
    const currentDate = now.toISOString().split("T")[0];
    // eslint-disable-next-line
    const currentTime = now.toTimeString().slice(0, 5);

    // ── 1. Reset form fields ─────────────────────────────────────────────────
    setFormInputs((prev) => {
      const newData = {
        ...prev,
        pcCallStatus: value,
        pcCallStatusCode: code,
        pcCallStatusDesc: description,
        // Always reset pending extension inputs so stale days/endingDate don't leak across modes
        days: "",
        endingDate: baseEndingDate || prev.endingDate,
      };
      if (code === "ELCS" || code === "PELCS") {
        // newData.earlyClosureDate = currentDate;
        // newData.earlyClosureTime = currentTime;
        newData.earlyClosureDate = prev.startingDate || currentDate;
        newData.earlyClosureTime = "09:00";
      } else {
        newData.earlyClosureDate = "";
        newData.earlyClosureTime = "";
        newData.refundDays = "";
        newData.refundAmount = "";
        newData.refundType = "";
        newData.refundRemarks = "";
      }
      return newData;
    });

    // const resetAmounts = (items) =>
    //   items.map((item) => ({
    //     ...item,
    //     grossValueInput: undefined,
    //     _refundAmt: undefined,
    //     _refundDays: undefined,
    //     closedGrossValue: undefined,
    //     closedNetValue: undefined,
    //     closedTaxAmount: undefined,
    //     itemEarlyClosureDate: "",
    //     itemEarlyClosureTime: "",
    //   }));
    const resetAmounts = (items) =>
      items.map((item) =>
        isFrozen(item)
          ? item // frozen → leave exactly as loaded (keeps day-5 backups safe)
          : {
              ...item,
              grossValueInput: undefined,
              _refundAmt: undefined,
              _refundDays: undefined,
              // keep closedGrossValue / closedNetValue / closedTaxAmount
              // keep itemEarlyClosureDate / itemEarlyClosureTime
            },
      );
    setRentItems(resetAmounts(backendRentItems));
    setNursingItems(resetAmounts(backendNursingItems));
    setAccessoryItems(resetAmounts(backendAccessoryItems));

    const updateRowStatuses = (itemsArray, newStatus) =>
      itemsArray.map((item) => {
        if (item.isTotal) return item;
        if (item.productReturned) return item;
        if (item.movedToChildExtension) return item;
        // Don't auto-open items that were backend-saved as Closed but not returned
        // These are in limbo — user must explicitly restore them
        const isBackendClosed = backendRentItems.find(
          (b) => b.local_id === item.local_id,
        )?.extensionStatus;
        if (
          normalizeStatus(isBackendClosed) === "Closed" &&
          !item.productReturned &&
          newStatus === "Open"
        ) {
          return item; // keep as Closed — user must restore manually
        }
        return { ...item, extensionStatus: newStatus };
      });

    const updateAllAccessoryStatuses = (newStatus) => {
      setAccessoryItems((prev) =>
        prev.map((acc) => {
          if (acc.productReturned) return acc;
          if (acc.movedToChildExtension) return acc;

          const isClosed = newStatus === "Closed";
          return {
            ...acc,
            extensionStatus: newStatus,
            previousGrossValue: acc.previousGrossValue ?? acc.grossValue,
            previousNetValue: acc.previousNetValue ?? acc.netValue,
            previousTaxAmount: acc.previousTaxAmount ?? acc.taxAmount,
            grossValue: isClosed
              ? 0
              : (acc.previousGrossValue ?? acc.grossValue),
            netValue: isClosed ? 0 : (acc.previousNetValue ?? acc.netValue),
            taxAmount: isClosed ? 0 : (acc.previousTaxAmount ?? acc.taxAmount),
            effectiveGrossValue: isClosed
              ? 0
              : (acc.previousGrossValue ?? acc.grossValue),
          };
        }),
      );
    };

    if (code === "EXT" || code === "FWP" || code === "ITS") {
      const autoSelected = {};
      rentItems.forEach((i) => {
        if (!i.isTotal) autoSelected[i.local_id] = "Yes";
      });
      nursingItems.forEach((i) => {
        if (!i.isTotal) autoSelected[i.local_id] = "Yes";
      });
      setSelectedExtensionItems(autoSelected);
      setRentItems((prev) => updateRowStatuses(prev, "Open"));
      setNursingItems((prev) => updateRowStatuses(prev, "Open"));
      updateAllAccessoryStatuses("Open"); // ← NEW
    } else if (code === "CD" || code === "ELCS") {
      const autoSelected = {};
      rentItems.forEach((i) => {
        if (!i.isTotal) autoSelected[i.local_id] = "No";
      });
      nursingItems.forEach((i) => {
        if (!i.isTotal) autoSelected[i.local_id] = "No";
      });
      setSelectedExtensionItems(autoSelected);
      setRentItems((prev) => updateRowStatuses(prev, "Closed"));
      setNursingItems((prev) => updateRowStatuses(prev, "Closed"));
      updateAllAccessoryStatuses("Closed"); // ← NEW
    } else if (code === "PEXT" || code === "PELCS") {
      const defaultSelected = {};
      rentItems.forEach((i) => {
        if (!i.isTotal) defaultSelected[i.local_id] = "Yes";
      });
      nursingItems.forEach((i) => {
        if (!i.isTotal) defaultSelected[i.local_id] = "Yes";
      });
      setSelectedExtensionItems(defaultSelected);
      setRentItems((prev) => updateRowStatuses(prev, "Open"));
      setNursingItems((prev) => updateRowStatuses(prev, "Open"));
      updateAllAccessoryStatuses("Open"); // ← NEW (default open, user toggles per-parent below)
    } else {
      setSelectedExtensionItems({});
    }
  };

  const handleComboboxChange = (data) => {
    const { textField, name, code, description } = data;
    if (textField === "pcCallStatus") {
      handlePCCallStatusChange(textField, name, code, description);
      return;
    } else {
      setDropdownData(data);
    }
  };

  const pcStatusCode = formInputs.pcCallStatusCode || "";

  // Enable only for EXT & PEXT
  // eslint-disable-next-line
  const isExtensionOrPartial =
    pcStatusCode === "EXT" || pcStatusCode === "PEXT";

  const disableDaysAndCharges =
    !pcStatusCode || pcStatusCode === "FWP" || pcStatusCode === "CD";

  const canEditDays = ["EXT", "PEXT"].includes(formInputs.pcCallStatusCode);

  const erfTypeOptions = useMemo(() => ERF_TYPES, []);

  const paymentModeDropdown = useMemo(
    () =>
      Object.values(PAYMENT_MODES).map((mode, i) => ({
        id: i + 1,
        name: mode,
      })),
    [],
  );

  const privilegeOptions = useMemo(() => {
    // Find the ERF object that matches the selected name
    const selectedType = ERF_TYPES.find(
      (type) => type.name === formInputs.erfType,
    );

    // Return its embedded privileges, or empty array if not found
    return selectedType?.privileges || [];
  }, [formInputs.erfType]);

  const [nursingItems, setNursingItems] = useState([]);
  // Calculate Totals
  const allItems = useMemo(
    () => [...rentItems, ...saleItems, ...nursingItems], // ← add nursingItems
    [rentItems, saleItems, nursingItems],
  );

  const totals = useMemo(() => {
    return allItems.reduce(
      (acc, item) => {
        acc.net += parseFloat(item.netValue || 0);
        acc.tax += parseFloat(item.taxAmount || 0);
        if (item.itemType === "Rent") {
          acc.security += parseFloat(item.securityAmt || 0);
        }
        return acc;
      },
      { net: 0, tax: 0, security: 0 },
    );
  }, [allItems]);

  const calculateRentTotals = (item) => {
    const qty = parseFloat(item.orderedQuantity) || 0;
    const days = parseFloat(item.days) || 0;
    const tax = parseFloat(item.taxPercentage) || 0;
    const rentSlab = item.rentSlab || "Per Day";

    let effectiveRentPrice = 0;

    switch (rentSlab) {
      case "Per Day":
        effectiveRentPrice = (parseFloat(item.rentPerDay) || 0) * days;
        break;
      case "7 Days":
        effectiveRentPrice = parseFloat(item.rent7Days) || 0;
        break;
      case "15 Days":
        effectiveRentPrice = parseFloat(item.rent15Days) || 0;
        break;
      case "30 Days":
        effectiveRentPrice = parseFloat(item.rent30Days) || 0;
        break;
      default:
        effectiveRentPrice = (parseFloat(item.rentPerDay) || 0) * days;
    }

    const netVal = qty * effectiveRentPrice;
    const taxAmt = netVal * (tax / 100);
    const grossVal = netVal + taxAmt;

    let sellingPrice = 0;
    if (rentSlab === "Per Day") {
      sellingPrice = parseFloat(item.rentPerDay) || 0;
    } else {
      sellingPrice = days > 0 ? effectiveRentPrice / days : 0;
    }

    return {
      netValue: parseFloat(netVal.toFixed(2)),
      taxAmount: parseFloat(taxAmt.toFixed(2)),
      grossValue: parseFloat(grossVal.toFixed(2)),
      sellingPrice: parseFloat(sellingPrice.toFixed(2)),
    };
  };

  // console.log("PCStatus Code", formInputs.pcCallStatusCode);

  const _isExtMode = ["EXT", "PEXT"].includes(formInputs.pcCallStatusCode);
  const _isClosureMode = ["ELCS", "PELCS", "CD"].includes(
    formInputs.pcCallStatusCode,
  );
  // EARLY closure only — drives the Early Closure Date field/column & stamping.
  // CD (closed on the normal end date) has no early-closure date.
  const _isEarlyClosureMode = ["ELCS", "PELCS"].includes(
    formInputs.pcCallStatusCode,
  );
  const _isOpenStatus = (s) => String(s || "Open").toUpperCase() !== "CLOSED";

  const _isExtensionRecord = (() => {
    const docNr = documentNumber || equipmentNr || "";
    return docNr.startsWith("EX") || docNr.includes("ERFEXT");
  })();

  // A payment entry has been recorded (Patient: real amount; Hospital: ₹0 row).
  // Use "an entry exists", NOT "amount > 0" — hospital collections are ₹0 here.
  const hasPaymentEntry =
    Array.isArray(formInputs?.paymentHistory) &&
    formInputs.paymentHistory.length > 0;

  // Hospital settlement also happens via invoice generation.
  const isInvoiced =
    formInputs.invoiceCreated === true || formInputs.extInvoiceCreated === true;

  const isPaymentCollected = formInputs.erfStatusCode === "REPAYCOLLECTED";
  const isFinanceConfirmed =
    formInputs.financeCollectionConfirmation === "Confirmed";

  // Restore lock (unchanged from your earlier decision).
  const isBillLocked = isFinanceConfirmed || isPaymentCollected;

  // Amount lock — gross must not change once collection step happened or invoiced.
  const isAmountLocked =
    isBillLocked || hasPaymentEntry || isInvoiced;

  const totalPayable = (() => {
    // PCD VIEW MODE — trust the DB-loaded value
    if (!isPCDEditing) {
      return Math.round(parseFloat(formInputs.lineItemGrossRounded) || 0);
    }

    // PCD EDIT MODE — recompute live
    let rawSum = 0;

    if (_isExtMode) {
      rawSum = [...rentItems, ...nursingItems]
        .filter(
          (i) =>
            !i.isTotal && _isOpenStatus(i.extensionStatus) && !isCreditNoted(i),
        )
        .reduce(
          (acc, i) =>
            acc +
            (parseFloat(i.netValue) || 0) +
            (parseFloat(i.taxAmount) || 0),
          0,
        );
    } else if (_isClosureMode) {
      rawSum = [...rentItems, ...nursingItems, ...saleItems]
        .filter((i) => !i.isTotal && !isCreditNoted(i))
        .reduce((acc, i) => {
          if (i.itemType === "Sale") {
            return acc + (parseFloat(i.grossValue) || 0);
          }
          // const isClosed = normalizeStatus(i.extensionStatus) === "Closed";
          // if (isClosed) {
          //   const closed =
          //     i.closedGrossValue !== undefined && i.closedGrossValue !== null
          //       ? parseFloat(i.closedGrossValue) || 0
          //       : parseFloat(i.grossValue) || 0;
          //   return acc + closed;
          // }
          // return acc + (parseFloat(i.grossValue) || 0);
          return acc + (parseFloat(i.grossValue) || 0);
        }, 0);
    } else {
      rawSum = (parseFloat(totals.net) || 0) + (parseFloat(totals.tax) || 0);
    }

    return Math.round(rawSum);
  })();

  const netAmount = !isPCDEditing
    ? (parseFloat(formInputs.netAmount) || 0).toFixed(2)
    : _isExtMode
      ? (
          Math.round(parseFloat(totalPayable) || 0) +
          (parseFloat(formInputs.visitCharges) || 0)
        ).toFixed(2)
      : (
          Math.round(parseFloat(totalPayable) || 0) +
          (parseFloat(formInputs.cartage) || 0) +
          (parseFloat(formInputs.installationCharges) || 0) +
          (parseFloat(formInputs.visitCharges) || 0)
        ).toFixed(2);

  const balanceAmount = !isPCDEditing
    ? (parseFloat(formInputs.balanceAmount) || 0).toFixed(2)
    : (
        parseFloat(netAmount) - (parseFloat(formInputs.receivedAmount) || 0)
      ).toFixed(2);

  const balanceSecurityAmt = (
    totals.security - (parseFloat(formInputs.receivedSecurityAmt) || 0)
  ).toFixed(2);

  const loadInitialPageData = useCallback(async () => {
    // Priority: URL/Props EX number > Redux documentNumber > equipmentNr
    let docNumberToUse = documentNumber || equipmentNr;

    if (!docNumberToUse) {
      toast.error("No document number found");
      return;
    }

    setLoading(true);

    try {
      // ========== DECIDE QUERY BASED ON DOCUMENT TYPE ==========
      const isExtensionRecord =
        docNumberToUse.startsWith("EX") || docNumberToUse.includes("ERFEXT");
      const queryName = isExtensionRecord ? "ERFExtDetail" : "ERFDetail";

      const payload = {
        queryName: queryName,
        documentNumber: docNumberToUse,
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      };

      const resultAction = await dispatch(getEquipmentRentFormDetail(payload));

      if (getEquipmentRentFormDetail.fulfilled.match(resultAction)) {
        const data = resultAction.payload.data?.[0];

        if (data) {
          // ========== SET FORM DATA ==========
          setFormInputs((prev) => ({ ...prev, ...data }));

          const nextRentItems =
            data.rentItems?.length > 0 ? data.rentItems : [];
          const nextNursingItems =
            data.nursingItems?.length > 0 ? data.nursingItems : [];

          setRentItems(nextRentItems);
          setNursingItems(nextNursingItems);

          // Set Sale Items
          if (data.saleItems && data.saleItems.length > 0) {
            setSaleItems(data.saleItems);
          } else if (
            data.serviceItems &&
            data.serviceItems.length > 0 &&
            data.erfType === "Service"
          ) {
            setSaleItems(data.serviceItems);
          } else if (data.items) {
            setSaleItems(
              data.items.filter(
                (i) => i.itemType !== "Rent" && i.itemType !== "Nursing",
              ),
            );
          }

          const nextAccessoryItems = Array.isArray(data?.accessoryItems)
            ? decorateAccessoryStatuses(
                data.accessoryItems,
                nextRentItems,
                nextNursingItems,
              )
            : [];
          setAccessoryItems(nextAccessoryItems);

          setBackendRentItems(JSON.parse(JSON.stringify(nextRentItems)));
          setBackendNursingItems(JSON.parse(JSON.stringify(nextNursingItems)));
          setBackendAccessoryItems(
            JSON.parse(JSON.stringify(nextAccessoryItems)),
          );

          handleDocumentMapping(data, nextNursingItems);

          // A record is NOT the latest if it already has a child extension created
          const hasChildExtension =
            !!data.childExtensionNr ||
            data.extensionLifecycleStatus === "EXTENDED";
          setIsLatestExtension(!hasChildExtension);

          // Set erfForType if available
          if (data.erfForType) {
            setErfForType(data.erfForType);
          }

          // Restore selected hospital if hospital ERF
          if (data.erfForType === "Hospital" && data.hospitalName) {
            setSelectedHospital({
              hospitalName: data.hospitalName,
              AssociateNumber: data.AssociateNumber,
              mobileNr: data.mobileNr,
              email: data.email,
            });
          }

          // console.log(" Data loading complete!");
        } else {
          // toast.error("No data found");
        }
      } else {
        toast.error("Failed to fetch ERF details");
      }
    } catch (error) {
      console.error(" Error loading details:", error);
      toast.error("Error loading ERF details");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [documentNumber, equipmentNr, dispatch]);

  const handleDocumentMapping = (data, nursingItemsList = []) => {
    // STEP 1: Set nursingData from nursingAttendance (same key for both EQ and EXT)
    if (data.nursingAttendance?.length > 0) {
      setNursingData(data.nursingAttendance);
    } else if (
      nursingItemsList.length > 0 &&
      (data.erfStartDate || data.startingDate) &&
      (data.erfEndDate || data.endingDate)
    ) {
      const startDate = data.erfStartDate || data.startingDate;
      const endDate = data.erfEndDate || data.endingDate;
      // Determine version: EXT records have extensionItemsdetails; original ERF uses "V1"
      const isExt = !!(data.erfStartDate === undefined && data.startingDate);
      const generated = nursingItemsList
        .filter((i) => !i.isTotal)
        .map((item) => {
          const extDetails = item.extensionItemsdetails || [];
          const version = isExt
            ? `E${extDetails.length > 0 ? extDetails.length - 1 : 0}`
            : data.version || "V1";
          return {
            local_id: item.local_id,
            materialCode: item.materialCode,
            materialDescription: item.materialDescription,
            barcode: item.barcode || "",
            version,
            fromDate: startDate,
            toDate: endDate,
            orderedQuantity: item.orderedQuantity,
            attendance: generateDateRange(startDate, endDate).map((day) => ({
              ...day,
              nurseName: data.nurseName || "",
              nurseMobileNr: data.nurseMobileNr || "",
              nurseUserId: data.nurseUserId || "",
              isReplacement: false,
              status: "",
            })),
          };
        });
      setNursingData(generated);
    } else {
      setNursingData([]);
    }

    const replacementMap = {};
    (data.nursingAttendance || []).forEach((item) => {
      (item.attendance || []).forEach((day) => {
        if (day.isReplacement) {
          replacementMap[day.date] = {
            nurseName: day.nurseName || "",
            mobileNr: day.nurseMobileNr || "",
            userId: day.nurseUserId || "",
            AssociateNumber: day.nurseUserId || "",
          };
        }
      });
    });
    setReplacementNurseMap(replacementMap);
  };

  // Sync the header Early Closure Date onto each closed, non-frozen item.
  // Runs only while PCD is editing in a closure flow (ELCS / PELCS).
  // Items that were already closed-and-dated in a prior save keep their own
  // date — this protects multi-stage early closures (day-5 vs day-15).
  useEffect(() => {
    // if (!_isClosureMode || !isPCDEditing) return;
    if (!_isEarlyClosureMode || !isPCDEditing) return;
    const headerDate = formInputs.earlyClosureDate || "";
    const headerTime = formInputs.earlyClosureTime || "";

    const stamp = (item, backendList) => {
      if (item.isTotal || isFrozen(item)) return item;
      if (normalizeStatus(item.extensionStatus) !== "Closed") return item;

      // Preserve a date saved in a previous session (multi-stage).
      const backend = backendList.find((b) => b.local_id === item.local_id);
      const datedInPriorSave =
        backend &&
        normalizeStatus(backend.extensionStatus) === "Closed" &&
        backend.itemEarlyClosureDate;
      if (datedInPriorSave) return item;

      if (item.itemEarlyClosureDate === headerDate) return item; // no-op guard
      return {
        ...item,
        itemEarlyClosureDate: headerDate,
        itemEarlyClosureTime: headerTime,
      };
    };

    setRentItems((prev) => prev.map((i) => stamp(i, backendRentItems)));
    setNursingItems((prev) => prev.map((i) => stamp(i, backendNursingItems)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formInputs.earlyClosureDate,
    formInputs.earlyClosureTime,
    _isEarlyClosureMode,
    isPCDEditing,
  ]);

  const checkIfLatestExtension = async (erfDocNumber, currentExtNr) => {
    if (!erfDocNumber || !currentExtNr) {
      setIsLatestExtension(true);
      return;
    }
    try {
      const obj = {
        queryName: "ExtEquipmentList",
        page: 1,
        per_page: 1000,
        timeZone: "Asia/Calcutta",
        rowFilter: { $and: [{ equipmentNr: erfDocNumber }] },
      };
      const response = await ItemService.generateQuery(obj);

      if (response?.status === 200 && Array.isArray(response.data)) {
        const alreadyHasChild = response.data.some(
          (item) => item.equipmentExtNr === currentExtNr,
        );

        setIsLatestExtension(!alreadyHasChild);
      } else {
        setIsLatestExtension(true);
      }
    } catch (err) {
      console.error("Latest check error:", err);
      setIsLatestExtension(true);
    }
  };

  const loadInitialPageDataHistory = async () => {
    const docNumberToUse = documentNumber || equipmentNr;
    if (!docNumberToUse) {
      toast.error("No document number found");
      return;
    }

    setLoading(true);

    setBaseStartingDate("");
    setBaseEndingDate("");

    try {
      const payload = {
        queryName: "ERFExtDetail",
        documentNumber: docNumberToUse,
      };
      const resultAction = await dispatch(getEquipmentRentFormDetail(payload));

      if (getEquipmentRentFormDetail.fulfilled.match(resultAction)) {
        const data = resultAction.payload.data?.[0];
        if (!data) return;

        const nextRentItems = data.rentItems?.length > 0 ? data.rentItems : [];
        const nextNursingItems =
          data.nursingItems?.length > 0 ? data.nursingItems : [];

        setRentItems(nextRentItems);
        setNursingItems(nextNursingItems);

        let latestFromDate = null; // This extension period's start
        let latestToDate = null; // This extension period's end
        let latestDays = 0;

        const allLineItems = [
          ...(data.rentItems || []),
          ...(data.nursingItems || []),
        ].filter((i) => !i.isTotal);

        allLineItems.forEach((item) => {
          if (item.extensionItemsdetails?.length > 0) {
            const lastExt =
              item.extensionItemsdetails[item.extensionItemsdetails.length - 1];
            if (lastExt.extensionToDate) {
              // Always take the most recent (latest) extension
              if (!latestToDate || lastExt.extensionToDate > latestToDate) {
                latestToDate = lastExt.extensionToDate;
                latestFromDate = lastExt.extensionFromDate; // ← THIS EXTENSION's start
                latestDays = lastExt.extendedDays || 0;
              }
            }
          }
        });

        // ── Fallback if no extensionItemsdetails (fresh EXT from old code) ────
        if (!latestFromDate) {
          // Try items' own fromDate (if prepareItem fix is applied)
          const firstItem = allLineItems[0];
          latestFromDate = firstItem?.fromDate || data.startingDate || "";
          latestToDate = firstItem?.toDate || data.endingDate || "";
          latestDays = firstItem?.days || data.days || 0;
        }

        // ── Attendance ───────────────────────────────────────────────────────
        handleDocumentMapping(data, nextNursingItems);

        // ── Set formInputs with CORRECT dates ────────────────────────────────
        setFormInputs((prev) => ({
          ...prev,
          ...data,
          // Override with correctly derived dates
          startingDate: latestFromDate, // ← This extension's start (e.g. 18-01-2026)
          endingDate: latestToDate, // ← This extension's end   (e.g. 20-01-2026)
          days: "", // ← Clear so user enters new days
          previousDays: latestDays,
        }));

        // ── Set base anchors ─────────────────────────────────────────────────
        setBaseStartingDate(latestFromDate); // Anchor: can't go before this
        setBaseEndingDate(latestToDate); // Anchor: new extension adds from here

        // ── Extension history flat list ──────────────────────────────────────
        const extensionList = [];
        data.rentItems?.forEach((item) => {
          item.extensionHistory?.forEach((ext) => {
            extensionList.push({
              ...ext,
              materialCode: item.materialCode,
              materialDescription: item.materialDescription,
              equipmentItemNr: item.equipmentItemNr,
            });
          });
        });
        setExtensionData(extensionList);

        const nextAccessoryItems = Array.isArray(data?.accessoryItems)
          ? decorateAccessoryStatuses(
              data.accessoryItems,
              nextRentItems,
              nextNursingItems,
            )
          : [];
        setAccessoryItems(nextAccessoryItems);

        setBackendRentItems(JSON.parse(JSON.stringify(nextRentItems)));
        setBackendNursingItems(JSON.parse(JSON.stringify(nextNursingItems)));
        setBackendAccessoryItems(
          JSON.parse(JSON.stringify(nextAccessoryItems)),
        );

        // ── Latest extension check ───────────────────────────────────────────
        if (!data.extensionLifecycleStatus) {
          await checkIfLatestExtension(data.equipmentNr, data.documentNumber);
        }
      } else {
        toast.error("Failed to fetch ERF details");
      }
    } catch (error) {
      console.error("Error loading details:", error);
      toast.error("Error loading ERF details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadNurse = async () => {
      if (formInputs.erfType === "Nursing Service") {
        setIsLoadingErfNurse(true);

        try {
          const payload = {
            queryName: "getSupplierList",
            module: "",
            page: 1,
            per_page: 1000,
            timeZone: "Asia/Calcutta",
            rowFilter: {
              $and: [
                {
                  "vendorCategory.vendorCategoryCode": "V13",
                },
              ],
            },
          };

          const response = await ItemService.generateQuery(payload);

          if (
            isMounted &&
            response?.status === 200 &&
            Array.isArray(response.data)
          ) {
            const formattedNurse = response.data.map((nurse) => ({
              id: nurse.userId || nurse._id,
              name: nurse.hospitalName || nurse.organisationName,
              userId: nurse.userId,
              hospitalName: nurse.hospitalName,
              organisationName: nurse.organisationName,
              AssociateNumber: nurse.AssociateNumber,
              mobileNr: nurse.mobileNr,
              email: nurse.email,
              address: nurse.address,
              address2: nurse.address2,
              city: nurse.city,
              state: nurse.state,
              cityPostalCode: nurse.cityPostalCode,
              serviceProviderCode: nurse.serviceProviderCode,
              serviceProviderName: nurse.serviceProviderName,

              certifications: nurse.certifications || [],
            }));

            setErfNurseList(formattedNurse);
          } else if (isMounted) {
            setErfNurseList([]);
          }
        } catch (err) {
          console.error("Error fetching ERF hospitals:", err);
          if (isMounted) {
            toast.error("Failed to load ERF hospital list");
            setErfNurseList([]);
          }
        } finally {
          if (isMounted) {
            setIsLoadingErfNurse(false);
          }
        }
      } else {
        if (isMounted) {
          setErfNurseList([]);
        }
      }
    };

    loadNurse();

    return () => {
      isMounted = false;
    };
  }, [formInputs.erfType]);

  useEffect(() => {
    const docNr = documentNumber || equipmentNr;
    if (!docNr) return;

    const isExtensionRecord =
      docNr.startsWith("EX") || docNr.includes("ERFEXT");

    if (isExtensionRecord) {
      loadInitialPageDataHistory(); // uses ERFExtDetail query, handles extension dates
    } else {
      loadInitialPageData(); // uses ERFDetail query for original records
    }
    // eslint-disable-next-line
  }, [documentNumber, equipmentNr]);

  useEffect(() => {
    if (!formInputs.documentNumber) return;
    // Only EXTENDED locks a record from creating a new child extension.
    // CLOSED is soft-state — patient may call back to continue rental,
    // so the record must remain editable for status changes.
    const lifecycle = formInputs.extensionLifecycleStatus;
    setIsLatestExtension(lifecycle !== "EXTENDED");
  }, [formInputs.extensionLifecycleStatus, formInputs.documentNumber]);

  // Enable/Disable Form
  useEffect(() => {
    const isEnabled =
      formInputs.erfType !== "Select" && formInputs.privilegeCase !== "Select";
    setIsFormEnabled(isEnabled);
  }, [formInputs.erfType, formInputs.privilegeCase]);

  // Handle Dropdown Selections
  useEffect(() => {
    if (getDropdownData) {
      const { textField, name } = getDropdownData;
      if (!name || name === "" || name === "Select") return;
      // eslint-disable-next-line
      const currentErfHospitalList = erfHospitalList;
      const currentErfNurseList = erfNurseList;
      // eslint-disable-next-line
      const currentCompanyList = companyList;
      // eslint-disable-next-line
      const currentDoctorsList = doctorsList;
      // eslint-disable-next-line
      const currentPatientList = patientList;

      setFormInputs((prev) => {
        const newData = { ...prev, [textField]: name };

        if (textField === "erfType") {
          const selectedErfType = getErfTypeByName(name);
          if (selectedErfType) {
            newData.erfTypeCode = selectedErfType.code;
            newData.erfTypeDescription = selectedErfType.description;
          }
          newData.privilegeCase = "Select";
        }

        // Reset Doctor Logic
        if (textField === "drType") {
          newData.hospital = "";
          newData.doctorName = "";
          newData.drCode = "";
          setHospitalList([]);
          setDoctorsList([]);

          if (name === "Hospital") {
            // User selected "Hospital" → Fetch hospital list
            fetchHospitalList();
          } else if (name === "Doctor") {
            // User selected "Doctor" → Fetch all doctors directly
            fetchAllDoctors();
          } else if (name === "Listed") {
            // Keep existing "Listed" logic if needed
            fetchHospitalList(); // Or keep your old logic
          }
        }

        if (textField === "hospital") {
          newData.doctorName = "";
          newData.drCode = "";

          if (name) {
            // Fetch doctors for this hospital
            fetchDoctorsByHospital(name);
          }
        }

        if (textField === "doctorName") {
          // Find the selected doctor from doctorsList
          const selectedDoc = doctorsList.find((doc) => doc.name === name);

          if (selectedDoc) {
            // Auto-fill doctor details
            newData.drCode = selectedDoc.userId || selectedDoc.code || "";

            // Optional: Auto-fill hospital if not already selected
            if (!newData.hospital && selectedDoc.hospitalName) {
              newData.hospital = selectedDoc.hospitalName;
            }
          }
        }

        // Reset ERF Type / Privilege
        if (textField === "erfType" || textField === "privilegeCase") {
          setRentItems([]);
          setSaleItems([]);
          newData.cartage = "";
          newData.installationCharges = "";
          newData.receivedAmount = "";
          newData.paymentMode = "Select";
          newData.cashAmount = "";
          newData.balanceAmtBreakup = "0";
          newData.chequeNo = "";
          newData.cardTransNo = "";
          newData.bankName = "";
          newData.receiptNo = "";
          newData.receivedSecurityAmt = "";
          newData.securityPincode = "";
          newData.securityAmtMode = "Select";
          newData.securityCashAmt = "";
          newData.balanceSecurityBreakup = "0";
          newData.securityChequeNo = "";
          newData.securityCardTransNo = "";
          newData.securityBankName = "";
          newData.securityMode = "Select";
          newData.securityModeCash = "";
          newData.securityModeBal = "0";

          if (textField === "erfType" && prev.erfType !== name) {
            newData.privilegeCase = "Select";
          }
        }

        // Reset Payment Mode Logic
        if (textField === "paymentMode") {
          newData.cashAmount = "";
          newData.balanceAmtBreakup = "0";
          newData.chequeNo = "";
          newData.cardTransNo = "";
          newData.bankName = "";
          newData.receiptNo = "";
        }

        // Reset Security Amt Mode Logic
        if (textField === "securityAmtMode") {
          newData.securityCashAmt = "";
          newData.balanceSecurityBreakup = "0";
          newData.securityChequeNo = "";
          newData.securityCardTransNo = "";
          newData.securityBankName = "";
        }

        // Reset Security Mode Logic
        if (textField === "securityMode") {
          newData.securityModeCash = "";
          newData.securityModeBal = "0";
        }

        // Reset ID Proof
        if (textField === "idProof") {
          newData.idProofNumber = "";
        }
        if (textField === "selectedNurseId") {
          const erfNurse = currentErfNurseList.find(
            (nurse) => nurse.name === name,
          );

          if (erfNurse) {
            setSelectedNurse(erfNurse);

            // ── Use DEDICATED nurse keys — never touch patient keys ──────────────
            newData.nurseUserId = erfNurse.userId || erfNurse.id || "";
            newData.nurseName =
              erfNurse.employeeName || erfNurse.organisationName || "";
            newData.nurseAssociateNumber = erfNurse.AssociateNumber || "";
            newData.nurseMobileNr = erfNurse.mobileNr || "";
            newData.nurseEmail = erfNurse.email || "";
            newData.nurseAddress = erfNurse.address || "";
            newData.nurseAddress2 = erfNurse.address2 || "";
            newData.nurseCity = erfNurse.city || "";
            newData.serviceProviderName = erfNurse.serviceProviderName || "";
            newData.serviceProviderCode = erfNurse.serviceProviderCode || "";

            // Pan/GST — look for documentTypeCode "100"
            const panCert = erfNurse.certifications?.find(
              (cert) => cert.documentTypeCode === "100",
            );
            newData.nursePanNo = panCert ? panCert.documentNumber : "";

            newData.nurseBillingAddress = [
              erfNurse.address,
              erfNurse.address2,
              erfNurse.city,
            ]
              .filter(Boolean)
              .join(", ");

            newData.nurseBillingState = erfNurse.state || "";
            newData.nurseBillingPinCode = erfNurse.cityPostalCode || "";

            // ── Patient fields are NOT touched ── patient data stays intact ──────
          } else {
            console.error("Nurse not found:", name);
          }
        }

        return newData;
      });
    }
    // eslint-disable-next-line
  }, [getDropdownData]);

  // Update Dynamic Balances
  useEffect(() => {
    const receivedAmount = parseFloat(formInputs.receivedAmount) || 0;
    const cashAmount = parseFloat(formInputs.cashAmount) || 0;
    const balanceAmtBreakup = (receivedAmount - cashAmount).toFixed(2);

    const receivedSecurityAmt = parseFloat(formInputs.receivedSecurityAmt) || 0;
    const securityCashAmt = parseFloat(formInputs.securityCashAmt) || 0;
    const balanceSecurityBreakup = (
      receivedSecurityAmt - securityCashAmt
    ).toFixed(2);

    const securityModeCash = parseFloat(formInputs.securityModeCash) || 0;
    const securityModeBal = (
      parseFloat(balanceSecurityAmt) - securityModeCash
    ).toFixed(2);

    setFormInputs((prev) => ({
      ...prev,
      balanceAmtBreakup,
      balanceSecurityBreakup,
      securityModeBal,
    }));
  }, [
    formInputs.receivedAmount,
    formInputs.cashAmount,
    formInputs.receivedSecurityAmt,
    formInputs.securityCashAmt,
    balanceSecurityAmt,
    formInputs.securityModeCash,
  ]);

  // Auto-fill Cash Amt for Security Mode
  useEffect(() => {
    if (!formInputs.securityMode || formInputs.securityMode === "Select")
      return;

    const balSec = parseFloat(balanceSecurityAmt) || 0;

    if (["Cash", "Euro", "Dollar"].includes(formInputs.securityMode)) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: balSec.toString(),
        securityModeBal: "0",
      }));
    } else if (
      ["Card", "Cheque", "Online", "Paytm", "UPI", "Card&Cheque"].includes(
        formInputs.securityMode,
      )
    ) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: "0",
        securityModeBal: balSec.toString(),
      }));
    } else if (["Card&Cash", "Cheque&Cash"].includes(formInputs.securityMode)) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: "",
        securityModeBal: balSec.toString(),
      }));
    }
  }, [formInputs.securityMode, balanceSecurityAmt]);

  const toggleCardVisibility = () => setCardVisible(!isCardVisible);
  const toggleCardVisibility2 = () => setCardVisible2(!isCardVisible2);
  const toggleCardVisibility3 = () => setCardVisible3(!isCardVisible3);
  const toggleCardVisibility4 = () => setCardVisible4(!isCardVisible4);
  const toggleCardVisibility5 = () => setCardVisible5(!isCardVisible5);
  const toggleVisibility = (key) =>
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  // Edit/View Handlers
  // eslint-disable-next-line
  const handleEdit = () => {
    setEdit(false);
    setDisabled(false);
  };
  // eslint-disable-next-line
  const handleCancelEdit = () => {
    setEdit(true);
    setDisabled(true);
    loadInitialPageData(); // Revert changes
  };

  // PCD-section edit toggle — independent of the legacy edit/disabled pair
  const handlePCDEdit = () => {
    setIsPCDEditing(true);
  };
  const handlePCDCancelEdit = () => {
    setIsPCDEditing(false);
    setRestoreSelection(new Set());
    loadInitialPageData(); // Revert any in-progress PCD edits
  };

  const handleInputChange = ({ target: { name, value } }) => {
    const errors = {};
    const numericFields = [
      "cartage",
      "installationCharges",
      "receivedAmount",
      "cashAmount",
      "receivedSecurityAmt",
      "securityCashAmt",
      "securityModeCash",
      "age",
      "weight",
    ];

    if (numericFields.includes(name) && parseFloat(value) < 0) {
      toast.error(`${name} cannot be negative`);
      return;
    }

    // Early Closure Date validation — must be between startingDate and endingDate
    if (name === "earlyClosureDate") {
      const { startingDate, endingDate } = formInputs;

      if (startingDate && value && value < startingDate) {
        toast.error("Early Closure Date cannot be before Starting Date");
        return;
      }

      if (endingDate && value && value > endingDate) {
        toast.error("Early Closure Date cannot be after Ending Date");
        return;
      }

      // if (value) {
      //   const stampDate = (item) => {
      //     if (item.isTotal) return item;
      //     if (item.productReturned) return item;
      //     if (item.movedToChildExtension) return item;
      //     if (normalizeStatus(item.extensionStatus) !== "Closed") return item;
      //     return { ...item, itemEarlyClosureDate: value };
      //   };
      //   setRentItems((prev) => prev.map(stampDate));
      //   setNursingItems((prev) => prev.map(stampDate));
      // }

      // setFormInputs((prev) => ({ ...prev, [name]: value }));
      // return;

      setFormInputs((prev) => ({ ...prev, [name]: value }));
      return;
    }

    setFormInputs((prev) => ({ ...prev, [name]: value }));

    if (name === "contactNumber") {
      !validateContactNumber(value)
        ? (errors.contactNumber = message.VALID_CONTACT_NUMBER)
        : (errors.contactNumber = "");
    }
    setValidation((prev) => ({ ...prev, [name]: errors[name] || "" }));
  };
  useEffect(() => {
    let isMounted = true;

    const loadHospitals = async () => {
      if (erfForType === "Hospital") {
        setErfIsLoadingHospitals(true);

        try {
          const payload = {
            queryName: "getSupplierList",
            module: "",
            page: 1,
            per_page: 1000,
            timeZone: "Asia/Calcutta",
            rowFilter: {
              $and: [
                {
                  "vendorCategory.vendorCategoryCode": "V04",
                },
              ],
            },
          };

          const response = await ItemService.generateQuery(payload);

          if (
            isMounted &&
            response?.status === 200 &&
            Array.isArray(response.data)
          ) {
            const formattedHospitals = response.data.map((hospital) => ({
              id: hospital.userId || hospital._id,
              name: hospital.hospitalName || hospital.organisationName,
              userId: hospital.userId,
              hospitalName: hospital.hospitalName,
              organisationName: hospital.organisationName,
              AssociateNumber: hospital.AssociateNumber,
              mobileNr: hospital.mobileNr,
              email: hospital.email,
              address: hospital.address,
              address2: hospital.address2,
              city: hospital.city,
              state: hospital.state,
              cityPostalCode: hospital.cityPostalCode,
              certifications: hospital.certifications || [], // Needed for GST auto-fill
            }));

            setErfHospitalList(formattedHospitals);
          } else if (isMounted) {
            setErfHospitalList([]);
          }
        } catch (err) {
          console.error("Error fetching ERF hospitals:", err);
          if (isMounted) {
            toast.error("Failed to load ERF hospital list");
            setErfHospitalList([]);
          }
        } finally {
          if (isMounted) {
            setErfIsLoadingHospitals(false);
          }
        }
      } else {
        if (isMounted) {
          setErfHospitalList([]);
        }
      }
    };

    loadHospitals();

    // Cleanup: prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [erfForType]);
  const handleERFSelection = (erfData) => {
    setSelectedERF(erfData);
    setIsERFLoaded(true);
    // Auto-fill all patient details
    setFormInputs((prev) => ({
      ...prev,
      // Patient Details
      patientName: erfData.patientName || "",
      contactNumber: erfData.contactNumber || "",
      age: erfData.age || "",
      weight: erfData.weight || "",
      gender: erfData.gender || "",
      gstNo: erfData.gstNo || "",
      panNo: erfData.panNo || "",
      isDiabetic: erfData.isDiabetic || "No",
      aadharNo: erfData.aadharNo || "",
      patientChallenges: erfData.patientChallenges || "",

      // Caregiver Details
      caregiverName: erfData.caregiverName || "",
      caregiverRelation: erfData.caregiverRelation || "",
      idProof: erfData.idProof || "Select",
      idProofNumber: erfData.idProofNumber || "",
      emailId: erfData.emailId || "",

      // Billing Address
      billingAddress: erfData.billingAddress || "",
      billingState: erfData.billingState || "Select",
      billingPinCode: erfData.billingPinCode || "",

      // Delivery Address
      deliveryAddress: erfData.deliveryAddress || "",
      deliveryState: erfData.deliveryState || "Select",
      deliveryPinCode: erfData.deliveryPinCode || "",

      // Doctor Details (optional)
      doctorName: erfData.doctorName || "",
      drType: erfData.drType || "Select",
      drCode: erfData.drCode || "",
      hospital: erfData.hospital || "",
    }));

    // Clear validation errors
    setValidation({});

    // Show success message
    toast.success(`Patient details loaded from ERF ${erfData.documentNumber}`, {
      duration: 3000,
      icon: "",
    });
  };

  useEffect(() => {
    // Helper: get the period start from an item
    const getItemPeriodStart = (item) => {
      const extDetails = item.extensionItemsdetails || [];
      if (extDetails.length > 0) {
        const latestExt = extDetails[extDetails.length - 1];
        return latestExt.extensionFromDate || item.fromDate;
      }
      return item.fromDate;
    };

    // Helper: get the period end from an item
    const getItemPeriodEnd = (item) => {
      const extDetails = item.extensionItemsdetails || [];
      if (extDetails.length > 0) {
        const latestExt = extDetails[extDetails.length - 1];
        return latestExt.extensionToDate || item.toDate;
      }
      return item.toDate;
    };

    // Helper: get the period days from an item
    const getItemPeriodDays = (item) => {
      const extDetails = item.extensionItemsdetails || [];
      if (extDetails.length > 0) {
        const latestExt = extDetails[extDetails.length - 1];
        return latestExt.extendedDays || item.days;
      }
      return item.days;
    };

    // Prefer rent items, fall back to nursing items
    // This handles all ERF types correctly
    const sourceItems = rentItems.length > 0 ? rentItems : nursingItems;
    const firstRow = sourceItems.find((i) => !i.isTotal);

    if (!firstRow) return;

    const periodStart = getItemPeriodStart(firstRow);
    const periodEnd = getItemPeriodEnd(firstRow);
    const periodDays = getItemPeriodDays(firstRow);

    // Update display values — but only set endingDate on initial load (when baseEndingDate is
    // not yet anchored). After the first load, editing item amounts must NOT reset the
    // user-entered extension ending date.
    setFormInputs((prev) => ({
      ...prev,
      ...(baseEndingDate
        ? {}
        : { endingDate: periodEnd, previousDays: periodDays }),
    }));

    // Only set base anchors if currently empty
    // loadInitialPageDataHistory resets both to "" before every fetch
    // so these will always be empty when new data arrives from a fresh load
    if (!baseStartingDate && periodStart) {
      setBaseStartingDate(periodStart);
    }
    if (!baseEndingDate && periodEnd) {
      setBaseEndingDate(periodEnd);
    }

    // eslint-disable-next-line
  }, [rentItems, nursingItems]);

  useEffect(() => {
    if (
      (rentItems && rentItems.length > 0) ||
      (nursingItems && nursingItems.length > 0)
    ) {
      setSelectedExtensionItems((prev) => {
        const updated = { ...prev };

        // Initialize rent items
        rentItems.forEach((item) => {
          if (!item.isTotal) {
            if (!updated[item.local_id]) {
              updated[item.local_id] = "Yes";
            }
          }
        });

        // Initialize nursing items
        nursingItems.forEach((item) => {
          if (!item.isTotal) {
            if (!updated[item.local_id]) {
              updated[item.local_id] = "Yes";
            }
          }
        });

        return updated;
      });
    }
  }, [rentItems, nursingItems]);

  useEffect(() => {
    setAccessoryItems((prev) =>
      decorateAccessoryStatuses(prev, rentItems, nursingItems),
    );
    // eslint-disable-next-line
  }, [rentItems, nursingItems]);

  useEffect(() => {
    if (!baseStartingDate) return;

    const isExtRecord =
      formInputs.documentNumber?.startsWith("EX") ||
      formInputs.documentNumber?.includes("ERFEXT");

    if (isExtRecord) {
      // Always overwrite with correct extension starting date
      setFormInputs((prev) => ({ ...prev, startingDate: baseStartingDate }));
    } else {
      // For original ERF — only set if not already set
      if (!formInputs.startingDate) {
        setFormInputs((prev) => ({ ...prev, startingDate: baseStartingDate }));
      }
    }
    // eslint-disable-next-line
  }, [baseStartingDate]);

  useEffect(() => {
    const { days } = formInputs;

    // This guard already exists but make sure it covers empty string too
    if (!days || Number(days) === 0 || days === "") {
      if (baseEndingDate) {
        setFormInputs((prev) => ({
          ...prev,
          endingDate: baseEndingDate, // ← reset to the base (latest extension end date)
        }));
      }
      return;
    }

    if (!baseEndingDate) return;

    const baseDate = new Date(baseEndingDate);
    const newEnd = new Date(baseDate);
    newEnd.setDate(baseDate.getDate() + Number(days));

    const formattedEndDate = newEnd.toISOString().split("T")[0];

    setFormInputs((prev) => ({
      ...prev,
      endingDate: formattedEndDate,
    }));
    // eslint-disable-next-line
  }, [formInputs.days, baseEndingDate]);

  useEffect(() => {
    setTravelHistory(formInputs.travelHistory ?? []);
  }, [formInputs.travelHistory]);

  useEffect(() => {
    if (!rentItems || rentItems.length === 0) {
      setRentTotalAmount(0);
      return;
    }

    const total = rentItems.reduce((sum, item) => {
      if (item.isTotal) return sum;

      return sum + Number(item.effectiveGrossValue ?? item.grossValue ?? 0);
    }, 0);

    setRentTotalAmount(total);
  }, [rentItems]);

  useEffect(() => {
    const visitCharges = Number(formInputs.visitCharges || 0);
    const isExtMode = ["EXT", "PEXT"].includes(formInputs.pcCallStatusCode);
    // For EXT/PEXT: sum open items' gross inline (cannot reference extensionLineTotals here
    // because that useMemo is declared later in the component — accessing it before
    // initialization throws a ReferenceError).
    let baseAmount;
    if (isExtMode) {
      const openGross = [...rentItems, ...nursingItems]
        .filter(
          (i) =>
            !i.isTotal &&
            String(i.extensionStatus || "Open").toUpperCase() !== "CLOSED" &&
            !isCreditNoted(i),
        )
        .reduce(
          (sum, i) =>
            sum +
            (parseFloat(i.effectiveGrossValue) ||
              parseFloat(i.grossValue) ||
              0),
          0,
        );
      baseAmount = Math.round(openGross);
    } else {
      // Safely parse and round the rent total
      baseAmount = Math.round(Number(rentTotalAmount) || 0);
    }

    const finalTotal = baseAmount + visitCharges;

    if (isPCDEditing) {
      setFormInputs((prev) => ({
        ...prev,
        totalAmount: finalTotal,
      }));
    }
    // eslint-disable-next-line
  }, [
    rentTotalAmount,
    formInputs.visitCharges,
    formInputs.pcCallStatusCode,
    rentItems,
    nursingItems,
    isPCDEditing,
  ]);

  const hideFailureModal = () => setShowFailureModal(false);

  // handleApproverSelection
  const handleApproverSelection = useCallback((index, selectedData) => {
    if (!selectedData || !selectedData.name || selectedData.name === "Select")
      return;

    setFormInputs((prev) => {
      const newApprovers = [...prev.approvers];

      newApprovers[index] = {
        userId: selectedData.userId || "M000",
        category: selectedData.category || "SALES",
        name: selectedData.name,
      };

      //  Check if approvers array actually changed
      const hasChanged =
        JSON.stringify(prev.approvers) !== JSON.stringify(newApprovers);
      if (!hasChanged) {
        return prev; // Don't update if nothing changed
      }

      return { ...prev, approvers: newApprovers };
    });
  }, []);

  const handleGetSelectedMaterial = (selectedData) => {
    if (!Array.isArray(selectedData) || selectedData.length === 0) return;

    const processItems = (items) =>
      items.map((item, index) => {
        const qty = 1;
        const taxPercentage = parseFloat(item.taxCode) || 0;

        let netVal = 0;
        let taxAmt = 0;
        let grossVal = 0;

        if (activeItemType === "Rent") {
          const days = 0;
          const rentPerDay = parseFloat(item.rentPerDayPrice) || 0;
          const effectiveRentPrice = rentPerDay * days;

          netVal = qty * effectiveRentPrice;
          taxAmt = netVal * (taxPercentage / 100);
          grossVal = netVal + taxAmt;

          return {
            local_id: `${Date.now()}-${index}`,

            // Material Reference
            materialCode: item.materialCode,
            materialDescription: item.materialDescription,
            barcode: item.barcode || "",

            // Reference Prices from Master - MATCHING CreateERF
            masterMRP: parseFloat(item.mrPrice) || 0,
            masterSellingPrice: parseFloat(item.salePrice) || 0,
            masterUnitPrice: parseFloat(item.unitPrice) || 0,

            // ERF Order Details
            itemType: activeItemType,
            orderedQuantity: qty,
            sellingPrice: parseFloat(item.rentPerDayPrice) || 0,

            // Rent Prices - ALL FOUR SLABS
            rentPerDay: parseFloat(item.rentPerDayPrice) || 0,
            rent7Days: parseFloat(item.rent7DaysPrice) || 0,
            rent15Days: parseFloat(item.rent15DaysPrice) || 0,
            rent30Days: parseFloat(item.rent30DaysPrice) || 0,

            // Slab fields - DEFAULT
            rentSlab: "Per Day",
            rentSlabCode: "PER_DAY",
            rentSlabDescription: "PER_DAY",

            // Date fields (for Rent)
            fromDate: getTodaysData(),
            toDate: "",
            days: 0,

            // Financial Calculations
            netValue: parseFloat(netVal.toFixed(2)),
            grossValue: parseFloat(grossVal.toFixed(2)),
            taxPercentage: taxPercentage,
            taxAmount: parseFloat(taxAmt.toFixed(2)),

            // Security (for Rent)
            actualSecurityAmt: parseFloat(item.securityAmt) || 0,
            securityAmt: parseFloat(item.securityAmt) || 0,

            // Additional
            refundAmt: 0,
            refundDays: 0,
            orderUnitOfMeasure: item.unit || "EA",
            storageLocn: "Main Store",
            currencyCode: "INR",
          };
        } else {
          // For Sale or Service
          const sellingPrice = parseFloat(item.salePrice) || 0;

          netVal = qty * sellingPrice;
          taxAmt = netVal * (taxPercentage / 100);
          grossVal = netVal + taxAmt;

          return {
            local_id: `${Date.now()}-${index}`,

            // Material Reference
            materialCode: item.materialCode,
            materialDescription: item.materialDescription,
            barcode: item.barcode || "",

            // Reference Prices from Master - MATCHING CreateERF
            masterMRP: parseFloat(item.mrPrice) || 0,
            masterSellingPrice: parseFloat(item.salePrice) || 0,
            masterUnitPrice: parseFloat(item.unitPrice) || 0,

            // ERF Order Details
            itemType: activeItemType,
            orderedQuantity: qty,
            sellingPrice: parseFloat(item.salePrice) || 0,

            // Rent Prices (even for Sale items, for consistency)
            rentPerDay: parseFloat(item.rentPerDayPrice) || 0,
            rent7Days: parseFloat(item.rent7DaysPrice) || 0,
            rent15Days: parseFloat(item.rent15DaysPrice) || 0,
            rent30Days: parseFloat(item.rent30DaysPrice) || 0,

            // Date fields
            fromDate: getTodaysData(),
            toDate: "",
            days: 0,

            // Financial Calculations
            netValue: parseFloat(netVal.toFixed(2)),
            grossValue: parseFloat(grossVal.toFixed(2)),
            taxPercentage: taxPercentage,
            taxAmount: parseFloat(taxAmt.toFixed(2)),

            // Security
            actualSecurityAmt: parseFloat(item.securityAmt) || 0,
            securityAmt: parseFloat(item.securityAmt) || 0,

            // Additional
            refundAmt: 0,
            refundDays: 0,
            orderUnitOfMeasure: item.unit || "EA",
            storageLocn: "Main Store",
            currencyCode: "INR",
          };
        }
      });

    // Duplicate checking - use barcode like CreateERF
    if (activeItemType === "Rent") {
      setRentItems((prev) => {
        const existingCodes = prev.map((item) => item.barcode);
        const duplicates = selectedData.filter((item) =>
          existingCodes.includes(item.barcode),
        );

        if (duplicates.length > 0) {
          toast.error(
            `Product(s) already exist in Rent list: ${duplicates
              .map((d) => d.materialCode)
              .join(", ")}`,
          );
          const newItems = selectedData.filter(
            (item) => !existingCodes.includes(item.barcode),
          );
          if (newItems.length === 0) return prev;
          return [...prev, ...processItems(newItems)];
        }
        return [...prev, ...processItems(selectedData)];
      });
    } else {
      setSaleItems((prev) => {
        const existingCodes = prev.map((item) => item.barcode);
        const duplicates = selectedData.filter((item) =>
          existingCodes.includes(item.barcode),
        );

        if (duplicates.length > 0) {
          toast.error(
            `Product(s) already exist in ${activeItemType} list: ${duplicates
              .map((d) => d.materialCode)
              .join(", ")}`,
          );
          const newItems = selectedData.filter(
            (item) => !existingCodes.includes(item.barcode),
          );
          if (newItems.length === 0) return prev;
          return [...prev, ...processItems(newItems)];
        }
        return [...prev, ...processItems(selectedData)];
      });
    }
    setModalShow(false);
  };

  const handleLineItemChange = (local_id, e, type) => {
    const { name, value } = e.target;

    const updateLogic = (prev) => {
      return prev.map((item) => {
        if (item.local_id === local_id) {
          let updatedItem = { ...item, [name]: value };

          // RENT LOGIC
          if (item.itemType === "Rent") {
            // ===== Date Calculations =====
            if (name === "fromDate") {
              if (updatedItem.rentSlab === "Per Day") {
                if (updatedItem.days > 0) {
                  updatedItem.toDate = calculateEndDate(
                    value,
                    updatedItem.days,
                  );
                } else if (updatedItem.toDate) {
                  updatedItem.days = calculateDays(value, updatedItem.toDate);
                }
              } else {
                // For fixed slabs, recalculate toDate
                updatedItem.toDate = calculateEndDate(value, updatedItem.days);
              }

              const calculated = calculateRentTotals(updatedItem);
              updatedItem = { ...updatedItem, ...calculated };
            }

            // ===== Days Change (only for Per Day slab) =====
            if (name === "days" && updatedItem.rentSlab === "Per Day") {
              const days = parseInt(value) || 0;
              updatedItem.toDate = calculateEndDate(updatedItem.fromDate, days);

              const calculated = calculateRentTotals(updatedItem);
              updatedItem = { ...updatedItem, ...calculated };
            }

            // ===== To Date Change (only for Per Day slab) =====
            if (name === "toDate" && updatedItem.rentSlab === "Per Day") {
              updatedItem.days = calculateDays(updatedItem.fromDate, value);

              const calculated = calculateRentTotals(updatedItem);
              updatedItem = { ...updatedItem, ...calculated };
            }

            // ===== Manual Rate Editing =====
            if (name === "sellingPrice") {
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const days = parseFloat(updatedItem.days) || 0;
              const rate = parseFloat(value) || 0;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              let netVal = 0;
              if (updatedItem.rentSlab === "Per Day") {
                // For Per Day: Rate is per-day price
                netVal = qty * rate * days;
              } else {
                // For fixed slabs: Rate represents total rent for the period
                netVal = qty * rate;
              }

              const taxAmt = netVal * (tax / 100);
              const grossVal = netVal + taxAmt;

              updatedItem.netValue = parseFloat(netVal.toFixed(2));
              updatedItem.taxAmount = parseFloat(taxAmt.toFixed(2));
              updatedItem.grossValue = parseFloat(grossVal.toFixed(2));
            }

            // ===== Quantity or Tax% Change =====
            if (
              (name === "orderedQuantity" || name === "taxPercentage") &&
              name !== "sellingPrice"
            ) {
              const calculated = calculateRentTotals(updatedItem);
              updatedItem = { ...updatedItem, ...calculated };
            }

            // ===== Security Amount Changes =====
            if (name === "securityAmt" || name === "actualSecurityAmt") {
              // Values already updated, totals will recalculate via useMemo
            }
          }
          // SALE/SERVICE LOGIC
          if (item.itemType === "Sale" || item.itemType === "Service") {
            // ===== Reverse Calculation (when Total is edited) =====
            if (name === "grossValue") {
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              const netVal = grossVal / (1 + tax / 100);
              const unitPrice = netVal / qty;
              const taxAmt = grossVal - netVal;

              updatedItem.netValue = parseFloat(netVal.toFixed(2));
              updatedItem.sellingPrice = parseFloat(unitPrice.toFixed(2));
              updatedItem.taxAmount = parseFloat(taxAmt.toFixed(2));
            }

            // ===== Tax % Changed =====
            else if (name === "taxPercentage") {
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0;
              const tax = parseFloat(value) || 0;

              const netVal = qty * price;
              const taxAmt = netVal * (tax / 100);
              const grossVal = netVal + taxAmt;

              updatedItem.netValue = parseFloat(netVal.toFixed(2));
              updatedItem.taxAmount = parseFloat(taxAmt.toFixed(2));
              updatedItem.grossValue = parseFloat(grossVal.toFixed(2));
            }

            // ===== Forward Calculation (Qty or Rate changed) =====
            else if (name === "orderedQuantity" || name === "sellingPrice") {
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              const netVal = qty * price;
              const taxAmt = netVal * (tax / 100);
              const grossVal = netVal + taxAmt;

              updatedItem.netValue = parseFloat(netVal.toFixed(2));
              updatedItem.taxAmount = parseFloat(taxAmt.toFixed(2));
              updatedItem.grossValue = parseFloat(grossVal.toFixed(2));
            }
          }

          return updatedItem;
        }
        return item;
      });
    };

    if (type === "Rent") {
      setRentItems((prev) => updateLogic(prev));
    } else {
      setSaleItems((prev) => updateLogic(prev));
    }
  };

  const addProductDetails = (type) => {
    setActiveItemType(type || formInputs.erfType);
    setModalShow(true);
  };
  const hideModal = () => setModalShow(false);

  // Fetch All Hospitals
  const fetchHospitalList = async () => {
    setIsLoadingHospitals(true);
    try {
      const payload = {
        queryName: "Get_All_Hospital_Names",
        module: "",
        page: 1,
        per_page: 3000, // Get all hospitals
        timeZone: "Asia/Calcutta",
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        setHospitalList(response.data);
      } else {
        setHospitalList([]);
      }
    } catch (err) {
      console.error("Error fetching hospitals:", err);
      toast.error("Failed to load hospital list");
      setHospitalList([]);
    } finally {
      setIsLoadingHospitals(false);
    }
  };

  // Fetch All Doctors (for "Doctor" type)
  const fetchAllDoctors = async () => {
    setIsLoadingDoctors(true);
    try {
      const payload = {
        queryName: "Get_All_Doctors",
        module: "",
        page: 1,
        per_page: 3000, // Get all doctors
        timeZone: "Asia/Calcutta",
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        // Format for dropdown
        const formattedDoctors = response.data.map((doc) => ({
          id: doc.userId,
          name: doc.doctorName,
          code: doc.userId,
          userId: doc.userId,
          doctorName: doc.doctorName,
          hospitalName: doc.hospitalName || doc.doctorHospitalName,
          description: `${doc.doctorName} - ${doc.hospitalName || doc.doctorHospitalName}`,
          ...doc, // Keep all original data
        }));

        setDoctorsList(formattedDoctors);
      } else {
        setDoctorsList([]);
      }
    } catch (err) {
      console.error("Error fetching doctors:", err);
      toast.error("Failed to load doctors list");
      setDoctorsList([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Fetch Doctors by Hospital
  const fetchDoctorsByHospital = async (hospitalName) => {
    setIsLoadingDoctors(true);
    try {
      const payload = {
        queryName: "Get_All_Doctors",
        module: "",
        page: 1,
        per_page: 3000,
        timeZone: "Asia/Calcutta",
        rowFilter: {
          $and: [
            {
              hospitalName: hospitalName,
            },
          ],
        },
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        // Format for dropdown
        const formattedDoctors = response.data.map((doc) => ({
          id: doc.userId,
          name: doc.doctorName,
          code: doc.userId,
          userId: doc.userId,
          doctorName: doc.doctorName,
          hospitalName: doc.hospitalName || doc.doctorHospitalName,
          description: `${doc.doctorName} (${doc.userId})`,
          ...doc, // Keep all original data for auto-fill
        }));

        setDoctorsList(formattedDoctors);
      } else {
        setDoctorsList([]);
        toast.info(`No doctors found for ${hospitalName}`);
      }
    } catch (err) {
      console.error("Error fetching doctors by hospital:", err);
      toast.error("Failed to load doctors for selected hospital");
      setDoctorsList([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  const getDataWithTotal = (items) => {
    if (items.length === 0) return [];

    const isExtMode = ["EXT", "PEXT"].includes(formInputs.pcCallStatusCode);
    const itemsForTotal = isExtMode
      ? items.filter(
          (i) => !i.isTotal && normalizeStatus(i.extensionStatus) !== "Closed",
        )
      : items.filter((i) => !i.isTotal);

    // const typeTotal = itemsForTotal.reduce(
    //   (acc, item) => {
    //     acc.netValue += parseFloat(item.netValue || 0);
    //     acc.grossValue += parseFloat(
    //       item.effectiveGrossValue ?? item.grossValue ?? 0,
    //     );
    //     acc.taxAmount += parseFloat(item.taxAmount || 0);
    //     if (item.itemType === "Rent") {
    //       acc.actualSecurityAmt += parseFloat(item.actualSecurityAmt || 0);
    //       acc.securityAmt += parseFloat(item.securityAmt || 0);
    //     }
    //     return acc;
    //   },
    const typeTotal = itemsForTotal.reduce(
      (acc, item) => {
        acc.netValue += parseFloat(item.netValue) || 0;
        acc.grossValue +=
          parseFloat(item.effectiveGrossValue) ||
          parseFloat(item.grossValue) ||
          0;
        acc.taxAmount += parseFloat(item.taxAmount) || 0;
        if (item.itemType === "Rent") {
          acc.actualSecurityAmt += parseFloat(item.actualSecurityAmt) || 0;
          acc.securityAmt += parseFloat(item.securityAmt) || 0;
        }
        return acc;
      },
      {
        netValue: 0,
        grossValue: 0,
        taxAmount: 0,
        actualSecurityAmt: 0,
        securityAmt: 0,
      },
    );

    const closedGrossTotal = items
      .filter(
        (i) => !i.isTotal && normalizeStatus(i.extensionStatus) === "Closed",
      )
      .reduce((sum, i) => {
        const val =
          i.closedGrossValue !== undefined && i.closedGrossValue !== null
            ? parseFloat(i.closedGrossValue) || 0
            : parseFloat(i.grossValue) || 0;
        return sum + val;
      }, 0);

    return [
      ...items, //  Just the original items, no expansion
      {
        isTotal: true,
        local_id: "total",
        netValue: typeTotal.netValue.toFixed(2),
        grossValue: typeTotal.grossValue.toFixed(2),
        taxAmount: typeTotal.taxAmount.toFixed(2),
        actualSecurityAmt: typeTotal.actualSecurityAmt.toFixed(2),
        securityAmt: typeTotal.securityAmt.toFixed(2),
        closedGrossTotal:
          closedGrossTotal > 0 ? closedGrossTotal.toFixed(2) : null,
      },
    ];
  };

  const incrementVersion = (version) => {
    // remove 'V' and convert to number
    const num = parseInt(version.replace("V", ""), 10);
    // increment and return with V prefix
    return `V${num + 1}`;
  };

  const DEFAULT_PAYMENT_SUMMARY = {
    A: 0,
    B: 0,
    C: 0,
    totalBillingReceived: 0,
    totalSecurityReceived: 0,
    totalCreditNote: 0,
    balAmt: 0,
    returnedAmount: 0,
  };

  const buildParentStatusMap = (rentList = [], nursingList = []) => {
    const map = {};
    [...rentList, ...nursingList]
      .filter((item) => !item?.isTotal)
      .forEach((item) => {
        map[item.local_id] = normalizeStatus(item.extensionStatus);
      });
    return map;
  };

  const decorateAccessoryStatuses = (
    accessories = [],
    rentList = [],
    nursingList = [],
  ) => {
    const statusMap = buildParentStatusMap(rentList, nursingList);

    return accessories.map((item) => {
      const inheritedStatus = statusMap[item.parentLocalId] || "Open";
      const isClosed = inheritedStatus === "Closed";

      return {
        ...item,
        extensionStatus: inheritedStatus,
        grossValue: isClosed ? 0 : item.grossValue || 0,
        netValue: isClosed ? 0 : item.netValue || 0,
        taxAmount: isClosed ? 0 : item.taxAmount || 0,
      };
    });
  };

  const splitAccessoriesByParentStatus = (
    accessories = [],
    rentList = [],
    nursingList = [],
  ) => {
    const statusMap = buildParentStatusMap(rentList, nursingList);

    const openAccessories = [];
    const closedAccessories = [];

    accessories.forEach((item) => {
      const inheritedStatus = statusMap[item.parentLocalId] || "Open";
      const merged = {
        ...item,
        extensionStatus: inheritedStatus,
      };

      if (inheritedStatus === "Closed") {
        closedAccessories.push(merged);
      } else {
        openAccessories.push(merged);
      }
    });

    return { openAccessories, closedAccessories };
  };

  const resetExtensionScopedFields = (payload) => ({
    ...payload,
    paymentHistory: [],
    paymentSummary: { ...DEFAULT_PAYMENT_SUMMARY },
    securityHistory: [],
    travelHistory: [], // child extension starts with no travel entries
    serviceAssignCategory: "",
    serviceAssignCategoryCode: "",
    serviceAssignCategoryDesc: "",
    serviceAssignCategoryDescription: "",
    serviceProviderCode: "",
    serviceProviderName: "",
    serviceProviderUserId: "",
    assignedServiceExecutives: [],
    shouldCollectMoney: false,
    closureReturn: null,
    pcCallStatus: "",
    pcCallStatusCode: "",
    pcCallStatusDesc: "",

    jobDoneRemarks: "",
    jobDoneStatus: "",
    jobDoneUpdatedAt: "",
    jobDoneUpdatedBy: "",
    assignDateAndTime: "",
    seTravelDetails: [],

    tallyHistory: [],
    tallyVoucherNo: "",
    creditNoteHistory: [],
  });

  // eslint-disable-next-line
  const buildClosureReturnItems = ({
    parentItems = [],
    accessoryList = [],
    currentDocNr,
    originalErfNr,
    existingClosureItems = [],
  }) => {
    const findExisting = (barcode, materialCode, equipmentItemNr) =>
      existingClosureItems.find(
        (row) =>
          row.barcode === barcode &&
          row.materialCode === materialCode &&
          String(row.equipmentItemNr || "") === String(equipmentItemNr || ""),
      );

    const buildRow = (item, isAccessory = false) => {
      const existing = findExisting(
        item.barcode,
        item.materialCode,
        item.equipmentItemNr,
      );
      if (existing) return existing;

      return {
        barcode: item.barcode || "",
        materialCode: item.materialCode || "",
        materialDescription: item.materialDescription || "",
        itemType: item.itemType || "Rent",
        orderedQuantity: item.orderedQuantity || 1,
        documentNumber: currentDocNr,
        equipmentItemNr: item.equipmentItemNr || "",
        equipmentNr: originalErfNr,
        equipmentRentNr: item.equipmentRentNr || currentDocNr,
        erfTypeCode: item.erfTypeCode || "RE",
        isAccessory,
        parentLocalId: item.parentLocalId || "",
        parentMaterialCode: item.parentMaterialCode || "",
        parentDescription: item.parentDescription || "",
        collectionStatus: "Pending",
        collectedBy: "",
        collectedAt: "",
        collectionRemarks: "",
        condition: "",
        stockInStatus: "Pending",
        stockInBy: "",
        stockInAt: "",
        stockInRemarks: "",
        qualityCheck: null,
      };
    };

    return [
      ...parentItems.map((item) => buildRow(item, false)),
      ...accessoryList.map((item) => buildRow(item, true)),
    ];
  };

  const handleUpdate = async (newRemarks) => {
    const validationErrors = {};
    let hasError = false;
    // ===== 1. Basic Fields Validation =====
    if (!validateRequired(formInputs.patientName)) {
      validationErrors.patientName = message.FILL_REQUIRED_FIELD;
      hasError = true;
    }
    if (!validateRequired(formInputs.contactNumber)) {
      validationErrors.contactNumber = message.FILL_REQUIRED_FIELD;
      hasError = true;
    }
    if (formInputs.idProof && formInputs.idProof !== "Select") {
      if (!validateRequired(formInputs.idProofNumber)) {
        validationErrors.idProofNumber = `Please enter ${formInputs.idProof} number`;
        hasError = true;
      }
    }

    // ===== 2. Approval Process Validation =====
    if (
      APPROVAL_REQUIRED_CASES.includes(formInputs.privilegeCase?.toUpperCase())
    ) {
      const requiredCount = parseInt(formInputs.approvalLevel);
      if (!requiredCount) {
        toast.error("Please select an Approval Level");
        return;
      }

      const selectedCount = formInputs.approvers.filter(
        (a) => a && a.userId,
      ).length;

      if (selectedCount < requiredCount) {
        toast.error(`Please select all ${requiredCount} required approver(s)`);
        return;
      }
    }

    // ===== 3. Specific Item Logic Validation =====
    switch (formInputs.erfType) {
      case "Sale":
        if (saleItems.length === 0) {
          toast.error("Please add at least one Sale product.");
          return;
        }
        break;
      case "Rent":
        if (rentItems.length === 0) {
          toast.error("Please add at least one Rent product.");
          return;
        }
        break;
      case "Sale & Rent":
        if (rentItems.length === 0) {
          toast.error("Please add at least one Rent product.");
          return;
        }
        if (saleItems.length === 0) {
          toast.error("Please add at least one Sale product.");
          return;
        }
        break;
      case "Service":
        if (saleItems.length === 0) {
          toast.error("Please add at least one Service item.");
          return;
        }
        break;
      case "Nursing Service":
        // No product validation needed
        break;
      default:
        toast.error("Please select a valid ERF Type.");
        return;
    }

    // ===== 4. Qty & Price Validation =====
    const finalItems = [...rentItems, ...saleItems];
    const invalidQty = finalItems.filter(
      (item) => (parseFloat(item.orderedQuantity) || 0) < 1,
    );

    if (invalidQty.length > 0) {
      toast.error("Quantity must be at least 1 for all products");
      return;
    }

    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;

    if (!privilegeSettings.allowSellBelowLRP) {
      const invalidSaleItems = saleItems.filter(
        (item) =>
          parseFloat(item.sellingPrice) < parseFloat(item.masterSellingPrice),
      );
      if (invalidSaleItems.length > 0) {
        toast.error(
          "Sale/Service Unit Price cannot be less than Master Selling Price for this Privilege Case",
        );
        return;
      }
    }

    setValidation((prev) => ({ ...prev, ...validationErrors }));

    if (hasError) {
      setErrorFields(Object.keys(validationErrors));
      setShowFailureModal(true);
      return;
    }

    setLoading(true);
    try {
      const triggerWorkflow = APPROVAL_REQUIRED_CASES.includes(
        formInputs.privilegeCase?.toUpperCase(),
      );

      // ===== 5. Convert items to numbers =====
      const processedRentItems = rentItems.map((item) =>
        convertToNumbers(item),
      );

      const processedSaleItems = saleItems.map((item) =>
        convertToNumbers(item),
      );

      // ===== 6. Construct Payload =====
      const payloadData = {
        ...formInputs,
        erfTypeCode: formInputs.erfTypeCode,
        erfTypeDescription: formInputs.erfTypeDescription,

        // Approval fields
        levelOfApproval: formInputs.approvalLevel,
        approverList: formInputs.approvers,

        // Approval Workflow Fields
        triggerWorkflow: triggerWorkflow,
        approverUserId1:
          triggerWorkflow && formInputs.approvers[0]
            ? formInputs.approvers[0].userId
            : "",
        approverUsername1:
          triggerWorkflow && formInputs.approvers[0]
            ? formInputs.approvers[0].name
            : "",
        approverUserId2:
          triggerWorkflow && formInputs.approvers[1]
            ? formInputs.approvers[1].userId
            : "",
        approverUsername2:
          triggerWorkflow && formInputs.approvers[1]
            ? formInputs.approvers[1].name
            : "",

        createdBy: currentUserId,

        // Convert all calculated amounts to numbers
        netAmount: parseFloat(netAmount),
        balanceAmount: parseFloat(balanceAmount),
        balanceSecurityAmt: parseFloat(balanceSecurityAmt),

        // Convert form input amounts to numbers
        // Cartage is 0 for EXT/PEXT — equipment is already at the customer site
        cartage: ["EXT", "PEXT"].includes(formInputs.pcCallStatusCode)
          ? 0
          : parseFloat(formInputs.cartage) || 0,
        installationCharges: parseFloat(formInputs.installationCharges) || 0,
        receivedAmount: parseFloat(formInputs.receivedAmount) || 0,
        cashAmount: parseFloat(formInputs.cashAmount) || 0,
        // Hospital ERFs never collect security amount
        receivedSecurityAmt:
          erfForType === "Hospital"
            ? 0
            : parseFloat(formInputs.receivedSecurityAmt) || 0,
        securityCashAmt: parseFloat(formInputs.securityCashAmt) || 0,
        securityModeCash: parseFloat(formInputs.securityModeCash) || 0,
        age: formInputs.age ? parseFloat(formInputs.age) : undefined,
        weight: formInputs.weight ? parseFloat(formInputs.weight) : undefined,
        replacementStartDate: formInputs.replacementStartDate,
        replacementEndDate: formInputs.replacementEndDate,
        replacementNurseName: formInputs.replacementNurseName,
        replacementNurseMobileNr: formInputs.replacementNurseMobileNr,
        replacementNurseUserId: formInputs.replacementNurseUserId,
        replacementNurseAssociateNumber:
          formInputs.replacementNurseAssociateNumber,
        replacementNurseEmail: formInputs.replacementNurseEmail,
        replacementNurseAddress: formInputs.replacementNurseAddress,

        nursingAttendance: nursingData,
        version: incrementVersion(formInputs?.version),
        updateRemarks: newRemarks,
      };

      // Delete unnecessary fields from update response
      if (payloadData.data) {
        delete payloadData.data;
      }
      if (payloadData._id) {
        delete payloadData._id;
      }

      // ===== 7. Specific Logic for Payload keys =====
      if (formInputs.erfType === "Service") {
        payloadData.rentItems = [];
        payloadData.saleItems = [];
        payloadData.serviceItems = processedSaleItems;
      } else if (formInputs.erfType === "Nursing Service") {
        payloadData.rentItems = [];
        payloadData.saleItems = [];
        payloadData.serviceItems = [];
      } else {
        payloadData.rentItems = processedRentItems;
        payloadData.saleItems = processedSaleItems;
        payloadData.serviceItems = [];
      }
      // console.log("Payload For Handle Update API", payloadData);

      await ItemService.updateEquipmentRentForm(payloadData);

      toast.success("ERF Updated Successfully");
      setEdit(true);
      setDisabled(true);
      navigate(Constants.CUSTOMER_V5_MR_ERF_LIST);
      // navigate(CustomerConstants.CUSTOMER_V5_MR_EXTENSION_ERF_LIST)
    } catch (err) {
      toast.error("Failed to update ERF");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTravelSave = async (newRows) => {
    const currentDocNr =
      formInputs.documentNumber || documentNumber || equipmentNr;
    const isExtensionRecord =
      currentDocNr?.startsWith("EX") || currentDocNr?.includes("ERFEXT");

    try {
      const updated = [...travelHistory, ...newRows];
      const payload = {
        documentNumber: currentDocNr,
        equipmentRentNr: currentDocNr,
        travelHistory: updated,
      };

      if (isExtensionRecord) {
        await ItemService.updateExtendEquipmentRent(payload);
      } else {
        await ItemService.updateEquipmentRentForm(payload);
      }

      setTravelHistory(updated);
      toast.success("Travel entries saved successfully");
    } catch {
      toast.error("Failed to save travel entries");
      throw new Error("save failed"); // re-throw so ERFTravelSection keeps newRows
    }
  };

  const handleTravelConfirmRow = async (rowId, stage, { remarks }) => {
    const currentDocNr =
      formInputs.documentNumber || documentNumber || equipmentNr;
    const isExtensionRecord =
      currentDocNr?.startsWith("EX") || currentDocNr?.includes("ERFEXT");

    try {
      const idx = travelHistory.findIndex((r) => r._id === rowId);
      if (idx === -1) return;

      const stageKey = {
        se: "seConfirmation",
        dispatch: "dispatchConfirmation",
        finance: "financeConfirmation",
      }[stage];

      const updatedRow = {
        ...travelHistory[idx],
        [stageKey]: {
          confirmed: true,
          confirmedBy: currentUserName,
          confirmedAt: new Date().toISOString(),
          remarks,
        },
      };

      const updated = [
        ...travelHistory.slice(0, idx),
        updatedRow,
        ...travelHistory.slice(idx + 1),
      ];

      const payload = {
        documentNumber: currentDocNr,
        equipmentRentNr: currentDocNr,
        travelHistory: updated,
      };

      if (isExtensionRecord) {
        await ItemService.updateExtendEquipmentRent(payload);
      } else {
        await ItemService.updateEquipmentRentForm(payload);
      }

      setTravelHistory(updated);

      const stageMessages = {
        se: "Travel entry submitted",
        dispatch: "Distance approved by Dispatch",
        finance: "Marked as paid by Finance",
      };
      toast.success(stageMessages[stage]);
    } catch {
      toast.error("Failed to confirm travel entry");
    }
  };

  const mergeStatusIntoBackendRows = (backendRows = [], workingRows = []) => {
    const workingMap = new Map(workingRows.map((row) => [row.local_id, row]));

    return backendRows.map((row) => {
      const workingRow = workingMap.get(row.local_id);
      return {
        ...convertToNumbers(row),
        extensionStatus:
          workingRow?.extensionStatus || row.extensionStatus || "Open",
        movedToChildExtension:
          workingRow?.movedToChildExtension ??
          row.movedToChildExtension ??
          false,
      };
    });
  };

  const handleUpdateExtension = async (newRemarks = []) => {
    if (!["EXT", "PEXT"].includes(formInputs.pcCallStatusCode)) {
      toast.error("Create Extension is only for Extension / Partial Extension");
      return;
    }
    if (!isLatestExtension) {
      toast.error("Extensions can only be created from the latest record");
      return;
    }

    // ── Validate dates / days ──────────────────────────────────────────────────
    const days = formInputs.days;
    const endingDateInput = formInputs.endingDate;
    if (!endingDateInput) {
      toast.error("Please enter the number of days (ending date is required)");
      return;
    }
    if (!days || Number(days) <= 0) {
      toast.error("Please enter days for the extension");
      return;
    }
    if (endingDateInput <= baseEndingDate) {
      toast.error(
        `Ending date must be after current end date (${baseEndingDate})`,
      );
      return;
    }

    // ── Validate open items have a price ──────────────────────────────────────
    const openItemsWithoutPrice = [
      ...rentItems.filter(
        (i) => !i.isTotal && normalizeStatus(i.extensionStatus) !== "Closed",
      ),
      ...nursingItems.filter(
        (i) => !i.isTotal && normalizeStatus(i.extensionStatus) !== "Closed",
      ),
    ].filter((item) => !item.sellingPrice || Number(item.sellingPrice) <= 0);
    if (openItemsWithoutPrice.length > 0) {
      toast.error(
        `Please enter price for all open items (${openItemsWithoutPrice.length} item(s) missing price)`,
      );
      return;
    }

    if (formInputs.pcCallStatusCode !== "PEXT") {
      const closedNotReturnedItems = [
        ...rentItems.filter(
          (i) =>
            !i.isTotal &&
            normalizeStatus(i.extensionStatus) === "Closed" &&
            !i.productReturned &&
            !i.movedToChildExtension,
        ),
        ...nursingItems.filter(
          (i) =>
            !i.isTotal &&
            normalizeStatus(i.extensionStatus) === "Closed" &&
            !i.productReturned &&
            !i.movedToChildExtension,
        ),
      ];
      if (closedNotReturnedItems.length > 0) {
        const names = closedNotReturnedItems
          .map((i) => i.materialDescription || i.materialCode)
          .join(", ");
        toast.error(
          `Cannot create extension — ${closedNotReturnedItems.length} item(s) are Closed but product not yet returned: ${names}. Please restore them first (check the Select checkbox and click Restore).`,
          { duration: 7000 },
        );
        return;
      }
    }

    setLoading(true);
    try {
      const now = new Date();
      const currentDate = now.toISOString().split("T")[0];
      const currentTime = now.toTimeString().slice(0, 5);
      const currentTimestamp = now.toISOString();
      const statusCode = formInputs.pcCallStatusCode;

      const originalErfNr = formInputs.equipmentNr || formInputs.documentNumber;
      const currentDocNr = formInputs.documentNumber;
      const isCurrentExt =
        formInputs.documentNumber?.startsWith("EX") ||
        formInputs.documentNumber?.includes("ERFEXT");

      const allRentItems = rentItems.filter((i) => !i.isTotal);
      const allNursingItems = nursingItems.filter((i) => !i.isTotal);
      const allAccessoryItems = decorateAccessoryStatuses(
        accessoryItems,
        allRentItems,
        allNursingItems,
      );

      const openRentItems = allRentItems.filter(
        (item) =>
          normalizeStatus(item.extensionStatus) !== "Closed" &&
          !isCreditNoted(item),
      );
      const closedRentItems = allRentItems.filter(
        (item) => normalizeStatus(item.extensionStatus) === "Closed",
      );

      const openNursingItems = allNursingItems.filter(
        (item) =>
          normalizeStatus(item.extensionStatus) !== "Closed" &&
          !isCreditNoted(item),
      );
      const closedNursingItems = allNursingItems.filter(
        (item) => normalizeStatus(item.extensionStatus) === "Closed",
      );

      // Sale accessories never go into extension — only rent accessories follow rent items
      const rentOnlyAccessories = allAccessoryItems.filter(
        (a) => a.itemType !== "Sale",
      );

      const { openAccessories, closedAccessories } =
        splitAccessoriesByParentStatus(
          rentOnlyAccessories,
          allRentItems,
          allNursingItems,
        );

      if (statusCode === "PEXT") {
        const totalOpen =
          openRentItems.length +
          openNursingItems.length +
          openAccessories.length;
        const totalClosed =
          closedRentItems.length +
          closedNursingItems.length +
          closedAccessories.length;

        if (totalOpen === 0) {
          toast.error("Partial Extension needs at least 1 Open item");
          setLoading(false);
          return;
        }
        if (totalClosed === 0) {
          toast.error("Partial Extension needs at least 1 Closed item");
          setLoading(false);
          return;
        }
      }

      if (openRentItems.length === 0 && openNursingItems.length === 0) {
        toast.error("No open rent / nursing items available for extension");
        setLoading(false);
        return;
      }

      const firstOpenItem = openRentItems[0] || openNursingItems[0];
      const firstItemHistory = firstOpenItem?.extensionItemsdetails || [];
      const lastHistoryEntry =
        firstItemHistory.length > 0
          ? firstItemHistory[firstItemHistory.length - 1]
          : null;

      const newExtStartDate = addOneDay(
        lastHistoryEntry?.extensionToDate ||
          firstOpenItem?.toDate ||
          baseEndingDate,
      );

      const prepareParentItem = (item) => {
        const converted = convertToNumbers(item);
        const extDetails = item.extensionItemsdetails || [];
        const latestExt =
          extDetails.length > 0 ? extDetails[extDetails.length - 1] : null;

        const extensionFromDate = addOneDay(
          latestExt?.extensionToDate || item.toDate || item.fromDate,
        );

        const extensionToDate = endingDateInput;
        const itemDays = parseInt(days) || 0;

        const grossValue = Number(
          item.effectiveGrossValue ?? item.grossValue ?? 0,
        );
        const netValue = Number(item.netValue ?? 0);
        const taxAmount = Number(item.taxAmount ?? 0);

        const backendSnap =
          backendRentItems.find((b) => b.local_id === item.local_id) || {};
        const prevGross =
          parseFloat(backendSnap.grossValue) ||
          parseFloat(item.grossValue) ||
          0;
        const prevNet =
          parseFloat(backendSnap.netValue) || parseFloat(item.netValue) || 0;
        const prevTax =
          parseFloat(backendSnap.taxAmount) || parseFloat(item.taxAmount) || 0;

        // Build ONE history entry per item per extension — version = E0, E1, E2 ...
        const newHistoryEntry = {
          version: `E${extDetails.length}`,
          extensionStatus: "Open",
          extensionFromDate,
          extensionToDate,
          extendedDays: itemDays,
          extensionAmount: grossValue,
          materialCode: item.materialCode,
          materialDescription: item.materialDescription,
          barcode: item.barcode,
          orderedQuantity: item.orderedQuantity,
          sellingPrice: item.sellingPrice,
          pcCallStatus: formInputs.pcCallStatus,
          timestamp: new Date().toISOString(),
          createdBy: currentUserId,
        };

        return {
          ...converted,
          extensionStatus: "Open",
          grossValue,
          effectiveGrossValue: grossValue,
          netValue,
          taxAmount,
          fromDate: extensionFromDate,
          toDate: extensionToDate,
          days: itemDays,

          previousGrossValue: prevGross,
          previousNetValue: prevNet,
          previousTaxAmount: prevTax,

          extensionItemsdetails: [...extDetails, newHistoryEntry],
          movedToChildExtension: false,

          closedGrossValue: undefined,
          closedNetValue: undefined,
          closedTaxAmount: undefined,
          itemEarlyClosureDate: "",
          itemEarlyClosureTime: "",
          refundAmt: 0,
          refundDays: 0,
        };
      };

      const prepareAccessoryItem = (item) => {
        const converted = convertToNumbers(item);
        const grossValue = Number(item.grossValue ?? 0);
        const netValue = Number(item.netValue ?? 0);
        const taxAmount = Number(item.taxAmount ?? 0);

        // ── Snapshot: find what the PARENT actually charged (backend-loaded, pre-recalc)
        const backendSnap =
          backendAccessoryItems.find((b) => b.local_id === item.local_id) || {};
        const prevGross =
          parseFloat(backendSnap.grossValue) ||
          parseFloat(item.grossValue) ||
          0;
        const prevNet =
          parseFloat(backendSnap.netValue) || parseFloat(item.netValue) || 0;
        const prevTax =
          parseFloat(backendSnap.taxAmount) || parseFloat(item.taxAmount) || 0;

        return {
          ...converted,
          isAccessory: true,
          extensionStatus: "Open",
          grossValue,
          effectiveGrossValue: grossValue,
          netValue,
          taxAmount,

          previousGrossValue: prevGross,
          previousNetValue: prevNet,
          previousTaxAmount: prevTax,

          // On the child, the accessory is NOT migrated — same as parent items.
          movedToChildExtension: false,
        };
      };

      const nursingAttendancePayload = openNursingItems.map((item) => {
        const extDetails = item.extensionItemsdetails || [];
        const version = `E${extDetails.length}`;
        const latestExt =
          extDetails.length > 0 ? extDetails[extDetails.length - 1] : null;
        // const fromDate =
        //   latestExt?.extensionToDate || item.toDate || item.fromDate;

        const fromDate = addOneDay(
          latestExt?.extensionToDate || item.toDate || item.fromDate,
        );

        const toDate = endingDateInput;
        return {
          version,
          local_id: item.local_id,
          materialCode: item.materialCode,
          materialDescription: item.materialDescription,
          barcode: item.barcode || "",
          fromDate,
          toDate,
          orderedQuantity: item.orderedQuantity,
          attendance: generateDateRange(fromDate, toDate).map((day) => ({
            ...day,
            nurseName: formInputs.nurseName || "",
            nurseMobileNr: formInputs.nurseMobileNr || "",
            nurseUserId: formInputs.nurseUserId || "",
            isReplacement: false,
            status: "",
          })),
          pcCallStatus: formInputs.pcCallStatus,
          createdBy: currentUserId,
          timestamp: new Date().toISOString(),
        };
      });

      const preparedOpenRentItems = openRentItems.map((item) =>
        prepareParentItem(item),
      );
      const preparedOpenNursingItems = openNursingItems.map((item) =>
        prepareParentItem(item),
      );
      const preparedOpenAccessories = openAccessories.map((item) =>
        prepareAccessoryItem(item),
      );

      // Use live extensionLineTotals memo — single source of truth matching what user sees on screen
      const {
        rentNetTotal,
        rentTaxTotal,
        rentGrossTotal,
        nursingNetTotal,
        nursingTaxTotal,
        nursingGrossTotal,
        lineItemNetTotal,
        lineItemTaxTotal,
        lineItemGrossTotal,
        lineItemGrossRounded,
        roundOffAmount,
      } = extensionLineTotals;

      const childNetAmount =
        extensionLineTotals.lineItemGrossRounded +
        (parseFloat(formInputs.visitCharges) || 0);

      // Child starts fresh — no payment received yet
      const childReceivedAmount = 0;
      const childBalanceAmount = parseFloat(
        (childNetAmount - childReceivedAmount).toFixed(2),
      );

      const childBasePayload = {
        ...formInputs,
        equipmentNr: originalErfNr,
        equipmentExtNr: currentDocNr,
        documentNumber: undefined,
        childExtensionNr: null,
        extensionLifecycleStatus: "ACTIVE",
        extendedStatus: "Rent Extended",
        erfStatus: "Pending Payment",
        erfStatusCode: "PAYPENDING",
        extensionType: statusCode === "PEXT" ? "Partial" : "Full",

        cartage: 0,
        rentCartage: 0,
        installationCharges: 0,

        saleNetTotal: 0,
        saleTaxTotal: 0,
        saleGrossTotal: 0,
        salesCartage: 0,

        // ── CRITICAL: Override with freshly computed values ──────────────────────
        receivedAmount: childReceivedAmount,
        netAmount: childNetAmount,
        balanceAmount: childBalanceAmount,
        // Reset payment breakup fields — child starts with no payment
        cashAmount: 0,
        balanceAmtBreakup: childNetAmount.toFixed(2),
        chequeNo: "",
        cardTransNo: "",
        bankName: "",
        receiptNo: "",

        financeCollectionConfirmation: "Not confirmed",
        tallyStatusIndicator: "Pending",
        creditNoteIndicator: null,
        refundAmountIndicator: null,

        timestamp: currentTimestamp,
        statusTimestamp: currentTimestamp,
        extensionDate: currentDate,
        extensionTime: currentTime,
        extensionTimestamp: currentTimestamp,
        startingDate: newExtStartDate,
        endingDate: formInputs.endingDate,
        days: parseFloat(formInputs.days) || 0,
        previousDays: parseFloat(formInputs.previousDays) || 0,
        visitCharges: parseFloat(formInputs.visitCharges) || 0,
        totalAmount: parseFloat(formInputs.totalAmount) || 0,
        extInvoiceCreated: false,
        invoiceCreated: false,

        // saleItems: saleItems.map(convertToNumbers),
        saleItems: [],
        rentItems: preparedOpenRentItems,
        nursingItems: preparedOpenNursingItems,
        accessoryItems: preparedOpenAccessories,

        nursingAttendance: nursingAttendancePayload,
        rentNetTotal,
        rentTaxTotal,
        rentGrossTotal,
        nursingNetTotal,
        nursingTaxTotal,
        nursingGrossTotal,
        lineItemNetTotal,
        lineItemTaxTotal,
        lineItemGrossTotal,
        lineItemGrossRounded,
        roundOffAmount,
        version: undefined,
        updateRemarks: newRemarks,
      };

      const newExtensionPayload = resetExtensionScopedFields(childBasePayload);

      delete newExtensionPayload._id;
      delete newExtensionPayload.data;

      // console.log("newExtensionPayload", newExtensionPayload);

      const createResponse =
        await ItemService.extendEquipmentRent(newExtensionPayload);
      const childDocumentNumber =
        createResponse?.data?.documentNumber ||
        createResponse?.documentNumber ||
        createResponse?.data?.data?.documentNumber;

      if (!childDocumentNumber) {
        throw new Error("Extension created response missing documentNumber");
      }

      const stampMovedFlag = (item) => ({
        ...item,
        movedToChildExtension:
          normalizeStatus(item.extensionStatus) === "Open"
            ? true
            : !!item.movedToChildExtension, // preserve any pre-existing true
      });

      const parentUpdatePayload = {
        documentNumber: currentDocNr,
        equipmentRentNr: currentDocNr,
        equipmentNr: originalErfNr,
        pcCallStatus: formInputs.pcCallStatus,
        pcCallStatusCode: statusCode,
        pcCallStatusDesc: formInputs.pcCallStatusDesc || "",
        extensionLifecycleStatus: "EXTENDED",
        childExtensionNr: childDocumentNumber || "",
        rentItems: mergeStatusIntoBackendRows(
          backendRentItems,
          allRentItems.map(stampMovedFlag),
        ),
        nursingItems: mergeStatusIntoBackendRows(
          backendNursingItems,
          allNursingItems.map(stampMovedFlag),
        ),
        accessoryItems: mergeStatusIntoBackendRows(
          backendAccessoryItems,
          allAccessoryItems
            .map((a) =>
              a.itemType === "Sale" ? { ...a, extensionStatus: "Closed" } : a,
            )
            .map(stampMovedFlag),
        ),
      };

      delete parentUpdatePayload._id;

      if (isCurrentExt) {
        // console.log("Extension Parent Payload", parentUpdatePayload);
        await ItemService.updateExtendEquipmentRent(parentUpdatePayload);
      } else {
        // console.log("ERF Parent Payload", parentUpdatePayload);
        await ItemService.updateEquipmentRentForm(parentUpdatePayload);
      }

      const extType =
        statusCode === "PEXT" ? "Partial Extension" : "Full Extension";
      const closedCount = closedRentItems.length + closedNursingItems.length;
      const closedMsg =
        closedCount > 0 ? `, ${closedCount} item(s) marked closed` : "";

      toast.success(
        `${extType} created — ${openRentItems.length + openNursingItems.length} item(s) extended${closedMsg}`,
      );

      setIsPCDEditing(false);

      navigate(CustomerConstants.CUSTOMER_V5_MR_EXTENSION_ERF_LIST);
    } catch (err) {
      toast.error(err?.message || "Failed to create extension");
      console.error("handleUpdateExtension error:", err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  const disableSubmit =
    ["CD", "FWP", "ITS", "SER", "REP"].includes(formInputs.pcCallStatusCode) ||
    !isLatestExtension;

  const disableUpdate =
    formInputs.pcCallStatusCode === "EXT" ||
    formInputs.pcCallStatusCode === "PEXT";

  const hasReopenedItemsThisSession = useMemo(() => {
    const isReopenedHere = (live, backend) => {
      const backendRow = backend?.find((b) => b.local_id === live.local_id);
      const wasClosedOnLoad =
        normalizeStatus(backendRow?.extensionStatus) === "Closed";
      return (
        wasClosedOnLoad &&
        normalizeStatus(live.extensionStatus) === "Open" &&
        !live.movedToChildExtension &&
        !live.productReturned
      );
    };
    return (
      rentItems.some((i) => isReopenedHere(i, backendRentItems)) ||
      nursingItems.some((i) => isReopenedHere(i, backendNursingItems))
    );
  }, [rentItems, nursingItems, backendRentItems, backendNursingItems]);

  // formInputs.pcCallStatusCode === "ITS";
  const statusCode = formInputs.pcCallStatusCode;
  // eslint-disable-next-line
  const isDaysMandatory = ["EXT", "PEXT"].includes(statusCode);

  const calcEndingDate = (daysInput) => {
    if (!baseEndingDate || !daysInput || parseInt(daysInput) <= 0) return;
    const base = new Date(baseEndingDate);
    base.setDate(base.getDate() + parseInt(daysInput));
    const endingDate = base.toISOString().split("T")[0];
    setFormInputs((prev) => ({ ...prev, endingDate }));
  };

  // Auto-recalculate all open item amounts when days changes (forward calc: sellingPrice × days × qty)
  const autoRecalcExtensionAmounts = useCallback((daysInput) => {
    const extDays = parseInt(daysInput) || 0;
    if (extDays <= 0) return;

    const recalcItem = (item) => {
      if (item.isTotal || normalizeStatus(item.extensionStatus) === "Closed")
        return item;
      const qty = parseFloat(item.orderedQuantity) || 1;
      const price = parseFloat(item.sellingPrice) || 0;
      const tax = parseFloat(item.taxPercentage) || 0;

      const netValue = parseFloat((price * extDays * qty).toFixed(2));
      const taxAmount = parseFloat((netValue * (tax / 100)).toFixed(2));
      const grossValue = parseFloat((netValue + taxAmount).toFixed(2));

      return {
        ...item,
        // Snapshot the original values the first time so Previous Total shows them
        previousGrossValue: item.previousGrossValue ?? item.grossValue,
        previousNetValue: item.previousNetValue ?? item.netValue,
        previousTaxAmount: item.previousTaxAmount ?? item.taxAmount,
        days: extDays, // keep item.days in sync so gross-amount reverse-calc uses correct days
        netValue,
        taxAmount,
        grossValue,
        effectiveGrossValue: grossValue,
        grossValueInput: undefined, // clear any in-progress manual edit
      };
    };

    setRentItems((prev) => prev.map(recalcItem));
    setNursingItems((prev) => prev.map(recalcItem));
  }, []);

  // Live totals — sum open rent + nursing items only (sale/service not in extension)
  const extensionLineTotals = useMemo(() => {
    const fmt = (n) => parseFloat(n.toFixed(2));
    const sumItems = (items) =>
      items
        .filter(
          (i) =>
            !i.isTotal &&
            normalizeStatus(i.extensionStatus) !== "Closed" &&
            !i.movedToChildExtension && // moved items are no longer the parent's
            !isCreditNoted(i),
        )
        .reduce(
          (acc, item) => {
            acc.net += parseFloat(item.netValue || 0);
            acc.tax += parseFloat(item.taxAmount || 0);
            acc.gross += parseFloat(item.grossValue || 0);
            return acc;
          },
          { net: 0, tax: 0, gross: 0 },
        );

    const rent = sumItems(rentItems);
    const nursing = sumItems(nursingItems);

    const totalGross = rent.gross + nursing.gross;
    const totalGrossRounded = Math.round(totalGross);

    return {
      rentNetTotal: fmt(rent.net),
      rentTaxTotal: fmt(rent.tax),
      rentGrossTotal: fmt(rent.gross),
      nursingNetTotal: fmt(nursing.net),
      nursingTaxTotal: fmt(nursing.tax),
      nursingGrossTotal: fmt(nursing.gross),
      lineItemNetTotal: fmt(rent.net + nursing.net),
      lineItemTaxTotal: fmt(rent.tax + nursing.tax),
      lineItemGrossTotal: fmt(totalGross),
      lineItemGrossRounded: totalGrossRounded,
      roundOffAmount: fmt(totalGrossRounded - totalGross),
    };
  }, [rentItems, nursingItems]);

  const [isUpdatingAttendance, setIsUpdatingAttendance] = useState(false);

  const handleUpdateAttendance = async () => {
    const endingDate = formInputs.endingDate;

    if (!endingDate) {
      toast.error("Ending date is required before updating attendance.");
      return;
    }

    const selectedNursingItems = nursingItems.filter(
      (item) =>
        !item.isTotal &&
        (selectedExtensionItems[item.local_id] === "Yes" ||
          (selectedExtensionItems[item.local_id] === undefined &&
            item.local_id)),
    );

    if (selectedNursingItems.length === 0) {
      toast.error("No nursing items selected for attendance.");
      return;
    }

    const isExtensionRecord =
      formInputs.documentNumber?.startsWith("EX") ||
      formInputs.documentNumber?.includes("ERFEXT");

    // Base payload — attendance fields only (no endingDate/days/pcCallStatus which belong to ERF update)
    const payloadData = {
      updatedBy: currentUserId,
      equipmentRentNr: formInputs.documentNumber,
      documentNumber: formInputs.documentNumber,
      replacementDetails: Object.keys(replacementNurseMap).map((dateKey) => ({
        date: dateKey,
        ...replacementNurseMap[dateKey],
      })),
    };

    payloadData.nursingAttendance = buildNursingAttendancePayload(); // reads nursingData always

    if (!payloadData.nursingAttendance.length) {
      toast.error("No nursing attendance rows available to update.");
      return;
    }

    delete payloadData._id;

    try {
      setIsUpdatingAttendance(true);
      setIsAttendanceUpdated(false);

      let apiResponse;

      if (isExtensionRecord) {
        const obj = {
          documentNumber: formInputs.documentNumber,
          equipmentRentNr: formInputs.documentNumber,
          equipmentNr: formInputs.equipmentNr,
          nursingAttendance: payloadData.nursingAttendance, // ← unified field
          replacementDetails: payloadData.replacementDetails,
          updatedBy: currentUserId,
        };
        // console.log("updateExtendEquipmentRent for Handle Attandance", obj)
        apiResponse = await ItemService.updateExtendEquipmentRent(obj);
      } else {
        const obj = {
          documentNumber: formInputs.documentNumber,
          equipmentRentNr: formInputs.documentNumber,
          equipmentNr: formInputs.equipmentNr,
          nursingAttendance: payloadData.nursingAttendance,
          replacementDetails: payloadData.replacementDetails,
          updatedBy: currentUserId,
        };
        // console.log("updateEquipmentRentForm for Handle Attandance", obj)
        apiResponse = await ItemService.updateEquipmentRentForm(obj);
      }

      if (!apiResponse) {
        throw new Error("Attendance update API returned empty response");
      }

      try {
        const docNumberToUse = formInputs.documentNumber;

        const payload = {
          queryName: isExtensionRecord ? "ERFExtDetail" : "ERFDetail",
          documentNumber: docNumberToUse,
        };
        // console.log("resultAction", payload)

        const resultAction = await dispatch(
          getEquipmentRentFormDetail(payload),
        );

        if (getEquipmentRentFormDetail.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data?.[0];

          if (data) {
            setFormInputs((prev) => ({ ...prev, ...data }));

            if (data.nursingItems?.length > 0) {
              setNursingItems(data.nursingItems);
            } else {
              setNursingItems([]);
            }

            if (data.nursingAttendance?.length > 0) {
              setNursingData(data.nursingAttendance);
            } else {
              setNursingData([]);
            }

            const dateMap = {};

            (data.nursingAttendance || []).forEach((item) => {
              (item.attendance || []).forEach((day) => {
                if (day.isReplacement) {
                  dateMap[day.date] = {
                    nurseName: day.nurseName || "",
                    mobileNr: day.nurseMobileNr || "",
                    userId: day.nurseUserId || "",
                    AssociateNumber: day.nurseUserId || "",
                  };
                }
              });
            });
            if (data.replacementDetails?.length > 0) {
              data.replacementDetails.forEach((r) => {
                dateMap[r.date] = {
                  nurseName: r.nurseName,
                  mobileNr: r.mobileNr,
                  userId: r.userId,
                  AssociateNumber: r.AssociateNumber,
                };
              });
            }
            setReplacementNurseMap(dateMap);
          }
        }
      } catch (reloadError) {
        console.warn("Error reloading ERF data:", reloadError);
      }

      setIsAttendanceUpdated(true);
      setTimeout(() => {
        setIsAttendanceUpdated(false);
      }, 3000);

      toast.success(
        `${isExtensionRecord ? "Extension" : "Nursing"} attendance updated successfully!`,
      );
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update attendance",
      );
      setIsAttendanceUpdated(false);
    } finally {
      setIsUpdatingAttendance(false);
    }
  };
  const handleMakeRefund = async () => {
    const additionalRefund = parseFloat(formInputs.additionalRefundAmount || 0);
    const productsRefund = parseFloat(formInputs.refundAmount || 0);
    const totalRefund = parseFloat(
      (productsRefund + additionalRefund).toFixed(2),
    );

    if (totalRefund <= 0) {
      toast.error("Total refund amount must be greater than 0");
      return;
    }
    if (!formInputs.refundType) {
      toast.error("Refund Type is required");
      return;
    }

    const newEntry = {
      refundDate: new Date().toISOString().split("T")[0],
      refundDays: parseFloat(formInputs.refundDays || 0),
      productsRefundAmount: productsRefund,
      additionalRefundAmount: additionalRefund,
      totalRefundAmount: totalRefund,
      refundRemarks: formInputs.refundRemarks || "",
      refundType: formInputs.refundType,
      enteredBy: userDataPC?.data?.userId || "",
      enteredAt: new Date().toISOString(),
    };

    const currentDocNr = formInputs.documentNumber;
    const isExtensionRecord =
      currentDocNr?.startsWith("EX") || currentDocNr?.includes("ERFEXT");

    const existingHistory = formInputs.refundHistory || [];
    const updatedHistory = [...existingHistory, newEntry];

    const payload = {
      documentNumber: currentDocNr,
      equipmentRentNr: currentDocNr,
      additionalRefundAmount: additionalRefund,
      totalRefundAmount: totalRefund,
      refundAmountIndicator: true,
      refundHistory: updatedHistory,
    };

    try {
      setLoading(true);
      if (isExtensionRecord) {
        await ItemService.updateExtendEquipmentRent(payload);
      } else {
        await ItemService.updateEquipmentRentForm(payload);
      }
      toast.success("Refund entry saved successfully");
      setWantToRefund(false);
      loadInitialPageData();
    } catch {
      toast.error("Failed to save refund entry");
    } finally {
      setLoading(false);
    }
  };

  const handleClearRefundIndicator = async () => {
    const currentDocNr = formInputs.documentNumber;
    const isExtensionRecord =
      currentDocNr?.startsWith("EX") || currentDocNr?.includes("ERFEXT");
    const payload = {
      documentNumber: currentDocNr,
      equipmentRentNr: currentDocNr,
      refundAmountIndicator: false,
    };
    try {
      setLoading(true);
      if (isExtensionRecord) {
        await ItemService.updateExtendEquipmentRent(payload);
      } else {
        await ItemService.updateEquipmentRentForm(payload);
      }
      toast.success("Refund indicator cleared");
      loadInitialPageData();
    } catch {
      toast.error("Failed to clear refund indicator");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEquipmentSTatus = async () => {
    const statusCode = formInputs.pcCallStatusCode;
    const updateOnlyCodes = ["CD", "FWP", "ITS", "ELCS", "PELCS"];
    if (!updateOnlyCodes.includes(statusCode)) {
      toast.error("Please use Create Extension for EXT / PEXT");
      return;
    }
    setLoading(true);

    try {
      const now = new Date();
      const timestamp = now.toISOString();
      const date = now.toISOString().split("T")[0];
      const time = now.toTimeString().slice(0, 5);

      const allRentItems = rentItems.filter((i) => !i.isTotal);
      const allNursingItems = nursingItems.filter((i) => !i.isTotal);
      const allAccessoryItems = accessoryItems || []; // ← NEW
      const allSaleItems = saleItems.filter((i) => !i.isTotal);

      const existingClosure = formInputs.closureReturn || {};
      // eslint-disable-next-line
      const existingClosureItems = existingClosure.items || [];

      const originalErfNr = formInputs.equipmentNr || formInputs.documentNumber;
      const currentDocNr = formInputs.documentNumber;

      const closedParentItems =
        statusCode === "CD" || statusCode === "ELCS"
          ? [...allRentItems, ...allNursingItems]
          : [
              ...allRentItems.filter(
                (i) => normalizeStatus(i.extensionStatus) === "Closed",
              ),
              ...allNursingItems.filter(
                (i) => normalizeStatus(i.extensionStatus) === "Closed",
              ),
            ];

      // Accessories whose parent is in closedParentItems
      const closedParentIds = new Set(closedParentItems.map((i) => i.local_id));
      //eslint-disable-next-line
      const closedAccessories = allAccessoryItems.filter((acc) =>
        closedParentIds.has(acc.parentLocalId),
      );

      const isCurrentAnExtension =
        formInputs.documentNumber?.startsWith("EX") ||
        formInputs.documentNumber?.includes("ERFEXT");

      const noOpenItems = [...allRentItems, ...allNursingItems].every(
        (i) => normalizeStatus(i.extensionStatus) === "Closed",
      );

      let newLifecycle;
      if (formInputs.extensionLifecycleStatus === "EXTENDED") {
        newLifecycle = "EXTENDED";
      } else if (
        statusCode === "CD" ||
        statusCode === "ELCS" ||
        (statusCode === "PELCS" && noOpenItems)
      ) {
        newLifecycle = "CLOSED";
      } else {
        newLifecycle = "ACTIVE";
      }

      const payload = {
        documentNumber: currentDocNr,
        equipmentRentNr: currentDocNr,
        equipmentNr: originalErfNr,
        pcCallStatus: formInputs.pcCallStatus,
        pcCallStatusCode: statusCode,
        extensionLifecycleStatus: newLifecycle,
        childExtensionNr: formInputs.childExtensionNr || null,
        timestamp,

        // // ── Rent items — preserve all amounts, persist closure draft for ELCS/PELCS ───
        // rentItems: allRentItems.map((item) => {
        //   const converted = convertToNumbers(item);
        //   delete converted._refundDays;
        //   delete converted._refundAmt;
        //   const isClosureCode = statusCode === "ELCS" || statusCode === "PELCS";
        //   const isClosed = normalizeStatus(item.extensionStatus) === "Closed";
        //   return {
        //     ...converted,
        //     extensionStatus: isClosed ? "Closed" : "Open",
        //     // ── Closure billing — only persisted for closed items under ELCS/PELCS ──
        //     ...(isClosureCode &&
        //       isClosed && {
        //         closedGrossValue:
        //           item.closedGrossValue !== undefined
        //             ? parseFloat(item.closedGrossValue) || 0
        //             : parseFloat(item.grossValue) || 0,
        //         closedNetValue:
        //           item.closedNetValue !== undefined
        //             ? parseFloat(item.closedNetValue) || 0
        //             : parseFloat(item.netValue) || 0,
        //         closedTaxAmount:
        //           item.closedTaxAmount !== undefined
        //             ? parseFloat(item.closedTaxAmount) || 0
        //             : parseFloat(item.taxAmount) || 0,
        //         itemEarlyClosureDate:
        //           item.itemEarlyClosureDate ||
        //           formInputs.earlyClosureDate ||
        //           "",
        //         itemEarlyClosureTime:
        //           item.itemEarlyClosureTime ||
        //           formInputs.earlyClosureTime ||
        //           "",
        //       }),
        //     // ── Per-item refund — PCD draft, finance team disburses separately ──────
        //     ...(isClosureCode && {
        //       refundDays:
        //         item._refundDays !== undefined
        //           ? item._refundDays || 0
        //           : converted.refundDays || 0,
        //       refundAmt:
        //         item._refundAmt !== undefined
        //           ? item._refundAmt || 0
        //           : converted.refundAmt || 0,
        //     }),
        //   };
        // }),
        rentItems: allRentItems.map((item) => {
          // Frozen items are saved exactly as loaded — never re-billed.
          if (isFrozen(item)) {
            const frozen = convertToNumbers(item);
            delete frozen._refundDays;
            delete frozen._refundAmt;
            return frozen;
          }

          const converted = convertToNumbers(item);
          delete converted._refundDays;
          delete converted._refundAmt;
          const isClosureCode = statusCode === "ELCS" || statusCode === "PELCS";
          const isClosed = normalizeStatus(item.extensionStatus) === "Closed";

          // "Previous" = the value saved in DB before this edit (page-load snapshot).
          const prev =
            backendRentItems.find((b) => b.local_id === item.local_id) || {};
          // // Back up only when PCD actually changed the gross this time.
          // const grossChanged =
          //   isClosed && Number(item.grossValue) !== Number(prev.grossValue);

          // return {
          //   ...converted,
          //   extensionStatus: isClosed ? "Closed" : "Open",
          //   // grossValue/netValue/taxAmount (in `converted`) hold what PCD typed.

          //   // // One-step backup of the PREVIOUS value, so Restore can undo it.
          //   // ...(grossChanged && {
          //   //   closedGrossValue: Number(prev.grossValue) || 0,
          //   //   closedNetValue: Number(prev.netValue) || 0,
          //   //   closedTaxAmount: Number(prev.taxAmount) || 0,
          //   // }),
          //   ...(grossChanged && {
          //     closedGrossValue: Number(prev.grossValue) || 0,
          //     closedNetValue: Number(prev.netValue) || 0,
          //     closedTaxAmount: Number(prev.taxAmount) || 0,
          //     closedSellingPrice: Number(prev.sellingPrice) || 0,
          //   }),

          return {
            ...converted,
            extensionStatus: isClosed ? "Closed" : "Open",
            // grossValue/netValue/taxAmount (in `converted`) hold what PCD typed.

            ...(isClosed
              ? {
                  closedGrossValue: Number(prev.grossValue) || 0,
                  closedNetValue: Number(prev.netValue) || 0,
                  closedTaxAmount: Number(prev.taxAmount) || 0,
                  closedSellingPrice: Number(prev.sellingPrice) || 0,
                }
              : {
                  closedGrossValue: "",
                  closedNetValue: "",
                  closedTaxAmount: "",
                  closedSellingPrice: "",
                }),

            ...(isClosureCode &&
              isClosed && {
                itemEarlyClosureDate:
                  item.itemEarlyClosureDate ||
                  formInputs.earlyClosureDate ||
                  "",
                itemEarlyClosureTime:
                  item.itemEarlyClosureTime ||
                  formInputs.earlyClosureTime ||
                  "",
              }),

            ...(isClosureCode && {
              refundDays:
                item._refundDays !== undefined
                  ? item._refundDays || 0
                  : converted.refundDays || 0,
              refundAmt:
                item._refundAmt !== undefined
                  ? item._refundAmt || 0
                  : converted.refundAmt || 0,
            }),
          };
        }),

        // // ── Nursing items ─────────────────────────────────────────────────────
        // nursingItems: allNursingItems.map((item) => {
        //   const converted = convertToNumbers(item);
        //   delete converted._refundDays;
        //   delete converted._refundAmt;
        //   const isClosureCode = statusCode === "ELCS" || statusCode === "PELCS";
        //   const isClosed = normalizeStatus(item.extensionStatus) === "Closed";
        //   return {
        //     ...converted,
        //     extensionStatus: isClosed ? "Closed" : "Open",
        //     // ── Closure billing — only persisted for closed items under ELCS/PELCS ──
        //     ...(isClosureCode &&
        //       isClosed && {
        //         closedGrossValue:
        //           item.closedGrossValue !== undefined
        //             ? parseFloat(item.closedGrossValue) || 0
        //             : parseFloat(item.grossValue) || 0,
        //         closedNetValue:
        //           item.closedNetValue !== undefined
        //             ? parseFloat(item.closedNetValue) || 0
        //             : parseFloat(item.netValue) || 0,
        //         closedTaxAmount:
        //           item.closedTaxAmount !== undefined
        //             ? parseFloat(item.closedTaxAmount) || 0
        //             : parseFloat(item.taxAmount) || 0,
        //         itemEarlyClosureDate:
        //           item.itemEarlyClosureDate ||
        //           formInputs.earlyClosureDate ||
        //           "",
        //         itemEarlyClosureTime:
        //           item.itemEarlyClosureTime ||
        //           formInputs.earlyClosureTime ||
        //           "",
        //       }),
        //     // ── Per-item refund — PCD draft, finance team disburses separately ──────
        //     ...(isClosureCode && {
        //       refundDays:
        //         item._refundDays !== undefined
        //           ? item._refundDays || 0
        //           : converted.refundDays || 0,
        //       refundAmt:
        //         item._refundAmt !== undefined
        //           ? item._refundAmt || 0
        //           : converted.refundAmt || 0,
        //     }),
        //   };
        // }),

        nursingItems: allNursingItems.map((item) => {
          // Frozen items are saved exactly as loaded — never re-billed.
          if (isFrozen(item)) {
            const frozen = convertToNumbers(item);
            delete frozen._refundDays;
            delete frozen._refundAmt;
            return frozen;
          }

          const converted = convertToNumbers(item);
          delete converted._refundDays;
          delete converted._refundAmt;
          const isClosureCode = statusCode === "ELCS" || statusCode === "PELCS";
          const isClosed = normalizeStatus(item.extensionStatus) === "Closed";

          // "Previous" = the value saved in DB before this edit (page-load snapshot).
          const prev =
            backendNursingItems.find((b) => b.local_id === item.local_id) || {};
          // // Back up only when PCD actually changed the gross this time.
          // const grossChanged =
          //   isClosed && Number(item.grossValue) !== Number(prev.grossValue);

          // return {
          //   ...converted,
          //   extensionStatus: isClosed ? "Closed" : "Open",
          //   // grossValue/netValue/taxAmount (in `converted`) hold what PCD typed.

          //   // // One-step backup of the PREVIOUS value, so Restore can undo it.
          //   // ...(grossChanged && {
          //   //   closedGrossValue: Number(prev.grossValue) || 0,
          //   //   closedNetValue: Number(prev.netValue) || 0,
          //   //   closedTaxAmount: Number(prev.taxAmount) || 0,
          //   // }),

          //   ...(grossChanged && {
          //     closedGrossValue: Number(prev.grossValue) || 0,
          //     closedNetValue: Number(prev.netValue) || 0,
          //     closedTaxAmount: Number(prev.taxAmount) || 0,
          //     closedSellingPrice: Number(prev.sellingPrice) || 0,
          //   }),

          return {
            ...converted,
            extensionStatus: isClosed ? "Closed" : "Open",

            // Closed → store a one-step backup of the last persisted value so
            // Open   → clear the backup so a reopened item carries no stale value.
            ...(isClosed
              ? {
                  closedGrossValue: Number(prev.grossValue) || 0,
                  closedNetValue: Number(prev.netValue) || 0,
                  closedTaxAmount: Number(prev.taxAmount) || 0,
                  closedSellingPrice: Number(prev.sellingPrice) || 0,
                }
              : {
                  closedGrossValue: "",
                  closedNetValue: "",
                  closedTaxAmount: "",
                  closedSellingPrice: "",
                }),

            ...(isClosureCode &&
              isClosed && {
                itemEarlyClosureDate:
                  item.itemEarlyClosureDate ||
                  formInputs.earlyClosureDate ||
                  "",
                itemEarlyClosureTime:
                  item.itemEarlyClosureTime ||
                  formInputs.earlyClosureTime ||
                  "",
              }),

            ...(isClosureCode && {
              refundDays:
                item._refundDays !== undefined
                  ? item._refundDays || 0
                  : converted.refundDays || 0,
              refundAmt:
                item._refundAmt !== undefined
                  ? item._refundAmt || 0
                  : converted.refundAmt || 0,
            }),
          };
        }),

        // ── Accessory items with cascaded extensionStatus ────────────────────
        accessoryItems: allAccessoryItems.map((acc) => ({
          ...convertToNumbers(acc),
          extensionStatus:
            normalizeStatus(acc.extensionStatus) === "Closed"
              ? "Closed"
              : "Open",
        })),

        nursingAttendance: nursingData || [],

        // ── CD: closureReturn includes parents AND their accessories ─────────
        ...(statusCode === "CD" && {
          closureDate: date,
          closureTime: time,
          closureTimestamp: timestamp,
          closureReason: formInputs.pbRemarks || formInputs.remarks,
        }),

        ...((statusCode === "ELCS" || statusCode === "PELCS") &&
          (() => {
            const closureTotals = computeClosureTotals(
              allRentItems,
              allNursingItems,
              allSaleItems,
            );
            // const newNetAmount = closureTotals.lineItemGrossRounded;

            const newNetAmount =
              closureTotals.lineItemGrossRounded +
              (parseFloat(formInputs.cartage) || 0) +
              (parseFloat(formInputs.installationCharges) || 0) +
              (parseFloat(formInputs.visitCharges) || 0);

            const totalReceived =
              parseFloat(formInputs?.paymentSummary?.totalBillingReceived) || 0;
            const newBalanceAmount = parseFloat(
              (newNetAmount - totalReceived).toFixed(2),
            );

            return {
              // Informational
              earlyClosureDate: formInputs.earlyClosureDate,
              earlyClosureTime: formInputs.earlyClosureTime,
              earlyClosureTimestamp: `${formInputs.earlyClosureDate}T${formInputs.earlyClosureTime}:00Z`,
              closureType: statusCode === "PELCS" ? "Partial" : "Full",

              // Recomputed header totals (closure-aware)
              rentNetTotal: closureTotals.rentNetTotal,
              rentTaxTotal: closureTotals.rentTaxTotal,
              rentGrossTotal: closureTotals.rentGrossTotal,
              nursingNetTotal: closureTotals.nursingNetTotal,
              nursingTaxTotal: closureTotals.nursingTaxTotal,
              nursingGrossTotal: closureTotals.nursingGrossTotal,
              lineItemNetTotal: closureTotals.lineItemNetTotal,
              lineItemTaxTotal: closureTotals.lineItemTaxTotal,
              lineItemGrossTotal: closureTotals.lineItemGrossTotal,
              lineItemGrossRounded: closureTotals.lineItemGrossRounded,
              roundOffAmount: closureTotals.roundOffAmount,
              netAmount: newNetAmount,
              totalAmount: newNetAmount,
              balanceAmount: newBalanceAmount,
              balanceAmtBreakup: newBalanceAmount.toFixed(2),
              visitCharges: parseFloat(formInputs.visitCharges) || 0,
            };
          })()),

        ...(statusCode === "FWP" && {
          followUpDate: date,
          followUpTime: time,
          followUpTimestamp: timestamp,
          remarks: formInputs.pbRemarks,
        }),

        ...(statusCode === "ITS" && {
          transitDate: date,
          transitTime: time,
          transitTimestamp: timestamp,
          expectedDeliveryDate: formInputs.expectedDeliveryDate,
        }),
      };

      delete payload._id;

      if (isCurrentAnExtension) {
        // console.log("Payload updateExtendEquipmentRent", payload)
        await ItemService.updateExtendEquipmentRent(payload);
      } else {
        // console.log("Payload updateEquipmentRentForm", payload)
        await ItemService.updateEquipmentRentForm(payload);
      }

      const msgMap = {
        CD: "Closed — all items and accessories marked for collection",
        FWP: `Follow Up scheduled for ${date}`,
        ITS: "In Transit — status updated",
        ELCS: `Early Closure (${formInputs.earlyClosureDate}) applied`,
        PELCS: `Partial Early Closure (${formInputs.earlyClosureDate}) applied`,
      };
      toast.success(msgMap[statusCode] || "Status updated");

      setIsPCDEditing(false);
      setRestoreSelection(new Set());
      if (newLifecycle === "ACTIVE") {
        setFormInputs((prev) => ({
          ...prev,
          pcCallStatusCode: "",
          pcCallStatus: "",
          pcCallStatusDesc: "",
        }));
      }

      navigate(CustomerConstants.CUSTOMER_V5_MR_EXTENSION_ERF_LIST);
    } catch (err) {
      toast.error("Failed to update status");
      console.error("handleUpdateEquipmentSTatus error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItemsToExtension = async () => {
    if (!formInputs.childExtensionNr) {
      toast.error("No child extension to add items to.");
      return;
    }
    if (!childIsLeaf) {
      toast.error(
        "Child extension has its own child. Add items there directly.",
      );
      return;
    }

    setLoading(true);
    try {
      // ── Identify items the user has reopened on the parent ──
      const reopenedRent = rentItems.filter(
        (i) =>
          !i.isTotal &&
          normalizeStatus(i.extensionStatus) === "Open" &&
          !i.movedToChildExtension &&
          !i.productReturned &&
          !isCreditNoted(i),
      );
      const reopenedNursing = nursingItems.filter(
        (i) =>
          !i.isTotal &&
          normalizeStatus(i.extensionStatus) === "Open" &&
          !i.movedToChildExtension &&
          !i.productReturned &&
          !isCreditNoted(i),
      );

      if (reopenedRent.length === 0 && reopenedNursing.length === 0) {
        toast.error("No items selected to add.");
        setLoading(false);
        return;
      }

      const reopenedParentLocalIds = new Set(
        [...reopenedRent, ...reopenedNursing].map((i) => i.local_id),
      );
      const reopenedAccessories = accessoryItems.filter(
        (a) =>
          reopenedParentLocalIds.has(a.parentLocalId) && !a.productReturned,
      );

      // ── Step 1: fetch child as-of-now (use ERFExtDetail for extension docs) ──
      const childIsExtensionDoc =
        formInputs.childExtensionNr.startsWith("EX") ||
        formInputs.childExtensionNr.includes("ERFEXT");

      const childQueryName = childIsExtensionDoc ? "ERFExtDetail" : "ERFDetail";

      const childRes = await ItemService.generateQuery({
        queryName: childQueryName,
        documentNumber: formInputs.childExtensionNr,
        timeZone: "Asia/Calcutta",
      });
      // console.log("childdocument response", childRes);
      const childDoc =
        (Array.isArray(childRes?.data) && childRes.data[0]) ||
        (Array.isArray(childRes?.data?.data) && childRes.data.data[0]) ||
        null;

      // console.log("ChildDoc days", childDoc);
      if (!childDoc) {
        throw new Error("Child extension not found");
      }

      // ── Step 2: append new items to child ──
      const dedupAppend = (existing, additions) => {
        const existingKeys = new Set(
          (existing || []).map(
            (i) => i.local_id || i.barcode || i.materialCode,
          ),
        );
        return [
          ...(existing || []),
          ...additions.filter(
            (i) => !existingKeys.has(i.local_id || i.barcode || i.materialCode),
          ),
        ];
      };

      const firstChildItem = (childDoc.rentItems || [])[0] || {};
      const childFromDate = firstChildItem.fromDate || childDoc.startingDate;
      const childToDate = firstChildItem.toDate || childDoc.endingDate;
      const childDays =
        parseFloat(firstChildItem.days || childDoc.days) ||
        (childFromDate && childToDate
          ? Math.round(
              (new Date(childToDate) - new Date(childFromDate)) /
                (1000 * 60 * 60 * 24),
            ) + 1
          : 0);

      const recalcForChild = (item) => {
        const backendSnap =
          backendRentItems.find((b) => b.local_id === item.local_id) ||
          backendNursingItems.find((b) => b.local_id === item.local_id) ||
          {};

        const tax = parseFloat(
          item.taxPercentage || item.masterTaxPercent || 0,
        );
        const backendGross = parseFloat(backendSnap.grossValue) || 0;
        const stateGross = parseFloat(item.grossValue) || 0;
        const stateDays = parseFloat(item.days) || 1;

        const newGrossValue =
          Math.abs(stateGross - backendGross) > 0.01
            ? parseFloat(stateGross.toFixed(2))
            : parseFloat(((backendGross / stateDays) * childDays).toFixed(2));

        const newTaxAmount = parseFloat(
          ((newGrossValue * tax) / (100 + tax)).toFixed(2),
        );
        const newNetValue = parseFloat(
          (newGrossValue - newTaxAmount).toFixed(2),
        );

        const originalSellingPrice = parseFloat(
          backendSnap.sellingPrice || item.sellingPrice || 0,
        );

        const extDetails = item.extensionItemsdetails || [];
        const newHistoryEntry = {
          version: `E${extDetails.length}`,
          extensionStatus: "Open",
          extensionFromDate: childFromDate,
          extensionToDate: childToDate,
          extendedDays: childDays,
          extensionAmount: newGrossValue,
          materialCode: item.materialCode,
          materialDescription: item.materialDescription,
          barcode: item.barcode,
          orderedQuantity: item.orderedQuantity,
          sellingPrice: originalSellingPrice,
          pcCallStatus: childDoc.pcCallStatus || "Extension",
          timestamp: new Date().toISOString(),
          createdBy: currentUserId,
        };

        return {
          ...item,
          fromDate: childFromDate,
          toDate: childToDate,
          days: childDays,
          netValue: newNetValue,
          grossValue: newGrossValue,
          taxAmount: newTaxAmount,
          effectiveGrossValue: newGrossValue,
          sellingPrice: originalSellingPrice,
          previousGrossValue:
            backendGross || parseFloat(item.previousGrossValue) || 0,
          previousNetValue:
            parseFloat(backendSnap.netValue) ||
            parseFloat(item.previousNetValue) ||
            0,
          previousTaxAmount:
            parseFloat(backendSnap.taxAmount) ||
            parseFloat(item.previousTaxAmount) ||
            0,
          movedToChildExtension: false,
          extensionStatus: "Open",
          extensionItemsdetails: [...extDetails, newHistoryEntry],

          closedGrossValue: undefined,
          closedNetValue: undefined,
          closedTaxAmount: undefined,
          itemEarlyClosureDate: "",
          itemEarlyClosureTime: "",
          refundAmt: 0,
          refundDays: 0,
        };
      };

      const newRentItems = dedupAppend(
        childDoc.rentItems,
        reopenedRent.map(recalcForChild),
      );
      const newNursingItems = dedupAppend(
        childDoc.nursingItems,
        reopenedNursing.map(recalcForChild),
      );
      const newAccessoryItems = dedupAppend(
        childDoc.accessoryItems,
        reopenedAccessories.map(recalcForChild),
      );

      // ── Step 3: totals from ALL child items (existing + newly added) ──
      const allRent = newRentItems.filter((i) => !i.isTotal);
      const allNursing = newNursingItems.filter((i) => !i.isTotal);

      const childRentNetTotal = allRent.reduce(
        (s, i) => s + (parseFloat(i.netValue) || 0),
        0,
      );
      const childRentTaxTotal = allRent.reduce(
        (s, i) => s + (parseFloat(i.taxAmount) || 0),
        0,
      );
      const childRentGrossTotal = allRent.reduce(
        (s, i) => s + (parseFloat(i.grossValue) || 0),
        0,
      );
      const childNursingNetTotal = allNursing.reduce(
        (s, i) => s + (parseFloat(i.netValue) || 0),
        0,
      );
      const childNursingTaxTotal = allNursing.reduce(
        (s, i) => s + (parseFloat(i.taxAmount) || 0),
        0,
      );
      const childNursingGrossTotal = allNursing.reduce(
        (s, i) => s + (parseFloat(i.grossValue) || 0),
        0,
      );
      const childLineItemNetTotal = childRentNetTotal + childNursingNetTotal;
      const childLineItemTaxTotal = childRentTaxTotal + childNursingTaxTotal;
      const childLineItemGrossTotal =
        childRentGrossTotal + childNursingGrossTotal;
      const childVisitCharges = parseFloat(childDoc.visitCharges) || 0;
      const childNetAmount =
        Math.round(childLineItemGrossTotal) + childVisitCharges;
      const childReceivedAmount = parseFloat(childDoc.receivedAmount) || 0;
      const childBalanceAmount = parseFloat(
        (childNetAmount - childReceivedAmount).toFixed(2),
      );

      const childPayload = {
        documentNumber: childDoc.documentNumber,
        equipmentRentNr: childDoc.documentNumber,
        equipmentNr: childDoc.equipmentNr,
        extensionLifecycleStatus: "ACTIVE",
        rentItems: newRentItems,
        nursingItems: newNursingItems,
        accessoryItems: newAccessoryItems,
        rentNetTotal: childRentNetTotal,
        rentTaxTotal: childRentTaxTotal,
        rentGrossTotal: childRentGrossTotal,
        nursingNetTotal: childNursingNetTotal,
        nursingTaxTotal: childNursingTaxTotal,
        nursingGrossTotal: childNursingGrossTotal,
        lineItemNetTotal: childLineItemNetTotal,
        lineItemTaxTotal: childLineItemTaxTotal,
        lineItemGrossTotal: childLineItemGrossTotal,
        lineItemGrossRounded: Math.round(childLineItemGrossTotal),
        roundOffAmount: parseFloat(
          (
            Math.round(childLineItemGrossTotal) - childLineItemGrossTotal
          ).toFixed(2),
        ),
        visitCharges: childVisitCharges,
        totalAmount: childNetAmount,
        netAmount: childNetAmount,
        receivedAmount: childReceivedAmount,
        balanceAmount: childBalanceAmount,
      };

      // console.log("child", childPayload);

      await ItemService.updateExtendEquipmentRent(childPayload);

      // ── Step 3: update parent — mark these items moved ──
      const reopenedAccLocalIds = new Set(
        reopenedAccessories.map((a) => a.local_id),
      );

      const stampParentMoved = (item, parentKeys) =>
        parentKeys.has(item.local_id)
          ? { ...item, movedToChildExtension: true, extensionStatus: "Open" }
          : item;

      const allParentItemsMoved = backendRentItems.every(
        (i) =>
          reopenedParentLocalIds.has(i.local_id) || i.movedToChildExtension,
      );

      const parentPayload = {
        documentNumber: formInputs.documentNumber,
        equipmentRentNr: formInputs.documentNumber,
        equipmentNr: formInputs.equipmentNr || formInputs.documentNumber,
        // If all items moved → upgrade from PEXT to full EXT
        ...(allParentItemsMoved && {
          pcCallStatus: "Extension",
          pcCallStatusCode: "EXT",
          pcCallStatusDesc: "Extension",
          extensionType: "Full",
        }),
        // childExtensionNr stays the same; lifecycle stays EXTENDED
        rentItems: backendRentItems.map((i) =>
          stampParentMoved(i, reopenedParentLocalIds),
        ),
        nursingItems: backendNursingItems.map((i) =>
          stampParentMoved(i, reopenedParentLocalIds),
        ),
        accessoryItems: backendAccessoryItems.map((a) =>
          stampParentMoved(a, reopenedAccLocalIds),
        ),
      };

      const isCurrentAnExtension =
        formInputs.documentNumber?.startsWith("EX") ||
        formInputs.documentNumber?.includes("ERFEXT");
      if (isCurrentAnExtension) {
        // console.log("updateExtendEquipmentRent", parentPayload);
        await ItemService.updateExtendEquipmentRent(parentPayload);
      } else {
        // console.log("updateEquipmentRentForm", parentPayload);
        await ItemService.updateEquipmentRentForm(parentPayload);
      }

      toast.success(
        `Added ${reopenedRent.length + reopenedNursing.length} item(s) to ${formInputs.childExtensionNr}`,
      );
      setIsPCDEditing(false);
      navigate(CustomerConstants.CUSTOMER_V5_MR_EXTENSION_ERF_LIST);
    } catch (err) {
      console.error("Add to extension error:", err);
      toast.error("Failed to add items. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const showExtensionHistory = (item) => {
    if (
      !item.extensionItemsdetails ||
      item.extensionItemsdetails.length === 0
    ) {
      toast.info("No extension history available for this item");
      return;
    }

    setSelectedItemHistory(item.extensionItemsdetails);
    setSelectedItemCode(item.materialCode);
    setShowExtensionHistoryModal(true);
  };

  const extensionHistoryColumns = [
    {
      name: "Version",
      selector: (row) => row.version,
      width: "100px",
      cell: (row) => (
        <span
          className="badge"
          style={{
            backgroundColor: "#7FAAA9",
            color: "black",
            padding: "6px 10px",
            borderRadius: "12px",
            fontWeight: 600,
          }}
        >
          {row.version}
        </span>
      ),
      sortable: true,
    },

    {
      name: "From Date",
      selector: (row) => row.extensionFromDate,
      width: "130px",
      cell: (row) => <span>{row.extensionFromDate}</span>,
      sortable: true,
    },
    {
      name: "To Date",
      selector: (row) => row.extensionToDate,
      width: "130px",
      cell: (row) => <span>{row.extensionToDate}</span>,
      sortable: true,
    },
    {
      name: "Days",
      selector: (row) => row.extendedDays,
      width: "80px",
      cell: (row) => <span className="fw-bold">{row.extendedDays}</span>,
      sortable: true,
    },
    {
      name: "Amount",
      selector: (row) => row.extensionAmount,
      width: "120px",
      cell: (row) => <span>{row.extensionAmount?.toFixed(2)}</span>,
      sortable: true,
    },
    {
      name: "Barcode",
      selector: (row) => row.barcode,
      width: "150px",
      cell: (row) => (
        <span className="text-muted small">{row.barcode || "-"}</span>
      ),
      sortable: true,
    },
    {
      name: "Timestamp",
      selector: (row) => row.timestamp,
      width: "180px",
      cell: (row) => (
        <span className="small text-muted">
          {new Date(row.timestamp).toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })}
        </span>
      ),
      sortable: true,
    },
  ];

  // eslint-disable-next-line no-unused-vars
  const [extensionData, setExtensionData] = useState([]);
  // const [loading, setLoading] = useState(false);

  const recalculateFinancialsFromGross = (item, grossInput) => {
    const parsedGross = parseFloat(grossInput);
    const grossValue = Number.isFinite(parsedGross) ? parsedGross : 0;
    const tax = parseFloat(item.taxPercentage) || 0;
    const qty = parseFloat(item.orderedQuantity) || 1;
    const days = parseFloat(item.days) || 1;

    const netValue = grossValue / (1 + tax / 100);
    const taxAmount = grossValue - netValue;
    // Reverse: effective rate/day so forward calc reproduces the same grossValue
    const sellingPrice =
      days * qty > 0
        ? parseFloat((netValue / (days * qty)).toFixed(4))
        : parseFloat(item.sellingPrice) || 0;

    return {
      grossValue: parseFloat(grossValue.toFixed(2)),
      effectiveGrossValue: parseFloat(grossValue.toFixed(2)),
      netValue: parseFloat(netValue.toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      sellingPrice,
    };
  };

  // const recalculateClosedFinancialsFromGross = (item, grossInput) => {
  //   const parsedGross = parseFloat(grossInput);
  //   const closedGross = Number.isFinite(parsedGross)
  //     ? Math.max(0, parsedGross)
  //     : 0;
  //   const tax = parseFloat(item.taxPercentage) || 0;
  //   const closedNet = closedGross / (1 + tax / 100);
  //   const closedTax = closedGross - closedNet;

  //   return {
  //     closedGrossValue: parseFloat(closedGross.toFixed(2)),
  //     closedNetValue: parseFloat(closedNet.toFixed(2)),
  //     closedTaxAmount: parseFloat(closedTax.toFixed(2)),
  //   };
  // };

  // const restoreItemToOpen = (item) => ({
  //   ...item,
  //   extensionStatus: "Open",
  //   // Swap the backup back into the live billing fields.
  //   grossValue: item.closedGrossValue ?? item.grossValue,
  //   netValue: item.closedNetValue ?? item.netValue,
  //   taxAmount: item.closedTaxAmount ?? item.taxAmount,
  //   sellingPrice: item.closedSellingPrice ?? item.sellingPrice,
  //   effectiveGrossValue: item.closedGrossValue ?? item.grossValue,
  //   // Clear with "" (not undefined) so the DB actually erases the backup.
  //   closedGrossValue: "",
  //   closedNetValue: "",
  //   closedTaxAmount: "",
  //   closedSellingPrice: "",
  //   itemEarlyClosureDate: "",
  //   itemEarlyClosureTime: "",
  //   refundAmt: 0,
  //   refundDays: 0,
  //   _refundAmt: undefined,
  //   _refundDays: undefined,
  //   grossValueInput: undefined,
  // });

  const restoreItemToOpen = (item) => {
    // A backup counts only if it's a real value — "", null, undefined all mean "none".
    const pick = (backup, current) =>
      backup === "" || backup === null || backup === undefined
        ? current
        : backup;
    // Always end up with a NUMBER so totals can never see "" → NaN.
    const toNum = (v) => parseFloat(v) || 0;

    const restoredGross = toNum(pick(item.closedGrossValue, item.grossValue));
    const restoredNet = toNum(pick(item.closedNetValue, item.netValue));
    const restoredTax = toNum(pick(item.closedTaxAmount, item.taxAmount));
    const restoredSelling = toNum(
      pick(item.closedSellingPrice, item.sellingPrice),
    );

    return {
      ...item,
      extensionStatus: "Open",
      // Swap the backup back into the live billing fields.
      grossValue: restoredGross,
      netValue: restoredNet,
      taxAmount: restoredTax,
      sellingPrice: restoredSelling,
      effectiveGrossValue: restoredGross,
      closedGrossValue: "",
      closedNetValue: "",
      closedTaxAmount: "",
      closedSellingPrice: "",
      itemEarlyClosureDate: "",
      itemEarlyClosureTime: "",
      refundAmt: 0,
      refundDays: 0,
      _refundAmt: undefined,
      _refundDays: undefined,
      grossValueInput: undefined,
    };
  };

  const derivePcCallStatusFromItems = (rentList, nursingList) => {
    const eligible = [...rentList, ...nursingList].filter(
      (i) => !i.isTotal && !i.movedToChildExtension && !isCreditNoted(i),
    );

    // No eligible items in the table — don't touch the header
    if (eligible.length === 0) return {};

    const closedCount = eligible.filter(
      (i) => normalizeStatus(i.extensionStatus) === "Closed",
    ).length;

    // CASE 1: All items Open → record returns to active rental
    if (closedCount === 0) {
      return {
        pcCallStatus: "",
        pcCallStatusCode: "",
        pcCallStatusDesc: "",
        earlyClosureDate: "",
        earlyClosureTime: "",
        earlyClosureTimestamp: "",
        closureType: "",
      };
    }

    // CASE 2: All items Closed → full early closure
    if (closedCount === eligible.length) {
      return {
        pcCallStatus: "Early Closure",
        pcCallStatusCode: "ELCS",
        pcCallStatusDesc: "",
        closureType: "Full",
        // earlyClosureDate / earlyClosureTime are preserved (they were
        // already set by the original closure; restore doesn't reset them
        // unless we're going to the "all open" case above).
      };
    }

    // CASE 3: Mixed → partial early closure
    return {
      pcCallStatus: "Partial Early Closure",
      pcCallStatusCode: "PELCS",
      pcCallStatusDesc: "",
      closureType: "Partial",
    };
  };

  const handleRestoreSelected = async () => {
    if (restoreSelection.size === 0) return;

    // if (isPaymentCollected) {
    //   toast.error(
    //     "Payment already collected — issue refund/credit note before restoring.",
    //   );
    //   return;
    // }
    if (isBillLocked) {
      toast.error("Bill is locked");
      return;
    }

    // ── 1. Compute next item/accessory state ────────────────────────────
    const restoreInList = (list, backendList) =>
      list.map((item) =>
        restoreSelection.has(item.local_id)
          ? // ? restoreItemToOpen(item, backendList)
            restoreItemToOpen(item)
          : item,
      );

    const nextRent = restoreInList(rentItems, backendRentItems);
    const nextNursing = restoreInList(nursingItems, backendNursingItems);

    // const nextAccessories = accessoryItems.map((acc) => {
    //   if (!restoreSelection.has(acc.parentLocalId)) return acc;
    //   const backendAcc =
    //     backendAccessoryItems.find((b) => b.local_id === acc.local_id) || {};
    //   return {
    //     ...acc,
    //     extensionStatus: backendAcc.extensionStatus || "Open",
    //     closedGrossValue: undefined,
    //     closedNetValue: undefined,
    //     closedTaxAmount: undefined,
    //     itemEarlyClosureDate: "",
    //     itemEarlyClosureTime: "",
    //   };
    // });
    const nextAccessories = accessoryItems.map((acc) => {
      if (!restoreSelection.has(acc.parentLocalId) || isFrozen(acc)) return acc;
      return {
        ...acc,
        extensionStatus: "Open",
        // grossValue: acc.previousGrossValue ?? acc.grossValue,
        // netValue: acc.previousNetValue ?? acc.netValue,
        // taxAmount: acc.previousTaxAmount ?? acc.taxAmount,
        // effectiveGrossValue: acc.previousGrossValue ?? acc.grossValue,
        closedGrossValue: "",
        closedNetValue: "",
        closedTaxAmount: "",
        itemEarlyClosureDate: "",
        itemEarlyClosureTime: "",
      };
    });

    // ── 2. Derive header (status code) and totals ───────────────────────
    const headerPatch = derivePcCallStatusFromItems(nextRent, nextNursing);
    const becomingFullyOpen = headerPatch.pcCallStatusCode === "";

    const newTotals = computeClosureTotals(
      nextRent,
      nextNursing,
      saleItems.filter((i) => !i.isTotal),
    );

    const newNetAmount =
      newTotals.lineItemGrossRounded +
      (parseFloat(formInputs.cartage) || 0) +
      (parseFloat(formInputs.installationCharges) || 0) +
      (parseFloat(formInputs.visitCharges) || 0);

    const totalReceived =
      parseFloat(formInputs?.paymentSummary?.totalBillingReceived) || 0;
    const newBalanceAmount = parseFloat(
      (newNetAmount - totalReceived).toFixed(2),
    );

    setRestoreConfirmDialog({
      show: true,
      data: {
        count: restoreSelection.size,
        becomingFullyOpen,
        newNetAmount,
        newBalanceAmount,
        newTotals,
        nextRent,
        nextNursing,
        nextAccessories,
        headerPatch,
      },
    });
  };

  const handleRestoreConfirmed = async () => {
    const {
      nextRent,
      nextNursing,
      nextAccessories,
      headerPatch,
      newNetAmount,
      newBalanceAmount,
      newTotals,
      becomingFullyOpen,
    } = restoreConfirmDialog.data;

    setRestoreConfirmDialog({ show: false, data: null });
    setLoading(true);

    try {
      setRentItems(nextRent);
      setNursingItems(nextNursing);
      setAccessoryItems(nextAccessories);

      setFormInputs((prev) => ({
        ...prev,
        ...headerPatch,
        rentNetTotal: newTotals.rentNetTotal,
        rentTaxTotal: newTotals.rentTaxTotal,
        rentGrossTotal: newTotals.rentGrossTotal,
        nursingNetTotal: newTotals.nursingNetTotal,
        nursingTaxTotal: newTotals.nursingTaxTotal,
        nursingGrossTotal: newTotals.nursingGrossTotal,
        lineItemNetTotal: newTotals.lineItemNetTotal,
        lineItemTaxTotal: newTotals.lineItemTaxTotal,
        lineItemGrossTotal: newTotals.lineItemGrossTotal,
        lineItemGrossRounded: newTotals.lineItemGrossRounded,
        roundOffAmount: newTotals.roundOffAmount,
        netAmount: newNetAmount,
        totalAmount: newNetAmount,
        balanceAmount: newBalanceAmount,
        balanceAmtBreakup: newBalanceAmount.toFixed(2),
      }));

      // paste the original payload-build + API call + toast + cleanup
      // (lines that were 5228–5325 in the original file)
      const newCode = headerPatch.pcCallStatusCode || "";
      const currentLifecycle = formInputs.extensionLifecycleStatus;
      const newLifecycle =
        currentLifecycle === "EXTENDED" ? "EXTENDED" : "ACTIVE";

      const payload = {
        documentNumber: formInputs.documentNumber,
        equipmentRentNr: formInputs.documentNumber,
        equipmentNr: formInputs.equipmentNr,
        pcCallStatus: headerPatch.pcCallStatus ?? "",
        pcCallStatusCode: newCode,
        pcCallStatusDesc: headerPatch.pcCallStatusDesc ?? "",
        extensionLifecycleStatus: newLifecycle,
        childExtensionNr: formInputs.childExtensionNr || null,
        timestamp: new Date().toISOString(),
        rentItems: nextRent
          .filter((i) => !i.isTotal)
          .map((item) => {
            const converted = convertToNumbers(item);
            delete converted._refundDays;
            delete converted._refundAmt;
            return converted;
          }),
        nursingItems: nextNursing
          .filter((i) => !i.isTotal)
          .map((item) => {
            const converted = convertToNumbers(item);
            delete converted._refundDays;
            delete converted._refundAmt;
            return converted;
          }),
        accessoryItems: nextAccessories.map(convertToNumbers),
        nursingAttendance: nursingData || [],
        earlyClosureDate: becomingFullyOpen
          ? ""
          : formInputs.earlyClosureDate || "",
        earlyClosureTime: becomingFullyOpen
          ? ""
          : formInputs.earlyClosureTime || "",
        earlyClosureTimestamp: becomingFullyOpen
          ? ""
          : formInputs.earlyClosureTimestamp || "",
        closureType: headerPatch.closureType ?? "",
        rentNetTotal: newTotals.rentNetTotal,
        rentTaxTotal: newTotals.rentTaxTotal,
        rentGrossTotal: newTotals.rentGrossTotal,
        nursingNetTotal: newTotals.nursingNetTotal,
        nursingTaxTotal: newTotals.nursingTaxTotal,
        nursingGrossTotal: newTotals.nursingGrossTotal,
        lineItemNetTotal: newTotals.lineItemNetTotal,
        lineItemTaxTotal: newTotals.lineItemTaxTotal,
        lineItemGrossTotal: newTotals.lineItemGrossTotal,
        lineItemGrossRounded: newTotals.lineItemGrossRounded,
        roundOffAmount: newTotals.roundOffAmount,
        netAmount: newNetAmount,
        totalAmount: newNetAmount,
        balanceAmount: newBalanceAmount,
        balanceAmtBreakup: newBalanceAmount.toFixed(2),
        visitCharges: parseFloat(formInputs.visitCharges) || 0,
      };
      delete payload._id;

      const isCurrentAnExtension =
        formInputs.documentNumber?.startsWith("EX") ||
        formInputs.documentNumber?.includes("ERFEXT");

      if (isCurrentAnExtension) {
        await ItemService.updateExtendEquipmentRent(payload);
      } else {
        await ItemService.updateEquipmentRentForm(payload);
      }

      toast.success(
        `${restoreConfirmDialog.data.count} item(s) restored${
          becomingFullyOpen ? " — closure cleared" : ""
        }.`,
      );

      setIsPCDEditing(false);
      setRestoreSelection(new Set());
      loadInitialPageData();
    } catch (err) {
      console.error("Restore error:", err);
      toast.error("Failed to save restore. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canEditExtensionAmount = (row) => {
    const isExtensionFlow = ["EXT", "PEXT"].includes(
      formInputs.pcCallStatusCode,
    );
    const isOpen = (row.extensionStatus || "Open") === "Open";
    return (
      isExtensionFlow &&
      isOpen &&
      isPCDEditing &&
      canEditOnThisRecord &&
      !row.movedToChildExtension &&
      !row.productReturned
    );
  };

  const isFrozen = (row) =>
    row.productReturned === true ||
    row.movedToChildExtension === true ||
    isCreditNoted(row);

  const canEditClosureAmount = (row) =>
    ["ELCS", "PELCS"].includes(formInputs.pcCallStatusCode) &&
    normalizeStatus(row.extensionStatus) === "Closed" &&
    isPCDEditing &&
    canEditOnThisRecord &&
    !isAmountLocked &&
    !isFrozen(row);

  const isRestoreEligible = (row) =>
    !row.isTotal &&
    normalizeStatus(row.extensionStatus) === "Closed" &&
    !row.productReturned &&
    !row.movedToChildExtension &&
    !isCreditNoted(row);

  const getColumns = (type) => {
    // eslint-disable-next-line
    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;

    const isEditable = !disabled && isFormEnabled;

    const isRefundEligibleType = (type) =>
      type === "Rent" || type === "Nursing";

    // BASE COLUMNS - Product Code & Details
    const baseCols = [
      {
        name: "Product Code",
        width: "170px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold text-end w-100">--</div>
          ) : (
            <InputField
              className="inputBox"
              name="materialCode"
              value={row.materialCode}
              disabled={true}
            />
          ),
      },
      {
        name: "Product Details",
        width: "240px",
        cell: (row) =>
          row.isTotal ? null : (
            <TextAreaField
              className="inputBox"
              name="materialDescription"
              value={row.materialDescription}
              onChange={(e) =>
                handleLineItemChange(
                  row.local_id,
                  e,
                  type === "Rent" ? "Rent" : "Sale",
                )
              }
              disabled={!isEditable}
            />
          ),
      },
    ];

    let middleCols = [];
    if (type === "Rent") {
      middleCols = [
        {
          name: "Type",
          width: "120px",
          cell: (row) => {
            if (row.isTotal) return null;

            const status = row.extensionStatus || "Open";

            return <TableStatusField status={status} />;
          },
        },

        {
          name: "Extension Status",
          width: "145px",
          cell: (row) => {
            if (row.isTotal) return null;

            if (row?.movedToChildExtension) {
              return (
                <span
                  className="badge bg-secondary"
                  title={`Item moved to ${formInputs.childExtensionNr || "child extension"}`}
                >
                  In New RE
                </span>
              );
            }
            if (isCreditNoted(row)) {
              return (
                <span
                  className="badge bg-danger"
                  title="Credit note done — item returned to godown"
                >
                  Credit Note
                </span>
              );
            }
            if (row?.productReturned) {
              return (
                <span
                  className="badge bg-success"
                  title="Item physically returned"
                >
                  Returned
                </span>
              );
            }

            const pcCode = formInputs.pcCallStatusCode;
            const canToggleItemStatus = ["PEXT", "PELCS", "FWP"].includes(
              pcCode,
            );
            const isForceOpen = pcCode === "EXT";
            const isForceClosed = ["CD", "ELCS"].includes(pcCode);

            const isClosedRow =
              normalizeStatus(row.extensionStatus) === "Closed";

            const isToggleDisabled =
              !canToggleItemStatus ||
              isForceOpen ||
              isForceClosed ||
              !isPCDEditing ||
              !canEditOnThisRecord ||
              (isClosedRow && _isClosureMode);

            let isChecked = false;
            if (isForceOpen) {
              isChecked = true;
            } else if (isForceClosed) {
              isChecked = false;
            } else {
              const currentStatus = row.extensionStatus || "Open";
              isChecked = currentStatus === "Open";
            }

            return (
              <div className="d-flex align-items-center justify-content-center">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={isChecked}
                    disabled={isToggleDisabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const extDays = parseInt(formInputs.days) || 0;

                      const isClosureFlow = ["ELCS", "PELCS"].includes(
                        formInputs.pcCallStatusCode,
                      );
                      const applyStatusToggle = (item) => {
                        if (item.local_id !== row.local_id) return item;

                        // ── Closed → Open (RESTORE) ──────────────────────────────
                        if (checked) {
                          // return restoreItemToOpen(item, backendRentItems);
                          return restoreItemToOpen(item);
                        }

                        // ── Open → Closed under ELCS/PELCS (CLOSURE) ─────────────
                        // if (isClosureFlow) {
                        //   return {
                        //     ...item,
                        //     extensionStatus: "Closed",
                        //     closedGrossValue: item.grossValue,
                        //     closedNetValue: item.netValue,
                        //     closedTaxAmount: item.taxAmount,
                        //     // Stamp this item with the current global closure date
                        //     itemEarlyClosureDate:
                        //       formInputs.earlyClosureDate || "",
                        //     itemEarlyClosureTime:
                        //       formInputs.earlyClosureTime || "",
                        //   };
                        // }
                        if (isClosureFlow) {
                          return {
                            ...item,
                            extensionStatus: "Closed",
                            itemEarlyClosureDate:
                              formInputs.earlyClosureDate || "",
                            itemEarlyClosureTime:
                              formInputs.earlyClosureTime || "",
                          };
                        }

                        // ── Open → Closed under PEXT (EXTENSION FLOW) ────────────
                        const snap = {
                          previousGrossValue:
                            item.previousGrossValue ?? item.grossValue,
                          previousNetValue:
                            item.previousNetValue ?? item.netValue,
                          previousTaxAmount:
                            item.previousTaxAmount ?? item.taxAmount,
                        };
                        if (extDays > 0) {
                          const qty = parseFloat(item.orderedQuantity) || 1;
                          const price = parseFloat(item.sellingPrice) || 0;
                          const tax = parseFloat(item.taxPercentage) || 0;
                          const net = parseFloat(
                            (price * extDays * qty).toFixed(2),
                          );
                          const taxAmt = parseFloat(
                            (net * (tax / 100)).toFixed(2),
                          );
                          const gross = parseFloat((net + taxAmt).toFixed(2));
                          return {
                            ...item,
                            ...snap,
                            extensionStatus: "Closed",
                            netValue: net,
                            taxAmount: taxAmt,
                            grossValue: gross,
                            effectiveGrossValue: gross,
                            grossValueInput: undefined,
                          };
                        }
                        return {
                          ...item,
                          ...snap,
                          extensionStatus: "Closed",
                        };
                      };

                      // Only ask if the user might lose data (refund or reduced closure bill).
                      if (checked) {
                        const hasRefundDraft =
                          parseFloat(row._refundAmt ?? row.refundAmt ?? 0) > 0;
                        const hasReducedClosureBill =
                          row.closedGrossValue !== undefined &&
                          parseFloat(row.closedGrossValue) !==
                            parseFloat(row.grossValue);

                        if (hasRefundDraft || hasReducedClosureBill) {
                          const refundLine = hasRefundDraft
                            ? `Clear Refund Amount (₹${row._refundAmt ?? row.refundAmt})`
                            : "";
                          const billLine = hasReducedClosureBill
                            ? `Clear Bill On Closure (₹${row.closedGrossValue})`
                            : "";
                          const dateLine = row.itemEarlyClosureDate
                            ? `Clear Early Closure Date (${row.itemEarlyClosureDate})`
                            : "";

                          setSingleRestoreDialog({
                            show: true,
                            data: {
                              itemName:
                                row.materialDescription || row.materialCode,
                              // grossValue: row.grossValue,
                              grossValue:
                                row.closedGrossValue ?? row.grossValue,
                              billLine,
                              refundLine,
                              dateLine,
                              onConfirm: () => {
                                // const isPelcs =
                                //   formInputs.pcCallStatusCode === "PELCS" &&
                                //   formInputs.earlyClosureDate;
                                let newRent =
                                  row.itemType === "Rent"
                                    ? rentItems.map(applyStatusToggle)
                                    : rentItems;
                                let newNursing =
                                  row.itemType !== "Rent"
                                    ? nursingItems.map(applyStatusToggle)
                                    : nursingItems;

                                // if (isPelcs) {
                                //   const closureDate = new Date(
                                //     formInputs.earlyClosureDate,
                                //   );
                                  // const applyRefund = (items) =>
                                  //   items.map((i) => {
                                  //     if (i.isTotal || i.productReturned)
                                  //       return i;
                                  //     if (
                                  //       normalizeStatus(i.extensionStatus) !==
                                  //       "Closed"
                                  //     )
                                  //       return {
                                  //         ...i,
                                  //         _refundDays: 0,
                                  //         _refundAmt: 0,
                                  //       };
                                  //     const refBase = i.toDate
                                  //       ? new Date(i.toDate)
                                  //       : new Date(formInputs.endingDate);
                                  //     const days = Math.max(
                                  //       0,
                                  //       Math.ceil(
                                  //         (refBase - closureDate) /
                                  //           (1000 * 60 * 60 * 24),
                                  //       ),
                                  //     );
                                  //     const netAmt =
                                  //       days *
                                  //       (parseFloat(i.sellingPrice) || 0) *
                                  //       (parseFloat(i.orderedQuantity) || 1);
                                  //     const taxRate =
                                  //       parseFloat(i.taxPercentage) || 0;
                                  //     const amt = parseFloat(
                                  //       (netAmt * (1 + taxRate / 100)).toFixed(
                                  //         2,
                                  //       ),
                                  //     );
                                  //     return {
                                  //       ...i,
                                  //       _refundDays: days,
                                  //       _refundAmt: amt,
                                  //     };
                                  //   });
                                  // newRent = applyRefund(newRent);
                                  // newNursing = applyRefund(newNursing);
                                // }

                                setRentItems(newRent);
                                setNursingItems(newNursing);

                                setAccessoryItems((prev) =>
                                  prev.map((acc) => {
                                    if (acc.parentLocalId !== row.local_id)
                                      return acc;
                                    if (acc.productReturned) return acc;
                                    if (acc.movedToChildExtension) return acc;
                                    return {
                                      ...acc,
                                      extensionStatus: "Open",
                                      previousGrossValue:
                                        acc.previousGrossValue ??
                                        acc.grossValue,
                                      previousNetValue:
                                        acc.previousNetValue ?? acc.netValue,
                                      previousTaxAmount:
                                        acc.previousTaxAmount ?? acc.taxAmount,
                                      grossValue:
                                        acc.previousGrossValue ??
                                        acc.grossValue,
                                      netValue:
                                        acc.previousNetValue ?? acc.netValue,
                                      taxAmount:
                                        acc.previousTaxAmount ?? acc.taxAmount,
                                      effectiveGrossValue:
                                        acc.previousGrossValue ??
                                        acc.grossValue,
                                    };
                                  }),
                                );

                                setSelectedExtensionItems((prev) => ({
                                  ...prev,
                                  [row.local_id]: "Yes",
                                }));

                                // if (isPelcs) {
                                //   const closureDate = new Date(
                                //     formInputs.earlyClosureDate,
                                //   );
                                //   const totalAmt = [...newRent, ...newNursing]
                                //     .filter((i) => !i.isTotal)
                                //     .reduce(
                                //       (s, i) =>
                                //         s + (parseFloat(i._refundAmt) || 0),
                                //       0,
                                //     );
                                //   const totalDays = formInputs.endingDate
                                //     ? Math.max(
                                //         0,
                                //         Math.ceil(
                                //           (new Date(formInputs.endingDate) -
                                //             closureDate) /
                                //             (1000 * 60 * 60 * 24),
                                //         ),
                                //       )
                                //     : 0;
                                //   setFormInputs((prev) => ({
                                //     ...prev,
                                //     refundDays: totalDays,
                                //     refundAmount: totalAmt.toFixed(2),
                                //   }));
                                // }
                              },
                            },
                          });
                          return; // suspend — resumes when user clicks Confirm in dialog
                        }
                      }

                      // const isPelcs =
                      //   formInputs.pcCallStatusCode === "PELCS" &&
                      //   formInputs.earlyClosureDate;

                      let newRent =
                        row.itemType === "Rent"
                          ? rentItems.map(applyStatusToggle)
                          : rentItems;
                      let newNursing =
                        row.itemType !== "Rent"
                          ? nursingItems.map(applyStatusToggle)
                          : nursingItems;

                      // if (isPelcs) {
                      //   const closureDate = new Date(
                      //     formInputs.earlyClosureDate,
                      //   );
                      //   const applyRefund = (items) =>
                      //     items.map((i) => {
                      //       if (i.isTotal || i.productReturned) return i;
                      //       if (normalizeStatus(i.extensionStatus) !== "Closed")
                      //         return { ...i, _refundDays: 0, _refundAmt: 0 };
                      //       const refBase = i.toDate
                      //         ? new Date(i.toDate)
                      //         : new Date(formInputs.endingDate);
                      //       const days = Math.max(
                      //         0,
                      //         Math.ceil(
                      //           (refBase - closureDate) / (1000 * 60 * 60 * 24),
                      //         ),
                      //       );
                      //       // Refund = gross (net + tax) — customer paid gross
                      //       const netAmt =
                      //         days *
                      //         (parseFloat(i.sellingPrice) || 0) *
                      //         (parseFloat(i.orderedQuantity) || 1);
                      //       const taxRate = parseFloat(i.taxPercentage) || 0;
                      //       const amt = parseFloat(
                      //         (netAmt * (1 + taxRate / 100)).toFixed(2),
                      //       );
                      //       return { ...i, _refundDays: days, _refundAmt: amt };
                      //     });
                      //   newRent = applyRefund(newRent);
                      //   newNursing = applyRefund(newNursing);
                      // }

                      setRentItems(newRent);
                      setNursingItems(newNursing);

                      setAccessoryItems((prev) =>
                        prev.map((acc) => {
                          if (acc.parentLocalId !== row.local_id) return acc;
                          if (acc.productReturned) return acc;
                          if (acc.movedToChildExtension) return acc;

                          const isClosed = !checked;
                          return {
                            ...acc,
                            extensionStatus: isClosed ? "Closed" : "Open",
                            previousGrossValue:
                              acc.previousGrossValue ?? acc.grossValue,
                            previousNetValue:
                              acc.previousNetValue ?? acc.netValue,
                            previousTaxAmount:
                              acc.previousTaxAmount ?? acc.taxAmount,
                            grossValue: isClosed
                              ? 0
                              : (acc.previousGrossValue ?? acc.grossValue),
                            netValue: isClosed
                              ? 0
                              : (acc.previousNetValue ?? acc.netValue),
                            taxAmount: isClosed
                              ? 0
                              : (acc.previousTaxAmount ?? acc.taxAmount),
                            effectiveGrossValue: isClosed
                              ? 0
                              : (acc.previousGrossValue ?? acc.grossValue),
                          };
                        }),
                      );

                      // ── 4. Track selection for payload ────────────────────────────────────
                      setSelectedExtensionItems((prev) => ({
                        ...prev,
                        [row.local_id]: checked ? "Yes" : "No",
                      }));

                      // ── 5. Update form-level refund totals for PELCS ──────────────────────
                      // if (isPelcs) {
                      //   const closureDate = new Date(
                      //     formInputs.earlyClosureDate,
                      //   );
                      //   const totalAmt = [...newRent, ...newNursing]
                      //     .filter((i) => !i.isTotal)
                      //     .reduce(
                      //       (s, i) => s + (parseFloat(i._refundAmt) || 0),
                      //       0,
                      //     );
                      //   const totalDays = formInputs.endingDate
                      //     ? Math.max(
                      //         0,
                      //         Math.ceil(
                      //           (new Date(formInputs.endingDate) -
                      //             closureDate) /
                      //             (1000 * 60 * 60 * 24),
                      //         ),
                      //       )
                      //     : 0;
                      //   setFormInputs((prev) => ({
                      //     ...prev,
                      //     refundDays: totalDays,
                      //     refundAmount: totalAmt.toFixed(2),
                      //   }));
                      // }
                    }}
                  />
                  <label className="form-check-label ms-1 small text-muted">
                    {isChecked ? "Yes" : "No"}
                  </label>
                </div>
              </div>
            );
          },
        },

        // SELECT — bulk-restore checkbox for closed eligible items
        {
          name: "Select",
          width: "80px",
          cell: (row) => {
            if (row.isTotal) return null;
            if (!isRestoreEligible(row)) return null;

            // const canSelect =
            //   _isClosureMode &&
            //   isPCDEditing &&
            //   canEditOnThisRecord &&
            //   !isPaymentCollected;
            const canSelect =
              _isClosureMode &&
              isPCDEditing &&
              canEditOnThisRecord &&
              !isBillLocked;

            const checked = restoreSelection.has(row.local_id);

            return (
              <input
                type="checkbox"
                className="form-check-input"
                checked={checked}
                disabled={!canSelect}
                onChange={() => {
                  setRestoreSelection((prev) => {
                    const next = new Set(prev);
                    if (next.has(row.local_id)) next.delete(row.local_id);
                    else next.add(row.local_id);
                    return next;
                  });
                }}
                title={
                  !canSelect
                    ? isPaymentCollected
                      ? "Payment collected — restore not allowed"
                      : "Edit mode required"
                    : "Select for restore"
                }
              />
            );
          },
        },

        // FROM DATE — shows pending from-date when EXT/PEXT has days filled; otherwise latest saved
        {
          name: "From",
          width: "150px",
          cell: (row) => {
            if (row.isTotal) return null;

            const isExtFlow = ["EXT", "PEXT"].includes(
              formInputs.pcCallStatusCode,
            );
            const isOpen = normalizeStatus(row.extensionStatus) !== "Closed";
            // const hasPendingExtDates =
            //   isExtFlow && isOpen && !!formInputs.endingDate;

            const hasPendingExtDates =
              isExtFlow &&
              isOpen &&
              !!formInputs.endingDate &&
              isPCDEditing &&
              canEditOnThisRecord;

            let displayFromDate = row.fromDate;
            if (row.extensionItemsdetails?.length > 0) {
              const latestExt =
                row.extensionItemsdetails[row.extensionItemsdetails.length - 1];
              displayFromDate = latestExt.extensionFromDate || row.fromDate;
            }
            if (hasPendingExtDates && baseEndingDate) {
              displayFromDate = addOneDay(baseEndingDate);
            }

            return (
              <InputField
                className={`inputBox${hasPendingExtDates ? " border-warning" : ""}`}
                type="date"
                value={displayFromDate}
                disabled
              />
            );
          },
        },

        // TO DATE — shows pending ending date for EXT/PEXT; original to-date otherwise
        {
          name: "To Date",
          width: "150px",
          cell: (row) => {
            if (row.isTotal) return null;

            const isExtFlow = ["EXT", "PEXT"].includes(
              formInputs.pcCallStatusCode,
            );
            const isOpen = normalizeStatus(row.extensionStatus) !== "Closed";
            // const hasPendingExtDates =
            //   isExtFlow && isOpen && !!formInputs.endingDate;
            const hasPendingExtDates =
              isExtFlow &&
              isOpen &&
              !!formInputs.endingDate &&
              isPCDEditing &&
              canEditOnThisRecord;

            let displayDate = row.toDate;
            if (row.extensionItemsdetails?.length > 0) {
              const latestExt =
                row.extensionItemsdetails[row.extensionItemsdetails.length - 1];
              displayDate = latestExt.extensionToDate || row.toDate;
            }
            if (hasPendingExtDates) displayDate = formInputs.endingDate;

            return (
              <InputField
                className={`inputBox${hasPendingExtDates ? " border-warning" : ""}`}
                type="date"
                value={displayDate}
                disabled
              />
            );
          },
        },

        // DAYS — shows pending days count for EXT/PEXT; original days otherwise
        {
          name: "Days",
          width: "100px",
          cell: (row) => {
            if (row.isTotal) return null;

            const isExtFlow = ["EXT", "PEXT"].includes(
              formInputs.pcCallStatusCode,
            );
            const isOpen = normalizeStatus(row.extensionStatus) !== "Closed";
            // const hasPendingExtDays = isExtFlow && isOpen && !!formInputs.days;
            const hasPendingExtDays =
              isExtFlow &&
              isOpen &&
              !!formInputs.days &&
              isPCDEditing &&
              canEditOnThisRecord;

            let displayDays = row.days;
            if (row.extensionItemsdetails?.length > 0) {
              const latestExt =
                row.extensionItemsdetails[row.extensionItemsdetails.length - 1];
              displayDays = latestExt.extendedDays || row.days;
            }
            if (hasPendingExtDays) displayDays = formInputs.days;

            return (
              <InputField
                className={`inputBox${hasPendingExtDays ? " border-warning" : ""}`}
                type="number"
                value={displayDays}
                disabled={true}
              />
            );
          },
        },
        //  HISTORY ICON - Opens modal showing ALL extensions
        {
          name: "History",
          width: "80px",
          cell: (row) => {
            if (row.isTotal) return null;

            const historyCount = row.extensionItemsdetails?.length || 0;

            return (
              <div className="text-center">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => showExtensionHistory(row)}
                  disabled={historyCount === 0}
                  title={
                    historyCount > 0
                      ? `View ${historyCount} extension(s)`
                      : "No history"
                  }
                >
                  <i className="bi bi-clock-history me-1"></i>
                  {historyCount > 0 && (
                    <span className="badge bg-primary rounded-pill ms-1">
                      {historyCount}
                    </span>
                  )}
                </button>
              </div>
            );
          },
          sortable: false,
        },
      ];
    }

    // FINANCIAL COLUMNS - Common for all types
    const financialCols = [
      {
        name: "Qty.",
        width: "90px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold text-center w-100">Total</div>
          ) : (
            <InputField
              className="inputBox"
              type="number"
              name="orderedQuantity"
              value={row.orderedQuantity}
              min="1"
              onChange={(e) =>
                handleLineItemChange(
                  row.local_id,
                  e,
                  type === "Rent" ? "Rent" : "Sale",
                )
              }
              disabled={true}
            />
          ),
      },
      {
        name: "Previous Total",
        width: "120px",
        cell: (row) =>
          row.isTotal ? null : (
            <InputField
              className="inputBox"
              type="number"
              value={row.previousGrossValue || row.grossValue || ""}
              disabled={true}
            />
          ),
      },
      {
        name: "Net Value",
        width: "130px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold">{row.netValue}</div>
          ) : (
            <InputField
              className="inputBox"
              type="number"
              value={row.netValue}
              disabled
            />
          ),
      },
      {
        name: "GST (%)",
        width: "100px",
        cell: (row) =>
          row.isTotal ? null : (
            <InputField
              className="inputBox"
              type="number"
              value={row.taxPercentage}
              disabled
            />
          ),
      },
      {
        name: "Tax Amount",
        width: "120px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold">{row.taxAmount}</div>
          ) : (
            <InputField
              className="inputBox"
              type="number"
              value={row.taxAmount}
              disabled
            />
          ),
      },
      {
        name: "Amount",
        width: "120px",
        cell: (row) => {
          if (row.isTotal) {
            return <div className="fw-bold">{row.grossValue}</div>;
          }

          // const canEditAmount = canEditExtensionAmount(row);
          const canEditAmount =
            canEditExtensionAmount(row) || canEditClosureAmount(row);
          const displayValue =
            row.grossValueInput !== undefined
              ? row.grossValueInput
              : (row.effectiveGrossValue ?? row.grossValue ?? "");

          const applyGrossValue = (rawValue) => {
            setRentItems((prev) =>
              prev.map((item) => {
                if (item.local_id !== row.local_id) return item;

                const finalValue = rawValue === "" ? 0 : rawValue;

                return {
                  ...item,
                  previousGrossValue:
                    item.previousGrossValue ?? item.grossValue,
                  previousNetValue: item.previousNetValue ?? item.netValue,
                  previousTaxAmount: item.previousTaxAmount ?? item.taxAmount,
                  grossValueInput: undefined,
                  ...recalculateFinancialsFromGross(item, finalValue),
                };
              }),
            );
          };

          return (
            <InputField
              className="inputBox"
              type="number"
              value={displayValue}
              disabled={!canEditAmount}
              onChange={(e) => {
                const rawValue = e.target.value;

                setRentItems((prev) =>
                  prev.map((item) =>
                    item.local_id === row.local_id
                      ? {
                          ...item,
                          grossValueInput: rawValue,
                        }
                      : item,
                  ),
                );
              }}
              onBlur={(e) => applyGrossValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyGrossValue(e.target.value);
                }
              }}
            />
          );
        },
      },
      // // NEW — Bill On Closure (visible always; editable only in ELCS/PELCS for closed items)
      // {
      //   name: "Bill On Closure",
      //   width: "140px",
      //   cell: (row) => {
      //     // if (row.isTotal) return null;
      //     if (row.isTotal) {
      //       return row.closedGrossTotal ? (
      //         <div className="fw-bold">{row.closedGrossTotal}</div>
      //       ) : null;
      //     }

      //     const isClosed = normalizeStatus(row.extensionStatus) === "Closed";

      //     const canEditClosedGross =
      //       _isClosureMode &&
      //       isClosed &&
      //       !row.productReturned &&
      //       !row.movedToChildExtension &&
      //       !isCreditNoted(row) &&
      //       !isPaymentCollected &&
      //       isPCDEditing &&
      //       canEditOnThisRecord;

      //     const displayValue =
      //       row.closedGrossValue !== undefined && row.closedGrossValue !== null
      //         ? row.closedGrossValue
      //         : isClosed
      //           ? row.grossValue
      //           : "";

      //     const tooltip = !_isClosureMode
      //       ? "Available only under Early Closure / Partial Early Closure"
      //       : !isClosed
      //         ? "Only closed items have a closure bill"
      //         : row.productReturned
      //           ? "Item already collected — locked"
      //           : row.movedToChildExtension
      //             ? "Item moved to child extension"
      //             : isPaymentCollected
      //               ? "Payment already collected — use Refund Amt to issue a refund"
      //               : !isLatestExtension
      //                 ? "Only the latest extension can be edited"
      //                 : "Editable: this value contributes to Net Amount";

      //     return (
      //       <InputField
      //         className="inputBox"
      //         type="number"
      //         min="0"
      //         value={displayValue}
      //         disabled={!canEditClosedGross}
      //         title={tooltip}
      //         onChange={(e) => {
      //           const raw = e.target.value;
      //           setRentItems((prev) =>
      //             prev.map((item) =>
      //               item.local_id === row.local_id
      //                 ? {
      //                     ...item,
      //                     ...recalculateClosedFinancialsFromGross(item, raw),
      //                   }
      //                 : item,
      //             ),
      //           );
      //         }}
      //       />
      //     );
      //   },
      // },
      // NEW — Per-item Early Closure Date (visible only in ELCS/PELCS)
      // ...(_isClosureMode
      ...(_isEarlyClosureMode && (type === "Rent" || type === "Nursing")
        ? [
            {
              name: "Early Closure Date",
              width: "160px",
              cell: (row) => {
                if (row.isTotal) return null;
                const isClosed =
                  normalizeStatus(row.extensionStatus) === "Closed";
                const canEditDate =
                  isClosed &&
                  !row.productReturned &&
                  !row.movedToChildExtension &&
                  !isCreditNoted(row) &&
                  isLatestExtension &&
                  isPCDEditing;
                return (
                  <InputField
                    className="inputBox"
                    type="date"
                    value={row.itemEarlyClosureDate || ""}
                    min={formInputs.startingDate}
                    max={formInputs.endingDate}
                    disabled={!canEditDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRentItems((prev) =>
                        prev.map((item) =>
                          item.local_id === row.local_id
                            ? { ...item, itemEarlyClosureDate: v }
                            : item,
                        ),
                      );
                    }}
                  />
                );
              },
            },
          ]
        : []),
    ];

    // BARCODE COLUMN - Last column
    const barcodeEnteringColumn = [
      {
        name: "Barcode",
        width: "180px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold text-end w-100">--</div>
          ) : (
            <InputField
              className="inputBox"
              name="barcode"
              value={row.barcode}
              disabled={true}
            />
          ),
      },
    ];

    const refundColumns = isRefundEligibleType(type)
      ? [
          // REFUND COLUMNS — always rendered; editable only for ELCS / PELCS closed items

          {
            name: "Refund Days",
            width: "110px",
            // cell: (row) => {
            //   if (row.isTotal) return null;
            //   return (
            //     <InputField
            //       className="inputBox"
            //       type="number"
            //       value={row._refundDays ?? row.refundDays ?? 0}
            //       disabled={true}
            //       title="Auto-calculated from closure date"
            //       onChange={() => {}}
            //     />
            //   );
            // },
            cell: (row) => {
              if (row.isTotal) return null;
              const isEarlyClosureFlow = ["ELCS", "PELCS"].includes(
                formInputs.pcCallStatusCode,
              );
              const isClosed =
                normalizeStatus(row.extensionStatus) === "Closed";
              // const isEditing = isEarlyClosureFlow && isClosed;
              const isEditing = isEarlyClosureFlow && isClosed && !row.productReturned;
              return (
                <InputField
                  className="inputBox"
                  type="number"
                  value={row._refundDays ?? row.refundDays ?? 0}
                  disabled={!isEditing || !isPCDEditing}
                  onChange={(e) => {
                    const days = Math.max(0, parseFloat(e.target.value) || 0);
                    setRentItems((prev) =>
                      prev.map((i) =>
                        i.local_id === row.local_id
                          ? { ...i, _refundDays: days }
                          : i,
                      ),
                    );
                  }}
                />
              );
            },
          },
          {
            name: "Refund Amt.",
            width: "130px",
            cell: (row) => {
              if (row.isTotal) {
                const totalRefund = rentItems
                  .filter(
                    (i) =>
                      !i.isTotal &&
                      normalizeStatus(i.extensionStatus) === "Closed",
                  )
                  .reduce(
                    (s, i) =>
                      s + (parseFloat(i._refundAmt ?? i.refundAmt) || 0),
                    0,
                  );
                return <div className="fw-bold">{totalRefund.toFixed(2)}</div>;
              }
              const isEarlyClosureFlow = ["ELCS", "PELCS"].includes(
                formInputs.pcCallStatusCode,
              );
              const isClosed =
                normalizeStatus(row.extensionStatus) === "Closed";
              const isLocked = row.productReturned === true;
              const isEditing = isEarlyClosureFlow && isClosed && !isLocked;
              return (
                <InputField
                  className="inputBox"
                  type="number"
                  value={row._refundAmt ?? row.refundAmt ?? 0}
                  disabled={!isEditing || !isPCDEditing}
                  title={
                    isLocked
                      ? "Already stocked in — refund locked"
                      : !isEarlyClosureFlow
                        ? "Select Early Closure to edit"
                        : !isClosed
                          ? "Only closed items have refund"
                          : "Refund amount (editable)"
                  }
                  onChange={(e) => {
                    const amt = Math.max(0, parseFloat(e.target.value) || 0);
                    setRentItems((prev) =>
                      prev.map((i) =>
                        i.local_id === row.local_id
                          ? { ...i, _refundAmt: amt }
                          : i,
                      ),
                    );
                    const totalAmt =
                      rentItems
                        .map((i) =>
                          i.local_id === row.local_id
                            ? { ...i, _refundAmt: amt }
                            : i,
                        )
                        .filter(
                          (i) =>
                            !i.isTotal &&
                            normalizeStatus(i.extensionStatus) === "Closed",
                        )
                        .reduce(
                          (s, i) => s + (parseFloat(i._refundAmt) || 0),
                          0,
                        ) +
                      nursingItems
                        .filter(
                          (i) =>
                            !i.isTotal &&
                            normalizeStatus(i.extensionStatus) === "Closed",
                        )
                        .reduce(
                          (s, i) => s + (parseFloat(i._refundAmt) || 0),
                          0,
                        );
                    setFormInputs((prev) => ({
                      ...prev,
                      refundAmount: totalAmt.toFixed(2),
                    }));
                  }}
                />
              );
            },
          },
        ]
      : [];

    // RETURN ALL COLUMNS IN ORDER
    return [
      ...baseCols,
      ...middleCols,
      ...financialCols,
      ...barcodeEnteringColumn,
      ...refundColumns,
    ];
  };

  //nursing columns
  const getNursingColumns = () => {
    // eslint-disable-next-line
    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;

    const isEditable = !disabled && isFormEnabled;

    // BASE COLUMNS
    const baseCols = [
      {
        name: "Product Code",
        width: "180px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold text-end w-100">--</div>
          ) : (
            <InputField
              className="inputBox"
              name="materialCode"
              value={row.materialCode}
              disabled={true}
            />
          ),
      },
      {
        name: "Product Details",
        width: "250px",
        cell: (row) =>
          row.isTotal ? null : (
            <TextAreaField
              className="inputBox"
              name="materialDescription"
              value={row.materialDescription}
              onChange={(e) => handleLineItemChange(row.local_id, e, "Nursing")}
              disabled={!isEditable}
            />
          ),
      },
    ];

    // MIDDLE COLUMNS - Same as Rent
    const middleCols = [
      {
        name: "Type",
        width: "120px",
        cell: (row) => {
          if (row.isTotal) return null;

          const status = row.extensionStatus || "Open";

          return <TableStatusField status={status} />;
        },
      },

      {
        name: "Extension Status",
        width: "145px",
        cell: (row) => {
          if (row.isTotal) return null;

          const pcCode = formInputs.pcCallStatusCode;

          const canToggleItemStatus = ["PEXT", "PELCS", "FWP"].includes(pcCode);

          const isForceOpen = pcCode === "EXT";
          const isForceClosed = ["CD", "ELCS"].includes(pcCode);

          // const isToggleDisabled =
          //   !canToggleItemStatus || isForceOpen || isForceClosed;

          const isClosedRow = normalizeStatus(row.extensionStatus) === "Closed";

          const isToggleDisabled =
            !canToggleItemStatus ||
            isForceOpen ||
            isForceClosed ||
            !isPCDEditing ||
            !canEditOnThisRecord ||
            // Under closure mode, reopening must go through the bulk
            // Restore Selected flow — don't allow toggle to flip Closed→Open.
            (isClosedRow && _isClosureMode);

          let isChecked = false;
          if (isForceOpen) {
            isChecked = true;
          } else if (isForceClosed) {
            isChecked = false;
          } else {
            const currentStatus = row.extensionStatus || "Open";
            isChecked = currentStatus === "Open";
          }

          return (
            <div className="d-flex align-items-center justify-content-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isChecked}
                  // disabled={row.extensionStatus === "Closed"}
                  disabled={isToggleDisabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const extDays = parseInt(formInputs.days) || 0;

                    // ── 1. Compute updated nursing item ──────────────────────────────────────
                    const isClosureFlow = ["ELCS", "PELCS"].includes(
                      formInputs.pcCallStatusCode,
                    );
                    const applyStatusToggle = (item) => {
                      if (item.local_id !== row.local_id) return item;

                      // Closed → Open (RESTORE)
                      if (checked) {
                        return restoreItemToOpen(item, backendNursingItems);
                      }

                      // Open → Closed under ELCS/PELCS (CLOSURE)
                      // if (isClosureFlow) {
                      //   return {
                      //     ...item,
                      //     extensionStatus: "Closed",
                      //     closedGrossValue: item.grossValue,
                      //     closedNetValue: item.netValue,
                      //     closedTaxAmount: item.taxAmount,
                      //     itemEarlyClosureDate:
                      //       formInputs.earlyClosureDate || "",
                      //     itemEarlyClosureTime:
                      //       formInputs.earlyClosureTime || "",
                      //   };
                      // }
                      if (isClosureFlow) {
                        return {
                          ...item,
                          extensionStatus: "Closed",
                          itemEarlyClosureDate:
                            formInputs.earlyClosureDate || "",
                          itemEarlyClosureTime:
                            formInputs.earlyClosureTime || "",
                        };
                      }

                      // Open → Closed under PEXT (EXTENSION FLOW) — original behaviour
                      const snap = {
                        previousGrossValue:
                          item.previousGrossValue ?? item.grossValue,
                        previousNetValue:
                          item.previousNetValue ?? item.netValue,
                        previousTaxAmount:
                          item.previousTaxAmount ?? item.taxAmount,
                      };
                      if (extDays > 0) {
                        const qty = parseFloat(item.orderedQuantity) || 1;
                        const price = parseFloat(item.sellingPrice) || 0;
                        const tax = parseFloat(item.taxPercentage) || 0;
                        const net = parseFloat(
                          (price * extDays * qty).toFixed(2),
                        );
                        const taxAmt = parseFloat(
                          (net * (tax / 100)).toFixed(2),
                        );
                        const gross = parseFloat((net + taxAmt).toFixed(2));
                        return {
                          ...item,
                          ...snap,
                          extensionStatus: "Closed",
                          netValue: net,
                          taxAmount: taxAmt,
                          grossValue: gross,
                          effectiveGrossValue: gross,
                          grossValueInput: undefined,
                        };
                      }
                      return { ...item, ...snap, extensionStatus: "Closed" };
                    };

                    // ── 2. Build final arrays — status first, then refund on top ─────────────
                    // IMPORTANT: never call setNursingItems twice; the second call would
                    // overwrite the extensionStatus set by the first.
                    const isPelcs =
                      formInputs.pcCallStatusCode === "PELCS" &&
                      formInputs.earlyClosureDate;

                    let newRent = rentItems;
                    let newNursing = nursingItems.map(applyStatusToggle);

                    if (isPelcs) {
                      const closureDate = new Date(formInputs.earlyClosureDate);
                      const applyRefund = (items) =>
                        items.map((i) => {
                          if (i.isTotal || i.productReturned) return i;
                          if (normalizeStatus(i.extensionStatus) !== "Closed")
                            return { ...i, _refundDays: 0, _refundAmt: 0 };
                          const refBase = i.toDate
                            ? new Date(i.toDate)
                            : new Date(formInputs.endingDate);
                          const days = Math.max(
                            0,
                            Math.ceil(
                              (refBase - closureDate) / (1000 * 60 * 60 * 24),
                            ),
                          );
                          // Refund = gross (net + tax) — customer paid gross
                          const netAmt =
                            days *
                            (parseFloat(i.sellingPrice) || 0) *
                            (parseFloat(i.orderedQuantity) || 1);
                          const taxRate = parseFloat(i.taxPercentage) || 0;
                          const amt = parseFloat(
                            (netAmt * (1 + taxRate / 100)).toFixed(2),
                          );
                          return { ...i, _refundDays: days, _refundAmt: amt };
                        });
                      newRent = applyRefund(newRent);
                      newNursing = applyRefund(newNursing);
                    }

                    setRentItems(newRent);
                    setNursingItems(newNursing);

                    // ── 3. Track selection for payload ────────────────────────────────────
                    setSelectedExtensionItems((prev) => ({
                      ...prev,
                      [row.local_id]: checked ? "Yes" : "No",
                    }));

                    // ── 4. Update form-level refund totals for PELCS ──────────────────────
                    if (isPelcs) {
                      const closureDate = new Date(formInputs.earlyClosureDate);
                      const totalAmt = [...newRent, ...newNursing]
                        .filter((i) => !i.isTotal)
                        .reduce(
                          (s, i) => s + (parseFloat(i._refundAmt) || 0),
                          0,
                        );
                      const totalDays = formInputs.endingDate
                        ? Math.max(
                            0,
                            Math.ceil(
                              (new Date(formInputs.endingDate) - closureDate) /
                                (1000 * 60 * 60 * 24),
                            ),
                          )
                        : 0;
                      setFormInputs((prev) => ({
                        ...prev,
                        refundDays: totalDays,
                        refundAmount: totalAmt.toFixed(2),
                      }));
                    }
                  }}
                />
                <label className="form-check-label ms-1 small text-muted">
                  {isChecked ? "Yes" : "No"}
                </label>
              </div>
            </div>
          );
        },
      },

      // SELECT — bulk-restore checkbox for closed eligible items
      {
        name: "Select",
        width: "80px",
        cell: (row) => {
          if (row.isTotal) return null;
          if (!isRestoreEligible(row)) return null;

          // const canSelect =
          //   _isClosureMode &&
          //   isPCDEditing &&
          //   canEditOnThisRecord &&
          //   !isPaymentCollected;
          const canSelect =
            _isClosureMode &&
            isPCDEditing &&
            canEditOnThisRecord &&
            !isBillLocked;

          const checked = restoreSelection.has(row.local_id);

          return (
            <input
              type="checkbox"
              className="form-check-input"
              checked={checked}
              disabled={!canSelect}
              onChange={() => {
                setRestoreSelection((prev) => {
                  const next = new Set(prev);
                  if (next.has(row.local_id)) next.delete(row.local_id);
                  else next.add(row.local_id);
                  return next;
                });
              }}
              title={
                !canSelect
                  ? isPaymentCollected
                    ? "Payment collected — restore not allowed"
                    : "Edit mode required"
                  : "Select for restore"
              }
            />
          );
        },
      },

      // FROM DATE — live pending preview for nursing items
      {
        name: "From",
        width: "150px",
        cell: (row) => {
          if (row.isTotal) return null;

          const isExtFlow = ["EXT", "PEXT"].includes(
            formInputs.pcCallStatusCode,
          );
          const isOpen = normalizeStatus(row.extensionStatus) !== "Closed";
          // const hasPendingExtDates =
          //   isExtFlow && isOpen && !!formInputs.endingDate;

          const hasPendingExtDates =
            isExtFlow &&
            isOpen &&
            !!formInputs.endingDate &&
            isPCDEditing &&
            canEditOnThisRecord;

          let displayFromDate = row.fromDate;
          if (row.extensionItemsdetails?.length > 0) {
            const latestExt =
              row.extensionItemsdetails[row.extensionItemsdetails.length - 1];
            displayFromDate = latestExt.extensionFromDate || row.fromDate;
          }
          // if (hasPendingExtDates && baseEndingDate) {
          //   displayFromDate = baseEndingDate;
          // }
          if (hasPendingExtDates && baseEndingDate) {
            // Preview must match the save: extension starts the day AFTER the previous
            // toDate, never the same day (avoids overlap / double-billing).
            displayFromDate = addOneDay(baseEndingDate);
          }

          return (
            <InputField
              className={`inputBox${hasPendingExtDates ? " border-warning" : ""}`}
              type="date"
              value={displayFromDate}
              disabled
            />
          );
        },
      },
      // TO DATE — live pending preview for nursing items; original to-date otherwise
      {
        name: "To Date",
        width: "150px",
        cell: (row) => {
          if (row.isTotal) return null;

          const isExtFlow = ["EXT", "PEXT"].includes(
            formInputs.pcCallStatusCode,
          );
          const isOpen = normalizeStatus(row.extensionStatus) !== "Closed";
          // const hasPendingExtDates =
          //   isExtFlow && isOpen && !!formInputs.endingDate;

          const hasPendingExtDates =
            isExtFlow &&
            isOpen &&
            !!formInputs.endingDate &&
            isPCDEditing &&
            canEditOnThisRecord;

          let displayDate = row.toDate;
          if (row.extensionItemsdetails?.length > 0) {
            const latestExt =
              row.extensionItemsdetails[row.extensionItemsdetails.length - 1];
            displayDate = latestExt.extensionToDate || row.toDate;
          }
          if (hasPendingExtDates) displayDate = formInputs.endingDate;

          return (
            <InputField
              className={`inputBox${hasPendingExtDates ? " border-warning" : ""}`}
              type="date"
              value={displayDate}
              disabled
            />
          );
        },
      },
      // DAYS — shows pending days count for EXT/PEXT; original days otherwise
      {
        name: "Days",
        width: "120px",
        cell: (row) => {
          if (row.isTotal) return null;

          const isExtFlow = ["EXT", "PEXT"].includes(
            formInputs.pcCallStatusCode,
          );
          const isOpen = normalizeStatus(row.extensionStatus) !== "Closed";
          // const hasPendingExtDays = isExtFlow && isOpen && !!formInputs.days;
          const hasPendingExtDays =
            isExtFlow &&
            isOpen &&
            !!formInputs.days &&
            isPCDEditing &&
            canEditOnThisRecord;

          let displayDays = row.days;
          if (row.extensionItemsdetails?.length > 0) {
            const latestExt =
              row.extensionItemsdetails[row.extensionItemsdetails.length - 1];
            displayDays = latestExt.extendedDays || row.days;
          }
          if (hasPendingExtDays) displayDays = formInputs.days;

          return (
            <InputField
              className={`inputBox${hasPendingExtDays ? " border-warning" : ""}`}
              type="number"
              value={displayDays}
              disabled={true}
            />
          );
        },
      },

      {
        name: "History",
        width: "80px",
        cell: (row) => {
          if (row.isTotal) return null;

          const historyCount = row.extensionItemsdetails?.length || 0;

          return (
            <div className="text-center">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => showExtensionHistory(row)}
                disabled={historyCount === 0}
                title={
                  historyCount > 0
                    ? `View ${historyCount} extension(s)`
                    : "No history"
                }
              >
                <i className="bi bi-clock-history me-1"></i>
                {historyCount > 0 && (
                  <span className="badge bg-primary rounded-pill ms-1">
                    {historyCount}
                  </span>
                )}
              </button>
            </div>
          );
        },
        sortable: false,
      },
    ];

    // FINANCIAL COLUMNS
    const financialCols = [
      {
        name: "Qty.",
        width: "120px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold text-center w-100">Total</div>
          ) : (
            <InputField
              className="inputBox"
              type="number"
              name="orderedQuantity"
              value={row.orderedQuantity}
              min="1"
              onChange={(e) => handleLineItemChange(row.local_id, e, "Nursing")}
              disabled={true}
            />
          ),
      },
      {
        name: "Previous Total",
        width: "120px",
        cell: (row) =>
          row.isTotal ? null : (
            <InputField
              className="inputBox"
              type="number"
              value={row.previousGrossValue || row.grossValue || ""}
              disabled
            />
          ),
      },
      {
        name: "Net Value",
        width: "130px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold">{row.netValue}</div>
          ) : (
            <InputField
              className="inputBox"
              type="number"
              value={row.netValue}
              disabled
            />
          ),
      },
      {
        name: "GST (%)",
        width: "100px",
        cell: (row) =>
          row.isTotal ? null : (
            <InputField
              className="inputBox"
              type="number"
              value={row.taxPercentage}
              disabled
            />
          ),
      },
      {
        name: "Tax Amount",
        width: "120px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold">{row.taxAmount}</div>
          ) : (
            <InputField
              className="inputBox"
              type="number"
              value={row.taxAmount}
              disabled
            />
          ),
      },
      {
        name: "Amount",
        width: "120px",
        cell: (row) => {
          if (row.isTotal) {
            return <div className="fw-bold">{row.grossValue}</div>;
          }

          // const canEditAmount = canEditExtensionAmount(row);
          const canEditAmount =
            canEditExtensionAmount(row) || canEditClosureAmount(row);
          const displayValue =
            row.grossValueInput !== undefined
              ? row.grossValueInput
              : (row.effectiveGrossValue ?? row.grossValue ?? "");

          const applyGrossValue = (rawValue) => {
            setNursingItems((prev) =>
              prev.map((item) => {
                if (item.local_id !== row.local_id) return item;

                const finalValue = rawValue === "" ? 0 : rawValue;

                return {
                  ...item,
                  previousGrossValue:
                    item.previousGrossValue ?? item.grossValue,
                  previousNetValue: item.previousNetValue ?? item.netValue,
                  previousTaxAmount: item.previousTaxAmount ?? item.taxAmount,
                  grossValueInput: undefined,
                  ...recalculateFinancialsFromGross(item, finalValue),
                };
              }),
            );
          };

          return (
            <InputField
              className="inputBox"
              type="number"
              value={displayValue}
              disabled={!canEditAmount}
              onChange={(e) => {
                const rawValue = e.target.value;

                setNursingItems((prev) =>
                  prev.map((item) =>
                    item.local_id === row.local_id
                      ? {
                          ...item,
                          grossValueInput: rawValue,
                        }
                      : item,
                  ),
                );
              }}
              onBlur={(e) => applyGrossValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyGrossValue(e.target.value);
                }
              }}
            />
          );
        },
      },
      // // NEW — Bill On Closure (visible always; editable only in ELCS/PELCS for closed items)
      // {
      //   name: "Bill On Closure",
      //   width: "140px",
      //   cell: (row) => {
      //     // if (row.isTotal) return null;
      //     if (row.isTotal) {
      //       return row.closedGrossTotal ? (
      //         <div className="fw-bold">{row.closedGrossTotal}</div>
      //       ) : null;
      //     }

      //     const isClosed = normalizeStatus(row.extensionStatus) === "Closed";
      //     // const canEditClosedGross =
      //     //   _isClosureMode &&
      //     //   isClosed &&
      //     //   !row.productReturned &&
      //     //   !row.movedToChildExtension &&
      //     //   !isCreditNoted(row) &&
      //     //   !isPaymentCollected &&
      //     //   isLatestExtension;

      //     const canEditClosedGross =
      //       _isClosureMode &&
      //       isClosed &&
      //       !row.productReturned &&
      //       !row.movedToChildExtension &&
      //       !isCreditNoted(row) &&
      //       !isPaymentCollected &&
      //       isPCDEditing &&
      //       canEditOnThisRecord;

      //     const displayValue =
      //       row.closedGrossValue !== undefined && row.closedGrossValue !== null
      //         ? row.closedGrossValue
      //         : isClosed
      //           ? row.grossValue
      //           : "";

      //     const tooltip = !_isClosureMode
      //       ? "Available only under Early Closure / Partial Early Closure"
      //       : !isClosed
      //         ? "Only closed items have a closure bill"
      //         : row.productReturned
      //           ? "Item already collected — locked"
      //           : row.movedToChildExtension
      //             ? "Item moved to child extension"
      //             : isPaymentCollected
      //               ? "Payment already collected — use Refund Amt to issue a refund"
      //               : !isLatestExtension
      //                 ? "Only the latest extension can be edited"
      //                 : "Editable: this value contributes to Net Amount";

      //     return (
      //       <InputField
      //         className="inputBox"
      //         type="number"
      //         min="0"
      //         value={displayValue}
      //         disabled={!canEditClosedGross}
      //         title={tooltip}
      //         onChange={(e) => {
      //           const raw = e.target.value;
      //           setRentItems((prev) =>
      //             prev.map((item) =>
      //               item.local_id === row.local_id
      //                 ? {
      //                     ...item,
      //                     ...recalculateClosedFinancialsFromGross(item, raw),
      //                   }
      //                 : item,
      //             ),
      //           );
      //         }}
      //       />
      //     );
      //   },
      // },
      // NEW — Per-item Early Closure Date (visible only in ELCS/PELCS)
      // ...(_isClosureMode
      ...(_isEarlyClosureMode
        ? [
            {
              name: "Early Closure Date",
              width: "160px",
              cell: (row) => {
                if (row.isTotal) return null;
                const isClosed =
                  normalizeStatus(row.extensionStatus) === "Closed";
                const canEditDate =
                  isClosed &&
                  !row.productReturned &&
                  !row.movedToChildExtension &&
                  !isCreditNoted(row) &&
                  isLatestExtension &&
                  isPCDEditing;
                return (
                  <InputField
                    className="inputBox"
                    type="date"
                    value={row.itemEarlyClosureDate || ""}
                    min={formInputs.startingDate}
                    max={formInputs.endingDate}
                    disabled={!canEditDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setNursingItems((prev) =>
                        prev.map((item) =>
                          item.local_id === row.local_id
                            ? { ...item, itemEarlyClosureDate: v }
                            : item,
                        ),
                      );
                    }}
                  />
                );
              },
            },
          ]
        : []),
    ];

    // BARCODE COLUMN
    const barcodeEnteringColumn = [
      {
        name: "Barcode",
        width: "180px",
        cell: (row) =>
          row.isTotal ? (
            <div className="fw-bold text-end w-100">--</div>
          ) : (
            <InputField
              className="inputBox"
              name="barcode"
              value={row.barcode}
              disabled={true}
            />
          ),
      },
      {
        name: "Refund Days",
        width: "110px",
        // cell: (row) => {
        //   if (row.isTotal) return null;
        //   return (
        //     <InputField
        //       className="inputBox"
        //       type="number"
        //       value={row._refundDays ?? row.refundDays ?? 0}
        //       disabled={true}
        //       title="Auto-calculated from closure date"
        //       onChange={() => {}}
        //     />
        //   );
        // },
        cell: (row) => {
          if (row.isTotal) return null;
          const isEarlyClosureFlow = ["ELCS", "PELCS"].includes(
            formInputs.pcCallStatusCode,
          );
          const isClosed = normalizeStatus(row.extensionStatus) === "Closed";
          // const isEditing = isEarlyClosureFlow && isClosed;
          const isEditing = isEarlyClosureFlow && isClosed && !row.productReturned;
          return (
            <InputField
              className="inputBox"
              type="number"
              value={row._refundDays ?? row.refundDays ?? 0}
              disabled={!isEditing || !isPCDEditing}
              onChange={(e) => {
                const days = Math.max(0, parseFloat(e.target.value) || 0);
                setRentItems((prev) =>
                  prev.map((i) =>
                    i.local_id === row.local_id
                      ? { ...i, _refundDays: days }
                      : i,
                  ),
                );
              }}
            />
          );
        },
      },
      {
        name: "Refund Amt.",
        width: "130px",
        cell: (row) => {
          if (row.isTotal) {
            const totalRefund = nursingItems
              .filter(
                (i) =>
                  !i.isTotal && normalizeStatus(i.extensionStatus) === "Closed",
              )
              .reduce(
                (s, i) => s + (parseFloat(i._refundAmt ?? i.refundAmt) || 0),
                0,
              );
            return <div className="fw-bold">{totalRefund.toFixed(2)}</div>;
          }
          const isEarlyClosureFlow = ["ELCS", "PELCS"].includes(
            formInputs.pcCallStatusCode,
          );
          const isClosed = normalizeStatus(row.extensionStatus) === "Closed";
          const isLocked = row.productReturned === true;
          const isEditing = isEarlyClosureFlow && isClosed && !isLocked;
          return (
            <InputField
              className="inputBox"
              type="number"
              value={row._refundAmt ?? row.refundAmt ?? 0}
              disabled={!isEditing || !isPCDEditing}
              title={
                isLocked
                  ? "Already stocked in — refund locked"
                  : !isEarlyClosureFlow
                    ? "Select Early Closure to edit"
                    : !isClosed
                      ? "Only closed items have refund"
                      : "Refund amount (editable)"
              }
              onChange={(e) => {
                const amt = Math.max(0, parseFloat(e.target.value) || 0);
                setNursingItems((prev) =>
                  prev.map((i) =>
                    i.local_id === row.local_id ? { ...i, _refundAmt: amt } : i,
                  ),
                );
                const totalAmt =
                  rentItems
                    .filter(
                      (i) =>
                        !i.isTotal &&
                        normalizeStatus(i.extensionStatus) === "Closed",
                    )
                    .reduce((s, i) => s + (parseFloat(i._refundAmt) || 0), 0) +
                  nursingItems
                    .map((i) =>
                      i.local_id === row.local_id
                        ? { ...i, _refundAmt: amt }
                        : i,
                    )
                    .filter(
                      (i) =>
                        !i.isTotal &&
                        normalizeStatus(i.extensionStatus) === "Closed",
                    )
                    .reduce((s, i) => s + (parseFloat(i._refundAmt) || 0), 0);
                setFormInputs((prev) => ({
                  ...prev,
                  refundAmount: totalAmt.toFixed(2),
                }));
              }}
            />
          );
        },
      },
    ];

    return [
      ...baseCols,
      ...middleCols,
      ...financialCols,
      ...barcodeEnteringColumn,
    ];
  };

  const refundHistoryColumns = [
    { name: "Date", selector: (r) => r.refundDate, width: "120px" },
    { name: "Refund Days", selector: (r) => r.refundDays, width: "110px" },
    {
      name: "Products Refund Amt",
      selector: (r) => r.productsRefundAmount,
      width: "175px",
    },
    {
      name: "Additional Amt",
      selector: (r) => r.additionalRefundAmount,
      width: "120px",
    },
    {
      name: "Total",
      cell: (r) => <strong>₹ {r.totalRefundAmount}</strong>,
      width: "120px",
    },
    { name: "Type", selector: (r) => r.refundType, width: "175px" },
    { name: "Remarks", selector: (r) => r.refundRemarks, width: "245px" },
    { name: "Entered By", selector: (r) => r.enteredBy, width: "130px" },
  ];

  const customStyles = {
    rows: {
      style: (row) => ({
        backgroundColor: row.isTotal ? "#cfe2ff" : "inherit",
        color: row.isTotal ? "#084298" : "inherit",
        fontWeight: row.isTotal ? "bold" : "normal",
      }),
    },
  };
  // eslint-disable-next-line
  const showApproveModal = async (name) => {
    setUpdateModalShow(true);
  };
  const updateHideModal = () => {
    setUpdateModalShow(false);
  };

  const handleValueChange = (remarks) => {
    const incrementVersion = (version = "V0") => {
      const num = parseInt(version.replace("V", ""), 10) || 0;
      return `V${num + 1}`;
    };

    const newVersion = incrementVersion(formInputs.version);

    const newRemarks = [
      ...(formInputs.extenssion || []),
      {
        remark: remarks,
        version: newVersion,
        // status: "Extended",
      },
    ];

    setFormInputs((prev) => ({
      ...prev,
      version: newVersion, //  header updated
      updateRemarks: newRemarks, //  extension updated
    }));

    handleUpdate(newRemarks);
  };

  const itemLevelHideModal = () => {
    setItemLevelModalShow(false);
  };

  const remarkLineItemColInfo = [
    {
      name: "Remarks",
      cell: (row) => <span>{row?.remark || ""} </span>,
      width: "180px",
    },
    {
      name: "Version",
      cell: (row) => <span>{row?.version || ""} </span>,
      width: "180px",
    },
  ];
  // eslint-disable-next-line
  const remarkItemShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(remarkLineItemColInfo);
    setModalTitle("Updated Remarks / Version");
    setItemLevelModalShow(true);
  };

  //eslint-disable-next-line
  const showExtenssionModla = (data) => {
    setModalShowExtenssionModal(true);
    setExtenssionData(data);
  };

  const hideModalExtenssionModal = () => {
    setModalShowExtenssionModal(false);
  };

  const handleSaveExtensions = (extensionData) => {
    setRentItems((prevItems) =>
      prevItems.map((item) =>
        item.local_id === extensionData.local_id
          ? {
              ...item,
              // Store extension details array
              extensionItemsdetails: extensionData.extensionItemsdetails || [],

              // Update dates and days only if extensions exist
              ...(extensionData.extensionItemsdetails &&
                extensionData.extensionItemsdetails.length > 0 && {
                  // First extension's from date
                  fromDate:
                    extensionData.extensionItemsdetails[0].extensionfromDate,

                  // Last extension's to date
                  toDate:
                    extensionData.extensionItemsdetails[
                      extensionData.extensionItemsdetails.length - 1
                    ].extensiontoDate,

                  // Total of all extension days
                  days: extensionData.extensionItemsdetails
                    .reduce(
                      (sum, ext) => sum + parseInt(ext.extendedDays || 0),
                      0,
                    )
                    .toString(),
                }),
            }
          : item,
      ),
    );
  };
  const userDataPC = JSON.parse(localStorage.getItem("userData"));

  const hasPCDRole = userDataPC?.data?.roles?.some(
    (r) => r.roleId === "SALES-PCD",
  );
  const isPCDisabled = !hasPCDRole;

  const [nursingData, setNursingData] = useState([]);

  const generateDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return [];
    const dates = [];
    // Normalize to YYYY-MM-DD to avoid timezone/time-component issues
    const startStr = String(fromDate).split("T")[0];
    const endStr = String(toDate).split("T")[0];
    const start = new Date(startStr + "T00:00:00");
    const end = new Date(endStr + "T00:00:00");

    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end)
      return [];

    // Use local date components (not toISOString which converts to UTC)
    const padZ = (n) => String(n).padStart(2, "0");

    while (start <= end) {
      const localDate = `${start.getFullYear()}-${padZ(start.getMonth() + 1)}-${padZ(start.getDate())}`;
      dates.push({
        date: localDate,
        dayName: start.toLocaleDateString("en-US", { weekday: "long" }),
        status: "",
      });

      start.setDate(start.getDate() + 1);
    }

    return dates;
  };

  const [isAttendanceUpdated, setIsAttendanceUpdated] = useState(false);

  const [editingRows, setEditingRows] = useState({});
  // eslint-disable-next-line
  const [replacementNurseDetails, setReplacementNurseDetails] = useState([]);

  const handleEnableEdit = (dateKey, index) => {
    setEditingRows((prev) => ({
      ...prev,
      [`${dateKey}-${index}`]: true,
    }));
  };

  const handleDisableEdit = (dateKey, index) => {
    setEditingRows((prev) => ({
      ...prev,
      [`${dateKey}-${index}`]: false,
    }));
  };

  const [viewDetailsModal, setViewDetailsModal] = useState({
    show: false,
    data: null,
    dateStr: null,
    isReplacement: false,
    replacementNurseDetails: null,
  });

  const handleAttendanceChangeMultiple = (items, index, field, value) => {
    // Unified: always update nursingData regardless of EQ or EXT record
    setNursingData((prev) =>
      prev.map((group) => ({
        ...group,
        attendance: group.attendance.map((row, i) => {
          if (i !== index) return row;

          if (
            value === "Replacement Present" ||
            value === "Replacement Absent"
          ) {
            setReplacementNurseDetails((prevRep) => [
              ...prevRep,
              {
                date: row.date,
                fromDate: group.fromDate,
                toDate: group.toDate,
                oldNurseName: row.nurseName,
                oldNurseUserId: row.nurseUserId,
                replacedOn: new Date().toISOString(),
              },
            ]);

            return {
              ...row,
              status: value,
            };
          }

          return {
            ...row,
            [field]: value,
          };
        }),
      })),
    );
  };

  const buildNursingAttendancePayload = () => {
    return (nursingData || []).map((item) => ({
      version: item.version || "",
      local_id: item.local_id,
      materialCode: item.materialCode,
      materialDescription: item.materialDescription,
      barcode: item.barcode || "",
      fromDate: item.fromDate,
      toDate: item.toDate,
      orderedQuantity: item.orderedQuantity,

      //  Replacement nurse details
      replacementStartDate: formInputs?.replacementStartDate || "",
      replacementEndDate: formInputs?.replacementEndDate || "",
      replacementNurseName: formInputs?.replacementNurseName || "",
      replacementNurseUserId: formInputs?.replacementNurseUserId || "",
      replacementNurseAssociateNumber:
        formInputs?.replacementNurseAssociateNumber || "",
      replacementNurseMobileNr: formInputs?.replacementNurseMobileNr || "",
      replacementNurseEmail: formInputs?.replacementNurseEmail || "",
      replacementNurseAddress: formInputs?.replacementNurseAddress || "",

      attendance: (item.attendance || []).map((day) => {
        //  NEW: Check if THIS day marked as Replacement
        const isReplacementDay =
          day.status === "Replacement Present" ||
          day.status === "Replacement Absent" ||
          (replacementNurseMap && replacementNurseMap[day.date]);

        //  Get correct nurse based on replacement status
        let nurseForThisDay;
        if (isReplacementDay && formInputs?.replacementNurseName) {
          // Use REPLACEMENT nurse
          nurseForThisDay = {
            nurseName: formInputs?.replacementNurseName,
            nurseMobileNr: formInputs?.replacementNurseMobileNr,
            nurseUserId: formInputs?.replacementNurseUserId,
            AssociateNumber: formInputs?.replacementNurseAssociateNumber,
            email: formInputs?.replacementNurseEmail,
            address: formInputs?.replacementNurseAddress,
          };
        } else {
          // Use ORIGINAL nurse
          nurseForThisDay = getNurseDetailsForDate(day.date);
        }

        return {
          date: day.date,
          dayName: day.dayName,
          status: day.status || "",

          // IMPORTANT: Send correct nurse details
          nurseName: nurseForThisDay.nurseName,
          nurseMobileNr: nurseForThisDay.mobileNr,
          nurseUserId: nurseForThisDay.userId,
          nurseAssociateNumber: nurseForThisDay.AssociateNumber,
          nurseEmail: nurseForThisDay.email,
          nurseAddress: nurseForThisDay.address,

          //  Mark if replacement
          isReplacement: isReplacementDay,
          replacementFromDate: isReplacementDay
            ? formInputs?.replacementStartDate
            : "",
          replacementToDate: isReplacementDay
            ? formInputs?.replacementEndDate
            : "",
        };
      }),

      presentCount: (item.attendance || []).filter(
        (d) => d.status === "Present" || d.status === "Replacement Present",
      ).length,
      absentCount: (item.attendance || []).filter(
        (d) => d.status === "Absent" || d.status === "Replacement Absent",
      ).length,
      totalDays: (item.attendance || []).length,
    }));
  };

  const handleSaveServiceAction = async () => {
    setLoading(true);
    try {
      const currentDocNr =
        formInputs.documentNumber || documentNumber || equipmentNr;
      const isExtensionRecord =
        currentDocNr?.startsWith("EX") || currentDocNr?.includes("ERFEXT");
      const payload = {
        documentNumber: formInputs.documentNumber,
        equipmentRentNr: formInputs.documentNumber,
        serviceAction: formInputs.serviceAction,
      };
      if (isExtensionRecord) {
        await ItemService.updateExtendEquipmentRent(payload);
      } else {
        await ItemService.updateEquipmentRentForm(payload);
      }
      toast.success("Service action saved");
    } catch {
      toast.error("Failed to save service action");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS mb-3">
            {/* HEADER */}
            <Row className="mb-4">
              <Row>
                <Col xs={12} md={8}>
                  <h2 className="mainTitle">
                    ERF Detail - {formInputs.patientName}
                    <span className="bg-table-status bg-table-documentNumber-status headerStatusDetailedPage ms-2">
                      {documentNumber || formInputs.equipmentRentNr}
                    </span>
                  </h2>
                  <p className="subText">Equipment Rental Details</p>
                </Col>
              </Row>
            </Row>
            <hr />

            <Loader isLoading={loading} className={"text-center"} />

            {!loading && (
              <>
                {/* ORDER DETAILS ACCORDION */}
                <div className="accordion mb-4" id="accordionExample">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col xs={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${isCardVisible ? "bi-chevron-down" : "bi-chevron-right"}`}
                            onClick={toggleCardVisibility}
                          ></i>
                        </Col>
                        <Col xs={1} className="text-center logoBtn">
                          <span className="iconLogo">OD</span>
                        </Col>
                        <Col xs={10}>
                          <span className="headerTitle">Order Details</span>
                          <span className="subHeaderTitle">
                            Basic information
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${isCardVisible ? "visible" : ""}`}
                    >
                      <div className="accordion-body pb-4">
                        {/* Order & Patient Fields */}
                        <Row className="pt-2">
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="ERF Type"
                              data={erfTypeOptions}
                              name="erfType"
                              textField="name"
                              setValue={formInputs?.erfType}
                              getvalue={setDropdownData}
                              // disabled={disabled}
                              disabled={true} // always disabled for editing ERF type
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Privilege Case"
                              data={privilegeOptions}
                              name="privilegeCase"
                              textField="name"
                              setValue={formInputs.privilegeCase}
                              getvalue={setDropdownData}
                              disabled={true} // always disabled for editing Privilage type
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              type="date"
                              label="Order Date"
                              name="orderDate"
                              value={formInputs.orderDate}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Sale Point"
                              data={SALE_POINT_OPTIONS}
                              name="salePoint"
                              textField="name"
                              setValue={formInputs.salePoint}
                              getvalue={setDropdownData}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Order Type"
                              data={ORDER_TYPE_OPTIONS}
                              name="orderType"
                              textField="name"
                              setValue={formInputs.orderType}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Source of Call"
                              data={SOURCE_OF_CALL_OPTIONS}
                              name="sourceOfCall"
                              textField="name"
                              setValue={formInputs.sourceOfCall}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Cost Center"
                              name="costCenter"
                              value={formInputs.costCenter}
                              onChange={handleInputChange}
                              disabled={true}
                            />
                          </Col>

                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Billing Name"
                              name="billingName"
                              value={formInputs?.billingName}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>

                          {APPROVAL_REQUIRED_CASES.includes(
                            formInputs.privilegeCase,
                          ) && (
                            <>
                              {/* <Col
                                xs={12}
                                md={12}
                                lg={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <label
                                  className="fw-bold mb-2"
                                  style={{ fontSize: "14px" }}
                                >
                                  Approval Level
                                </label>
                                <div className="d-flex gap-4 ps-3 p-3">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="approvalLevel"
                                      id="levelOne"
                                      checked={formInputs.approvalLevel === "1"}
                                      onChange={() =>
                                        setFormInputs((prev) => ({
                                          ...prev,
                                          approvalLevel: "1",
                                          approvers: [],
                                        }))
                                      }
                                    />
                                    <label
                                      className="form-check-label pointer"
                                      htmlFor="levelOne"
                                    >
                                      1 Level Approval
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="approvalLevel"
                                      id="levelTwo"
                                      checked={formInputs.approvalLevel === "2"}
                                      onChange={() =>
                                        setFormInputs((prev) => ({
                                          ...prev,
                                          approvalLevel: "2",
                                          approvers: [],
                                        }))
                                      }
                                      disabled={true}
                                    />
                                    <label
                                      className="form-check-label pointer"
                                      htmlFor="levelTwo"
                                    >
                                      2 Level Approval
                                    </label>
                                  </div>
                                </div>
                              </Col> */}

                              {/* <Row>
                                {(formInputs.approvalLevel === "1" ||
                                  formInputs.approvalLevel === "2") && (
                                  <Col
                                    xs={12}
                                    md={6}
                                    lg={3}
                                    className="commTopButtonRightLeftPadding mb-3"
                                  >
                                    <ComboboxField
                                      label="Select Level 1 Approver"
                                      data={APPROVED_BY_OPTIONS_LEVEL_ONE}
                                      name="approver_1"
                                      textField="name"
                                      required="true"
                                      getvalue={(data) =>
                                        handleApproverSelection(0, data)
                                      }
                                      setValue={
                                        formInputs.approverList[0]?.name
                                      }
                                      // disabled={!isFormEnabled}
                                      disabled={true}
                                    />
                                  </Col>
                                )}

                                {formInputs.approvalLevel === "2" && (
                                  <Col
                                    xs={12}
                                    md={6}
                                    lg={3}
                                    className="commTopButtonRightLeftPadding mb-3"
                                  >
                                    <ComboboxField
                                      label="Select Level 2 Approver"
                                      data={APPROVED_BY_OPTIONS_LEVEL_TWO}
                                      name="approver_2"
                                      required="true"
                                      textField="name"
                                      getvalue={(data) =>
                                        handleApproverSelection(1, data)
                                      }
                                      setValue={
                                        formInputs.approverList[1]?.name
                                      }
                                      // disabled={!isFormEnabled}
                                      disabled={true}
                                    />
                                  </Col>
                                )}
                              </Row> */}
                            </>
                          )}
                          {APPROVAL_REQUIRED_CASES.includes(
                            formInputs.privilegeCase,
                          ) && (
                            <>
                              {/* <Col
                                xs={12}
                                md={12}
                                lg={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <label
                                  className="fw-bold mb-2"
                                  style={{ fontSize: "14px" }}
                                >
                                  Approval Level
                                </label>
                                <div className="d-flex gap-4 ps-3 p-3">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="approvalLevel"
                                      id="levelOne"
                                      checked={formInputs.approvalLevel === "1"}
                                      onChange={() =>
                                        setFormInputs((prev) => ({
                                          ...prev,
                                          approvalLevel: "1",
                                          approvers: [],
                                        }))
                                      }
                                    />
                                    <label
                                      className="form-check-label pointer"
                                      htmlFor="levelOne"
                                    >
                                      1 Level Approval
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="approvalLevel"
                                      id="levelTwo"
                                      checked={formInputs.approvalLevel === "2"}
                                      onChange={() =>
                                        setFormInputs((prev) => ({
                                          ...prev,
                                          approvalLevel: "2",
                                          approvers: [],
                                        }))
                                      }
                                      disabled={true}
                                    />
                                    <label
                                      className="form-check-label pointer"
                                      htmlFor="levelTwo"
                                    >
                                      2 Level Approval
                                    </label>
                                  </div>
                                </div>
                              </Col> */}

                              <Row>
                                {/* Level 1 Dropdown - Appears for both Level 1 and Level 2 selection */}
                                {(formInputs.approvalLevel === "1" ||
                                  formInputs.approvalLevel === "2") && (
                                  <Col
                                    xs={12}
                                    md={6}
                                    lg={3}
                                    className="commTopButtonRightLeftPadding mb-3"
                                  >
                                    <ComboboxField
                                      label="Selected Approver"
                                      data={APPROVED_BY_OPTIONS_LEVEL_ONE}
                                      name="approver_1"
                                      textField="name"
                                      required="true"
                                      getvalue={(data) =>
                                        handleApproverSelection(0, data)
                                      }
                                      setValue={
                                        formInputs.approverList[0]?.name
                                      }
                                      // disabled={!isFormEnabled}
                                      disabled={true}
                                    />
                                  </Col>
                                )}

                                {/* Level 2 Dropdown - Appears ONLY if Level 2 is selected */}
                                {formInputs.approvalLevel === "2" && (
                                  <Col
                                    xs={12}
                                    md={6}
                                    lg={3}
                                    className="commTopButtonRightLeftPadding mb-3"
                                  >
                                    <ComboboxField
                                      label="Select Level 2 Approver"
                                      data={APPROVED_BY_OPTIONS_LEVEL_TWO}
                                      name="approver_2"
                                      required="true"
                                      textField="name"
                                      getvalue={(data) =>
                                        handleApproverSelection(1, data)
                                      }
                                      setValue={
                                        formInputs.approverList[1]?.name
                                      }
                                      // disabled={!isFormEnabled}
                                      disabled={true}
                                    />
                                  </Col>
                                )}
                              </Row>
                            </>
                          )}
                        </Row>

                        {/* ERF For Type Selection */}
                        <Row className="pt-2 mb-4 bg-light p-3 rounded">
                          <Col xs={12}>
                            <h6 className="text-primary mb-3">
                              ERF Type Selection
                            </h6>
                            <div className="d-flex gap-4">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="erfForType"
                                  id="erfForPatient"
                                  value="Patient"
                                  checked={erfForType === "Patient"}
                                  onChange={(e) => {
                                    setErfForType(e.target.value);
                                    setSelectedHospital(null);
                                  }}
                                  disabled={true}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="erfForPatient"
                                >
                                  <strong>Patient</strong>
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="erfForType"
                                  id="erfForHospital"
                                  value="Hospital"
                                  checked={erfForType === "Hospital"}
                                  onChange={(e) => {
                                    setErfForType(e.target.value);
                                    // Clear patient data
                                    setFormInputs((prev) => ({
                                      ...prev,
                                      patientName: "",
                                      contactNumber: "",
                                      age: "",
                                      weight: "",
                                      aadharNo: "",
                                    }));
                                  }}
                                  disabled={true}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="erfForHospital"
                                >
                                  <strong>Hospital</strong>
                                </label>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>
                {/* HOSPITAL INFORMATION SECTION - Only show for B2B */}
                {erfForType === "Hospital" && (
                  <div className="accordion mb-4" id="accordionHospital">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col xs={1} className="text-left arrowBtn">
                            <i className="bi bi-chevron-down"></i>
                          </Col>
                          <Col xs={1} className="text-center logoBtn">
                            <span className="iconLogo">HI</span>
                          </Col>
                          <Col xs={10}>
                            <span className="headerTitle">
                              Hospital Information
                            </span>
                          </Col>
                        </Row>
                      </h2>

                      <div className="accordion-collapse collapse show visible">
                        <div className="accordion-body pb-4">
                          {/* Hospital Selection */}
                          <Row className="mb-4">
                            <Col
                              xs={12}
                              md={6}
                              lg={4}
                              className="commTopButtonRightLeftPadding"
                            >
                              <ComboboxField
                                label="Select Hospital For ERF"
                                data={erfHospitalList}
                                name="selectedHospitalId"
                                textField="name"
                                // setValue={selectedHospital?.userId || ""}
                                setValue={
                                  selectedHospital
                                    ? selectedHospital.hospitalName ||
                                      selectedHospital.organisationName
                                    : ""
                                } // Show the NAME, not userId
                                getvalue={setDropdownData}
                                // disabled={!isFormEnabled}
                                isLoading={isErfLoadingHospitals}
                                placeholder="Select Hospital"
                                required={true}
                                disabled={true}
                              />
                            </Col>
                          </Row>

                          {/* Hospital Details Display (Read-Only) */}
                          <>
                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Hospital Name"
                                  name="hospitalName"
                                  value={formInputs.hospitalName || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Association Number"
                                  name="AssociateNumber"
                                  value={formInputs.AssociateNumber || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Contact Number 123"
                                  name="mobileNr"
                                  value={formInputs.mobileNr || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Email"
                                  name="email"
                                  value={formInputs.email || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                            </Row>

                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="GST No"
                                  name="gstNo"
                                  value={formInputs.gstNo || ""}
                                  onChange={handleInputChange}
                                  // disabled={disabled || !isFormEnabled}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Address Line 1"
                                  name="address"
                                  value={formInputs.address || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Address Line 2"
                                  name="address2"
                                  value={formInputs.address2 || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="City"
                                  name="city"
                                  value={formInputs.city || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <TextAreaField
                                  className="inputBox"
                                  label="Billing Address"
                                  name="billingAddress"
                                  value={formInputs.billingAddress || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="State"
                                  name="billingState"
                                  value={formInputs.billingState || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Postal Code"
                                  name="billingPinCode"
                                  value={formInputs.billingPinCode || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                            </Row>
                          </>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="accordion mb-4" id="accordionExample">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            className={`bi ${isCardVisible3 ? "bi-chevron-down" : "bi-chevron-right"}`}
                            onClick={toggleCardVisibility3}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">PI</span>
                        </Col>
                        <Col xs={10}>
                          <span className="headerTitle">
                            Patient Information
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${isCardVisible3 ? "visible" : ""}`}
                    >
                      <div className="accordion-body pb-4">
                        <h6 className="text-primary mb-3 ps-2 mt-2">
                          Doctor Information
                        </h6>
                        <Row>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Dr. Type"
                              data={DOCTOR_TYPE_OPTIONS}
                              name="drType"
                              textField="name"
                              setValue={formInputs.drType}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>

                          {/* Show Hospital Dropdown when drType is "Hospital" or "Listed" */}
                          {(formInputs.drType === "Hospital" ||
                            formInputs.drType === "Listed") && (
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <ComboboxField
                                label="Hospital"
                                data={hospitalList}
                                name="hospital"
                                textField="name"
                                setValue={formInputs.hospital}
                                getvalue={setDropdownData}
                                disabled={disabled || !isFormEnabled}
                                isLoading={isLoadingHospitals}
                                placeholder="Select Hospital"
                              />
                            </Col>
                          )}
                          {/* Doctor Name Field - Dynamic based on Dr. Type */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            {formInputs.drType === "Hospital" ? (
                              // Hospital → Dropdown with doctors from selected hospital
                              <ComboboxField
                                label="Doctor Name"
                                data={doctorsList}
                                name="doctorName"
                                textField="name"
                                setValue={formInputs.doctorName}
                                getvalue={setDropdownData}
                                disabled={
                                  !formInputs.hospital ||
                                  !isFormEnabled ||
                                  disabled
                                }
                                isLoading={isLoadingDoctors}
                                placeholder={
                                  !formInputs.hospital
                                    ? "Select hospital first"
                                    : "Select Doctor"
                                }
                              />
                            ) : formInputs.drType === "Doctor" ? (
                              // Doctor → Dropdown with all doctors
                              <ComboboxField
                                label="Doctor Name"
                                data={doctorsList}
                                name="doctorName"
                                textField="name"
                                setValue={formInputs.doctorName}
                                getvalue={setDropdownData}
                                disabled={!isFormEnabled || disabled}
                                isLoading={isLoadingDoctors}
                                placeholder="Select Doctor"
                              />
                            ) : formInputs.drType === "Listed" ? (
                              // Listed → Dropdown with doctors from selected hospital (old logic)
                              <ComboboxField
                                label="Doctor Name"
                                data={doctorsList}
                                name="doctorName"
                                textField="name"
                                setValue={formInputs.doctorName}
                                getvalue={setDropdownData}
                                disabled={
                                  !formInputs.hospital ||
                                  !isFormEnabled ||
                                  disabled
                                }
                                isLoading={isLoadingDoctors}
                                placeholder={
                                  !formInputs.hospital
                                    ? "Select hospital first"
                                    : "Select Doctor"
                                }
                              />
                            ) : (
                              // Non-Listed, Not Given → Manual text input
                              <InputField
                                className="inputBox"
                                label="Doctor Name"
                                name="doctorName"
                                value={formInputs.doctorName}
                                onChange={handleInputChange}
                                disabled={
                                  formInputs.drType === "Not Given" ||
                                  !isFormEnabled ||
                                  disabled
                                }
                              />
                            )}
                          </Col>

                          {/* Dr. Code - Auto-filled for Hospital/Doctor/Listed, Manual for Non-Listed */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Dr. Code"
                              name="drCode"
                              value={formInputs.drCode}
                              onChange={handleInputChange}
                              disabled={
                                formInputs.drType === "Hospital" ||
                                formInputs.drType === "Doctor" ||
                                formInputs.drType === "Listed" ||
                                formInputs.drType === "Not Given" ||
                                !isFormEnabled ||
                                disabled
                              }
                            />
                          </Col>
                        </Row>
                        {/* ERF Autocomplete Search */}
                        <Row className="mb-4">
                          <Col xs={12}>
                            <div className="p-3 bg-light rounded border">
                              <h6 className="text-primary mb-3">
                                Quick Fill from previous ERF
                              </h6>

                              <ERFAutocompleteSearch
                                onERFSelect={handleERFSelection}
                                disabled={!isFormEnabled}
                                placeholder="Type patient name or ERF number (min. 2 characters)..."
                              />

                              {/* Preview of Loaded ERF */}
                              {isERFLoaded && selectedERF && (
                                <div
                                  className="alert alert-soft-primary d-flex align-items-center mt-3 border-0 shadow-sm"
                                  role="alert"
                                >
                                  <div className="fs-4 me-3 text-primary">
                                    <i className="bi bi-check-circle-fill"></i>
                                  </div>
                                  <div className="flex-grow-1">
                                    <h6 className="alert-heading fw-bold mb-1">
                                      ERF Loaded Successfully
                                    </h6>
                                    <p className="mb-0 small text-muted">
                                      <strong>ID:</strong>{" "}
                                      {selectedERF.documentNumber} &bull;
                                      <strong> Patient:</strong>{" "}
                                      {selectedERF.patientName} &bull;
                                      <strong> Contact:</strong>{" "}
                                      {selectedERF.contactNumber}
                                    </p>
                                  </div>
                                  <button
                                    className="btn btn-sm btn-outline-danger ms-3"
                                    onClick={() => {
                                      setSelectedERF(null);
                                      setIsERFLoaded(false);
                                    }}
                                  >
                                    <i className="bi bi-x-lg me-1"></i> Clear
                                  </button>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>

                        <hr className="my-3 text-muted" />

                        {/* Patient Info */}

                        <h6
                          className="text-primary mb-3 ps-2"
                          style={{ fontSize: "14px", fontWeight: "600" }}
                        >
                          Patient Information
                        </h6>
                        <Row>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Patient Name"
                              name="patientName"
                              value={formInputs.patientName}
                              onChange={handleInputChange}
                              required
                              error={validation.patientName}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Contact No"
                              name="mobileNr"
                              value={formInputs.mobileNr}
                              onChange={handleInputChange}
                              required
                              type="number"
                              error={validation.mobileNr}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={6}
                            md={3}
                            lg={2}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Age"
                              name="age"
                              value={formInputs.age}
                              onChange={handleInputChange}
                              type="number"
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={6}
                            md={3}
                            lg={2}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Weight (Kg)"
                              name="weight"
                              value={formInputs.weight}
                              onChange={handleInputChange}
                              type="number"
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={2}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="GST No"
                              name="gstNo"
                              value={formInputs.gstNo}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="PAN No"
                              name="panNo"
                              value={formInputs.panNo}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Aadhaar No"
                              name="aadharNo"
                              value={formInputs.aadharNo}
                              onChange={handleInputChange}
                              error={validation.aadharNo}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={2}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Is Diabetic?"
                              data={IS_DIABETIC_OPTIONS}
                              name="isDiabetic"
                              textField="name"
                              setValue={formInputs.isDiabetic}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={12}
                            lg={12}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <TextAreaField
                              className="inputBox"
                              label="Patient Challenges"
                              name="patientChallenges"
                              value={formInputs.patientChallenges}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                        </Row>

                        <hr className="my-3 text-muted" />

                        {/* Caregiver Info */}
                        <h6
                          className="text-primary mb-3 ps-2"
                          style={{ fontSize: "14px", fontWeight: "600" }}
                        >
                          Caregiver Information
                        </h6>
                        <Row>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Caregiver Name"
                              name="caregiverName"
                              value={formInputs.caregiverName}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Relationship"
                              name="caregiverRelation"
                              value={formInputs.caregiverRelation}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="ID Proof Type"
                              data={ID_PROOF_OPTIONS}
                              name="idProof"
                              textField="name"
                              setValue={formInputs.idProof}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          {formInputs.idProof &&
                            formInputs.idProof !== "Select" && (
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label={`${formInputs.idProof} Number`}
                                  name="idProofNumber"
                                  placeholder={`Enter ${formInputs.idProof} number`}
                                  value={formInputs.idProofNumber}
                                  onChange={handleInputChange}
                                  disabled={disabled || !isFormEnabled}
                                  required
                                  error={validation.idProofNumber}
                                />
                              </Col>
                            )}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Email ID"
                              name="emailId"
                              value={formInputs.emailId}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                        </Row>

                        {/* Addresses */}
                        <Row className="mt-2">
                          <Col md={6} className="pe-md-4">
                            <h6
                              className="text-primary mb-3 ps-2"
                              style={{ fontSize: "14px", fontWeight: "600" }}
                            >
                              Billing Address
                            </h6>
                            <Row>
                              <Col
                                xs={12}
                                md={12}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <TextAreaField
                                  className="inputBox"
                                  label="Address"
                                  name="billingAddress"
                                  value={formInputs.billingAddress}
                                  onChange={handleInputChange}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <ComboboxField
                                  label="State"
                                  data={STATE_OPTIONS}
                                  name="billingState"
                                  textField="name"
                                  setValue={formInputs.billingState}
                                  getvalue={setDropdownData}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Pin Code"
                                  name="billingPinCode"
                                  value={formInputs.billingPinCode}
                                  onChange={handleInputChange}
                                  type="number"
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                            </Row>
                          </Col>
                          <Col md={6} className="ps-md-4 border-start-md">
                            <h6
                              className="text-primary mb-3 ps-2"
                              style={{ fontSize: "14px", fontWeight: "600" }}
                            >
                              Delivery Address
                            </h6>
                            <Row>
                              <Col
                                xs={12}
                                md={12}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <TextAreaField
                                  className="inputBox"
                                  label="Address"
                                  name="deliveryAddress"
                                  value={formInputs.deliveryAddress}
                                  onChange={handleInputChange}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <ComboboxField
                                  label="State"
                                  data={STATE_OPTIONS}
                                  name="deliveryState"
                                  textField="name"
                                  setValue={formInputs.deliveryState}
                                  getvalue={setDropdownData}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Pin Code"
                                  name="deliveryPinCode"
                                  value={formInputs.deliveryPinCode}
                                  onChange={handleInputChange}
                                  type="number"
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="accordion mb-4" id="accordionExample">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            className={`bi ${isCardVisible4 ? "bi-chevron-down" : "bi-chevron-right"}`}
                            onClick={toggleCardVisibility4}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">PC</span>
                        </Col>
                        <Col xs={8} md={7} lg={8}>
                          <span className="headerTitle">
                            Information to be filled by PC Dept.
                          </span>
                          <span className="subHeaderTitle">
                            Basic information
                          </span>
                        </Col>
                        {isPCDUser &&
                          (documentNumber || equipmentNr) &&
                          canEditOnThisRecord && (
                            <Col
                              xs={4}
                              md={3}
                              lg={2}
                              className="d-flex justify-content-end mb-1 mt-1"
                            >
                              {!isPCDEditing ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={handlePCDEdit}
                                >
                                  <i className="bi bi-pencil-square me-2" />
                                  Edit PCD Section
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={handlePCDCancelEdit}
                                  disabled={loading}
                                >
                                  <i className="bi bi-x-circle me-2" />
                                  Cancel
                                </button>
                              )}
                            </Col>
                          )}
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${isCardVisible4 ? "visible" : ""}`}
                    >
                      <div className="accordion-body pb-4">
                        <Row className="pt-2">
                          {/* PC Call Status */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <ComboboxField
                              className="inputBox"
                              label="PC Call Status"
                              name="pcCallStatus"
                              code="pcCallStatusCode"
                              description="pcCallStatusDesc"
                              data={pcCallStatusOptions}
                              index="pcCallStatus"
                              iconClassName="dropdownIcon"
                              getvalue={handleComboboxChange} //  Use wrapper function
                              setValue={formInputs.pcCallStatus}
                              disabled={isPCDisabled || !isPCDEditing}
                            />
                          </Col>
                          {/* Days */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Days"
                              name="days"
                              type="number"
                              value={formInputs.days}
                              onChange={(e) => {
                                handleInputChange(e);
                                calcEndingDate(e.target.value);
                                autoRecalcExtensionAmounts(e.target.value);
                              }}
                              disabled={
                                !hasPCDRole ||
                                !canEditDays ||
                                !!formInputs.childExtensionNr ||
                                !isPCDEditing
                              }
                            />
                          </Col>

                          {/* Previous Days */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Previous Days"
                              name="previousDays"
                              value={formInputs.previousDays}
                              disabled
                            />
                          </Col>

                          {/* Starting Date */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Starting Date"
                              type="date"
                              name="startingDate"
                              value={formInputs.startingDate}
                              onChange={handleInputChange}
                              disabled={isPCDisabled || !isPCDEditing}
                            />
                          </Col>

                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Ending Date"
                              type="date"
                              name="endingDate"
                              // value={(() => {
                              //   let latestDate = formInputs.endingDate;

                              //   if (rentItems && rentItems.length > 0) {
                              //     rentItems.forEach((item) => {
                              //       if (
                              //         !item.isTotal &&
                              //         item.extensionItemsdetails &&
                              //         item.extensionItemsdetails.length > 0
                              //       ) {
                              //         const latestExt =
                              //           item.extensionItemsdetails[item.extensionItemsdetails.length - 1];
                              //         if (latestExt?.extensionToDate) {
                              //           if (!latestDate || latestExt.extensionToDate > latestDate) {
                              //             latestDate = latestExt.extensionToDate;
                              //           }
                              //         }
                              //       }
                              //     });
                              //   }

                              //   if (!latestDate && nursingItems && nursingItems.length > 0) {
                              //     nursingItems.forEach((item) => {
                              //       if (
                              //         !item.isTotal &&
                              //         item.extensionItemsdetails &&
                              //         item.extensionItemsdetails.length > 0
                              //       ) {
                              //         const latestExt =
                              //           item.extensionItemsdetails[item.extensionItemsdetails.length - 1];
                              //         if (latestExt?.extensionToDate) {
                              //           if (!latestDate || latestExt.extensionToDate > latestDate) {
                              //             latestDate = latestExt.extensionToDate;
                              //           }
                              //         }
                              //       }
                              //     });
                              //   }

                              //   return latestDate;
                              // })()}
                              value={formInputs.endingDate}
                              onChange={handleInputChange}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Early Closure Date"
                              type="date"
                              name="earlyClosureDate"
                              value={formInputs.earlyClosureDate}
                              onChange={handleInputChange}
                              min={formInputs.startingDate}
                              max={formInputs.endingDate}
                              disabled={isPCDisabled || !isPCDEditing}
                            />
                          </Col>

                          {/* Remarks */}
                          <Col
                            xs={12}
                            md={12}
                            lg={6}
                            className="commTopButtonRightLeftPadding"
                          >
                            <TextAreaField
                              className="inputBox"
                              label="Remarks"
                              name="pbRemarks"
                              value={formInputs.pbRemarks}
                              onChange={handleInputChange}
                              disabled={isPCDisabled || !isPCDEditing}
                            />
                          </Col>

                          {/* Payment Location */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <ComboboxField
                              className="inputBox"
                              label="Payment Location"
                              name="paymentLocation"
                              value={formInputs.paymentLocation}
                              onChange={handleInputChange}
                              // options={paymentLocationList}
                              // disabled={isPCDisabled || !isPCDEditing}
                              disabled
                            />
                          </Col>

                          {/* RE No */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="RE No"
                              name="reNo"
                              value={formInputs.reNo}
                              disabled
                            />
                          </Col>

                          {/* Visit Charges */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Visit Charges"
                              name="visitCharges"
                              type="number"
                              value={formInputs.visitCharges}
                              onChange={handleInputChange}
                              disabled={
                                isPCDisabled ||
                                disableDaysAndCharges ||
                                !!formInputs.childExtensionNr ||
                                !isPCDEditing
                              }
                            />
                          </Col>

                          {/* Total Amount */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Total Amount"
                              name="totalAmount"
                              value={formInputs.totalAmount}
                              disabled
                            />
                          </Col>

                          {/* Payment Received Date */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Payment Rec. Date"
                              type="date"
                              name="paymentReceivedDate"
                              value={formInputs.paymentReceivedDate}
                              onChange={handleInputChange}
                              // disabled={isPCDisabled || !isPCDEditing}
                              disabled={true}
                            />
                          </Col>

                          {/* ── Refund Section ── */}
                          {/* <Col
                            xs={12}
                            className="commTopButtonRightLeftPadding mt-3 mb-2"
                          >
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="wantToRefundToggle"
                                checked={wantToRefund}
                                onChange={(e) =>
                                  setWantToRefund(e.target.checked)
                                }
                                disabled={isPCDisabled}
                              />
                              <label
                                className="form-check-label fw-bold"
                                htmlFor="wantToRefundToggle"
                              >
                                Want to make a refund?
                              </label>
                            </div>
                          </Col> */}

                          <Col
                            xs={12}
                            className="commTopButtonRightLeftPadding mt-3 mb-2"
                          >
                            {/* Added d-flex and align-items-center to keep them vertically aligned */}
                            <div className="form-switch d-flex align-items-center">
                              {/* 1. Label comes FIRST now */}
                              <label
                                className="form-check-label fw-bold"
                                htmlFor="wantToRefundToggle"
                                style={{
                                  cursor: isPCDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                Want to make Refund?{" "}
                                <span
                                  className={
                                    wantToRefund
                                      ? "text-success ms-1"
                                      : "text-muted ms-1"
                                  }
                                >
                                  {wantToRefund ? "Yes" : "No"}
                                </span>
                              </label>

                              {/* 2. Checkbox comes SECOND */}
                              {/* Added 'm-0 ms-3' to remove Bootstrap's default weird negative margin and add a clean gap */}
                              <input
                                className="form-check-input m-0 ms-3"
                                type="checkbox"
                                id="wantToRefundToggle"
                                checked={wantToRefund}
                                onChange={(e) =>
                                  setWantToRefund(e.target.checked)
                                }
                                disabled={isPCDisabled || !isPCDEditing}
                                style={{
                                  cursor:
                                    isPCDisabled || !isPCDEditing
                                      ? "not-allowed"
                                      : "pointer",
                                }}
                              />
                            </div>
                          </Col>

                          {/* Refund Days */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Refund Days"
                              name="refundDays"
                              type="number"
                              value={formInputs.refundDays}
                              onChange={handleInputChange}
                              // disabled={isPCDisabled || !isPCDEditing}
                              disabled={
                                isPCDisabled || !wantToRefund || !isPCDEditing
                              }
                            />
                          </Col>

                          {/* Refund Amount */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Products Refund Amount"
                              name="refundAmount"
                              type="number"
                              value={formInputs.refundAmount}
                              onChange={handleInputChange}
                              disabled={
                                isPCDisabled || !wantToRefund || !isPCDEditing
                              }
                            />
                          </Col>
                          {/* Additional Refund Amount — manual entry by PCD */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Additional Refund Amount"
                              name="additionalRefundAmount"
                              type="number"
                              value={formInputs.additionalRefundAmount || ""}
                              onChange={handleInputChange}
                              disabled={
                                isPCDisabled || !wantToRefund || !isPCDEditing
                              }
                            />
                          </Col>

                          {/* Total Refund Amount — computed, always read-only */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Total Refund Amount"
                              name="totalRefundAmount"
                              type="number"
                              value={(
                                parseFloat(formInputs.refundAmount || 0) +
                                parseFloat(
                                  formInputs.additionalRefundAmount || 0,
                                )
                              ).toFixed(2)}
                              disabled={true}
                            />
                          </Col>

                          {/* Refund Remarks */}
                          <Col
                            xs={12}
                            md={12}
                            lg={6}
                            className="commTopButtonRightLeftPadding"
                          >
                            <TextAreaField
                              className="inputBox"
                              label="Refund Remarks"
                              name="refundRemarks"
                              value={formInputs.refundRemarks}
                              onChange={handleInputChange}
                              disabled={
                                isPCDisabled || !wantToRefund || !isPCDEditing
                              }
                            />
                          </Col>

                          {/* Refund Type */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <ComboboxField
                              label="Refund Type"
                              data={refundStatuses}
                              name="refundType"
                              textField="name"
                              setValue={formInputs.refundType}
                              getvalue={setDropdownData}
                              disabled={
                                isPCDisabled || !wantToRefund || !isPCDEditing
                              }
                            />
                          </Col>
                          <Row className="mb-3 mt-3">
                            <Col
                              xs={12}
                              className="d-flex align-items-center gap-3 ps-4"
                            >
                              {showChildLink && (
                                <div className="alert alert-info d-flex align-items-center mb-3">
                                  <i className="bi bi-arrow-right-circle me-2" />
                                  <span>
                                    This record was extended to record:{" "}
                                    <strong
                                      className="text-primary"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => {
                                        if (!formInputs?.childExtensionNr)
                                          return;
                                        dispatch(clearEquipmentRentNumbers());

                                        dispatch(
                                          setEquipmentRentExtDocumentNumber(
                                            formInputs?.childExtensionNr,
                                          ),
                                        );
                                        // navigate(CustomerConstants.CUSTOMER_V5_MR_EXTENSION_ERF_LIST);
                                      }}
                                    >
                                      {formInputs.childExtensionNr}
                                    </strong>
                                  </span>
                                </div>
                              )}

                              {/* {["CD", "FWP", "ITS", "ELCS", "PELCS"].includes(
                                formInputs.pcCallStatusCode,
                              ) &&
                                isActive && (
                                  <button
                                    className="btn btn-warning"
                                    onClick={handleUpdateEquipmentSTatus}
                                    disabled={disableUpdate || loading}
                                  >
                                    <i className="bi bi-arrow-clockwise me-2" />
                                    Update Status
                                  </button>
                                )} */}
                              {isPCDEditing &&
                                ["CD", "FWP", "ITS", "ELCS", "PELCS"].includes(
                                  formInputs.pcCallStatusCode,
                                ) &&
                                (isActive ||
                                  (isTerminal && isLatestExtension)) && (
                                  <button
                                    className="btn btn-warning"
                                    onClick={handleUpdateEquipmentSTatus}
                                    disabled={disableUpdate || loading}
                                  >
                                    <i className="bi bi-arrow-clockwise me-2" />
                                    Update Status
                                  </button>
                                )}

                              {!isPCDisabled && (
                                <button
                                  className="btn btn-success"
                                  onClick={handleMakeRefund}
                                  disabled={loading || !wantToRefund}
                                >
                                  <i className="bi bi-check-circle me-2" />
                                  Make Refund
                                </button>
                              )}

                              {/* Removed the '&& formInputs.refundAmountIndicator' from here */}
                              {false && canLoggedInUserClearRefundIndicator && (
                                <button
                                  className="btn btn-outline-danger ms-2"
                                  onClick={handleClearRefundIndicator}
                                  disabled={
                                    loading || !formInputs.refundAmountIndicator
                                  }
                                >
                                  <i className="bi bi-x-circle me-2" />
                                  Clear Refund Indicator
                                </button>
                              )}
                            </Col>
                          </Row>
                        </Row>

                        {isPCDUser && <hr className="my-4 text-muted" />}

                        <Row className="mb-2 mt-2">
                          {isPCDUser && (
                            <Col
                              xs={12}
                              className="mb-3 commTopButtonRightLeftPadding"
                            >
                              <h6 className="fw-bold text-dark mb-1">
                                Equipment Maintenance & Support of ERF
                              </h6>
                              <p
                                className=""
                                style={{ fontSize: "12px", color: "#0f477f" }}
                              >
                                Service Request can be created for latest Record
                                only
                              </p>
                            </Col>
                          )}
                          <Row>
                            {isPCDUser && !isExtended && (
                              <Col
                                xs={6}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding"
                              >
                                <label className="form-label fw-semibold">
                                  Service Action
                                </label>
                                <select
                                  className="form-control inputBox"
                                  name="serviceAction"
                                  value={formInputs.serviceAction || ""}
                                  onChange={handleInputChange}
                                >
                                  <option value="">-- None --</option>
                                  <option value="Service">Service</option>
                                  <option value="Replacement">
                                    Replacement
                                  </option>
                                </select>
                              </Col>
                            )}
                          </Row>
                          <Row className="mt-3">
                            <Col
                              xs={6}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding"
                            >
                              {isPCDUser &&
                                !isExtended &&
                                [
                                  "Service",
                                  "Replacement",
                                  "Service Completed",
                                  "Service Cancelled",
                                ].includes(formInputs.serviceAction) && (
                                  <button
                                    className="btn btn-info"
                                    onClick={handleSaveServiceAction}
                                  >
                                    <i className="bi bi-tools me-2" />
                                    Save Service Action
                                  </button>
                                )}
                            </Col>
                          </Row>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>
                {formInputs.erfType === "Nursing Service" && (
                  <div className="accordion mb-4" id="accordionNursing">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col xs={1} className="text-left arrowBtn">
                            <i
                              className={`bi ${isCardVisible5 ? "bi-chevron-down" : "bi-chevron-right"}`}
                              onClick={toggleCardVisibility5}
                            ></i>
                          </Col>

                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-center logoBtn"
                          >
                            <span className="iconLogo">NS</span>
                          </Col>

                          <Col xs={12} md={8} lg={8}>
                            <span className="headerTitle">Nursing Details</span>
                          </Col>
                        </Row>
                      </h2>

                      <div
                        className={`accordion-collapse collapse show ${isCardVisible5 ? "visible" : ""}`}
                      >
                        <div className="accordion-body">
                          <div className="mb-4">
                            <Row>
                              {/* Nurse Information Fields */}

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Name"
                                  name="nurseName"
                                  value={formInputs?.nurseName}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse User ID"
                                  name="nurseUserId"
                                  value={formInputs?.nurseUserId}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Associate Number"
                                  name="nurseAssociateNumber"
                                  value={formInputs?.nurseAssociateNumber}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Mobile Nr"
                                  name="nurseMobileNr"
                                  value={formInputs?.nurseMobileNr}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Email"
                                  name="nurseEmail"
                                  value={formInputs?.nurseEmail}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse PAN No"
                                  name="nursePanNo"
                                  value={formInputs?.nursePanNo}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Address"
                                  name="nurseAddress"
                                  value={formInputs?.nurseAddress}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Adress"
                                  name="nurseAddress"
                                  value={formInputs?.nurseAddress}
                                  onChange={handleInputChange}
                                  // type="number"
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Address 2"
                                  name="nurseAddress2"
                                  value={formInputs?.nurseAddress2}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse City"
                                  name="nurseCity"
                                  value={formInputs?.nurseCity}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Billing Address"
                                  name="nurseBillingAddress"
                                  value={formInputs?.nurseBillingAddress}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Billing State"
                                  name="nurseBillingState"
                                  value={formInputs?.nurseBillingState}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Billing Pin Code"
                                  name="nurseBillingPinCode"
                                  value={formInputs?.nurseBillingPinCode}
                                  onChange={handleInputChange}
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nursing Gross Total"
                                  name="nursingGrossTotal"
                                  value={formInputs?.nursingGrossTotal}
                                  onChange={handleInputChange}
                                  type="number"
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Service Provide Code"
                                  name="serviceProviderCode"
                                  value={formInputs?.serviceProviderCode}
                                  onChange={handleInputChange}
                                  // type="number"
                                  disabled={disabled}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Service Provider Name"
                                  name="serviceProviderName"
                                  value={formInputs?.serviceProviderName}
                                  onChange={handleInputChange}
                                  // type="number"
                                  disabled={disabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Erf Start Date"
                                  name="erfStartDate"
                                  value={formInputs?.erfStartDate}
                                  onChange={handleInputChange}
                                  // type="number"
                                  disabled={disabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Erf End Date"
                                  name="erfEndDate"
                                  value={formInputs?.erfEndDate}
                                  onChange={handleInputChange}
                                  // type="number"
                                  disabled={disabled}
                                />
                              </Col>
                              <Row className="mb-4">
                                <Col
                                  xs={12}
                                  className="commTopButtonRightLeftPadding mb-2"
                                >
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="replacementCheckbox"
                                      checked={
                                        formInputs?.replacement === "RPCM"
                                      }
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormInputs((prev) => ({
                                          ...prev,
                                          replacement: checked ? "RPCM" : "",
                                        }));
                                      }}
                                    />
                                    <label
                                      className="form-check-label fw-medium"
                                      htmlFor="replacementCheckbox"
                                    >
                                      Replacement (RPCM)
                                    </label>
                                  </div>
                                </Col>

                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Replacement Start Date"
                                    name="replacementStartDate"
                                    type="date"
                                    value={formInputs?.replacementStartDate}
                                    onChange={(e) => {
                                      handleInputChange(e);
                                    }}
                                    disabled={
                                      !isFormEnabled ||
                                      formInputs?.replacement !== "RPCM" ||
                                      !formInputs?.erfStartDate ||
                                      !formInputs?.erfEndDate
                                    }
                                    min={formInputs?.erfStartDate}
                                    max={formInputs?.erfEndDate}
                                  />
                                </Col>

                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Replacement End Date"
                                    name="replacementEndDate"
                                    type="date"
                                    value={formInputs?.replacementEndDate}
                                    onChange={(e) => {
                                      handleInputChange(e);
                                    }}
                                    disabled={
                                      !isFormEnabled ||
                                      formInputs?.replacement !== "RPCM" ||
                                      !formInputs?.erfStartDate ||
                                      !formInputs?.erfEndDate
                                    }
                                    min={formInputs?.erfStartDate}
                                    max={formInputs?.erfEndDate}
                                  />
                                </Col>

                                <Col
                                  xs={12}
                                  md={6}
                                  lg={4}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <ComboboxField
                                    label="Select Replacement Nurse *"
                                    data={erfNurseList}
                                    name="replacementNurseId"
                                    textField="name"
                                    setValue={
                                      formInputs?.replacementNurseName || ""
                                    }
                                    getvalue={(data) => {
                                      if (!data?.name || data.name === "Select")
                                        return;

                                      const selectedNurse = erfNurseList.find(
                                        (n) =>
                                          (n.hospitalName ||
                                            n.organisationName ||
                                            n.name) === data.name,
                                      );

                                      if (!selectedNurse) {
                                        toast.error("Nurse not found");
                                        return;
                                      }

                                      //  Save BOTH name and ID separately
                                      setFormInputs((prev) => ({
                                        ...prev,
                                        replacementNurseId:
                                          selectedNurse.userId ||
                                          selectedNurse.id,
                                        replacementNurseName:
                                          selectedNurse.hospitalName ||
                                          selectedNurse.organisationName ||
                                          selectedNurse.name,
                                        replacementNurseMobileNr:
                                          selectedNurse.mobileNr,
                                        replacementNurseUserId:
                                          selectedNurse.userId,
                                        replacementNurseAssociateNumber:
                                          selectedNurse.AssociateNumber,
                                        replacementNurseEmail:
                                          selectedNurse.email,
                                        replacementNurseAddress:
                                          selectedNurse.address,
                                      }));

                                      // Create date map for replacement dates
                                      if (
                                        formInputs?.replacementStartDate &&
                                        formInputs?.replacementEndDate
                                      ) {
                                        const startDate = new Date(
                                          formInputs.replacementStartDate,
                                        );
                                        const endDate = new Date(
                                          formInputs.replacementEndDate,
                                        );

                                        if (startDate > endDate) {
                                          toast.error(
                                            "Start date must be before end date",
                                          );
                                          return;
                                        }

                                        const dateMap = {};
                                        const currentDate = new Date(startDate);

                                        while (currentDate <= endDate) {
                                          const dateKey = currentDate
                                            .toISOString()
                                            .split("T")[0];
                                          dateMap[dateKey] = {
                                            nurseName:
                                              selectedNurse.hospitalName ||
                                              selectedNurse.organisationName ||
                                              selectedNurse.name,
                                            mobileNr: selectedNurse.mobileNr,
                                            userId: selectedNurse.userId,
                                            AssociateNumber:
                                              selectedNurse.AssociateNumber,
                                            email: selectedNurse.email,
                                            address: selectedNurse.address,
                                          };
                                          currentDate.setDate(
                                            currentDate.getDate() + 1,
                                          );
                                        }

                                        setReplacementNurseMap((prev) => ({
                                          ...prev,
                                          ...dateMap,
                                        }));
                                      }
                                    }}
                                    disabled={
                                      !isFormEnabled ||
                                      !formInputs?.replacementStartDate ||
                                      !formInputs?.replacementEndDate
                                    }
                                    isLoading={isLoadingErfNurse}
                                    placeholder="Select Replacement Nurse"
                                    required={true}
                                  />
                                </Col>
                              </Row>
                            </Row>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PRICE SCHEDULE (PRODUCT DETAILS) ACCORDION */}
                {formInputs.erfType !== "Nursing Service" && (
                  <div className="accordion mb-4" id="accordionProduct">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-left arrowBtn"
                          >
                            <i
                              className={`bi ${isCardVisible2 ? "bi-chevron-down" : "bi-chevron-right"}`}
                              onClick={toggleCardVisibility2}
                            ></i>
                          </Col>
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-center logoBtn"
                          >
                            <span className="iconLogo">P</span>
                          </Col>
                          <Col xs={12} md={8} lg={8}>
                            <span className="headerTitle">Product Details</span>
                          </Col>
                        </Row>
                      </h2>
                      <div
                        className={`accordion-collapse collapse show ${isCardVisible2 ? "visible" : ""}`}
                      >
                        <div className="accordion-body">
                          {/* RENT TABLE */}
                          <>
                            {(formInputs.erfType === "Rent" ||
                              formInputs.erfType === "Sale & Rent") && (
                              <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center bg-light p-2 border rounded-top">
                                  <Col
                                    xs={6}
                                    md={5}
                                    lg={7}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <h6 className="fw-bold  mb-0 text-primary ps-2">
                                      Rent Products
                                    </h6>
                                  </Col>
                                  {/* Restore Selected — visible during PCD edit + closure mode */}
                                  {isPCDEditing &&
                                    _isClosureMode &&
                                    canEditOnThisRecord && (
                                      <Col
                                        xs={3}
                                        md={3}
                                        lg={3}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <button
                                          type="button"
                                          className="btn btn-outline-warning btn-sm"
                                          onClick={handleRestoreSelected}
                                          disabled={
                                            restoreSelection.size === 0 ||
                                            loading
                                          }
                                          title={
                                            restoreSelection.size === 0
                                              ? "Select closed items to enable restore"
                                              : "Restore selected items to Open"
                                          }
                                        >
                                          <i className="bi bi-arrow-counterclockwise me-1" />
                                          Restore Selected (
                                          {restoreSelection.size})
                                        </button>
                                      </Col>
                                    )}
                                  <Col
                                    xs={3}
                                    md={4}
                                    lg={2}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <button
                                      className="btnTable"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addProductDetails("Rent");
                                      }}
                                      disabled={disabled || !isFormEnabled}
                                    >
                                      <i className="bi bi-plus-lg"></i> Add Rent
                                      Item
                                    </button>
                                  </Col>
                                </div>
                                <DataTable
                                  columns={getColumns("Rent")}
                                  data={getDataWithTotal(rentItems)}
                                  pagination
                                  paginationRowsPerPageOptions={
                                    Constants.ROW_PER_PAGE
                                  }
                                  noDataComponent={
                                    <div className="p-2 text-center text-muted">
                                      No Rent items.
                                    </div>
                                  }
                                  conditionalRowStyles={customStyles.rows}
                                />
                                <hr />
                              </div>
                            )}
                          </>

                          {/* SALE / SERVICE TABLE */}
                          {(formInputs.erfType === "Sale" ||
                            formInputs.erfType === "Sale & Rent" ||
                            formInputs.erfType === "Service") && (
                            <div className="mb-4">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <Col
                                  xs={8}
                                  md={8}
                                  lg={10}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <h6 className="fw-bold ps-2 text-primary">
                                    {formInputs.erfType === "Service"
                                      ? "Service Items"
                                      : "Sale Products"}
                                  </h6>
                                </Col>
                                <Col
                                  xs={4}
                                  md={4}
                                  lg={2}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <button
                                    className="btnTable"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addProductDetails(
                                        formInputs.erfType === "Sale & Rent"
                                          ? "Sale"
                                          : formInputs.erfType,
                                      );
                                    }}
                                    disabled={disabled || !isFormEnabled}
                                  >
                                    <i className="bi bi-plus-lg"></i> Add{" "}
                                    {formInputs.erfType === "Service"
                                      ? "Service"
                                      : "Sale"}{" "}
                                    Item
                                  </button>
                                </Col>
                              </div>
                              <DataTable
                                columns={getColumns(
                                  formInputs.erfType === "Service"
                                    ? "Service"
                                    : "Sale",
                                )}
                                data={getDataWithTotal(saleItems)}
                                pagination
                                paginationRowsPerPageOptions={
                                  Constants.ROW_PER_PAGE
                                }
                                noDataComponent={
                                  <div className="p-2 text-center text-muted">
                                    No items.
                                  </div>
                                }
                                conditionalRowStyles={customStyles.rows}
                              />
                            </div>
                          )}

                          <ERFExtensionAccessoriesSection
                            rentItems={rentItems.filter(
                              (item) => !item.isTotal,
                            )}
                            nursingItems={nursingItems.filter(
                              (item) => !item.isTotal,
                            )}
                            accessoryItems={accessoryItems}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Extension History Modal */}
                <Modal
                  show={showExtensionHistoryModal}
                  onHide={() => setShowExtensionHistoryModal(false)}
                  size="xl"
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      <i className="bi bi-clock-history me-2"></i>
                      Extension History - {selectedItemCode}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {selectedItemHistory && selectedItemHistory.length > 0 ? (
                      <DataTable
                        columns={extensionHistoryColumns}
                        data={selectedItemHistory}
                        pagination
                        paginationPerPage={5}
                        paginationRowsPerPageOptions={[5, 10, 15]}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent={
                          <div className="text-center text-muted py-4">
                            <i className="bi bi-inbox display-4 d-block mb-3"></i>
                            <p>No extension history available</p>
                          </div>
                        }
                      />
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-inbox display-4 d-block mb-3"></i>
                        <p>No extension history available</p>
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowExtensionHistoryModal(false)}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Close
                    </button>
                  </Modal.Footer>
                </Modal>
                {/* ── Bulk Restore Confirmation Dialog ── */}
                <Modal
                  show={restoreConfirmDialog.show}
                  onHide={() =>
                    setRestoreConfirmDialog({ show: false, data: null })
                  }
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      <i className="bi bi-arrow-counterclockwise me-2"></i>
                      Confirm Restore
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {restoreConfirmDialog.data && (
                      <>
                        <p className="mb-2">
                          Restore{" "}
                          <strong>{restoreConfirmDialog.data.count}</strong>{" "}
                          item(s)?
                        </p>
                        <div className="alert alert-info py-2 mb-2">
                          <strong>New Net Amount:</strong> ₹
                          {restoreConfirmDialog.data.newNetAmount}
                        </div>
                        {restoreConfirmDialog.data.becomingFullyOpen ? (
                          <p className="text-warning mb-0">
                            No closed items will remain — PC Call Status will be
                            cleared and the record returns to active rental
                            state. This will be saved to the backend.
                          </p>
                        ) : (
                          <p className="text-muted mb-0">
                            Closure data will be cleared for the selected items.
                            This will be saved to the backend.
                          </p>
                        )}
                      </>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <button
                      className="btn btn-secondary"
                      onClick={() =>
                        setRestoreConfirmDialog({ show: false, data: null })
                      }
                    >
                      <i className="bi bi-x-circle me-2"></i>Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleRestoreConfirmed}
                    >
                      <i className="bi bi-arrow-counterclockwise me-2"></i>
                      Confirm Restore
                    </button>
                  </Modal.Footer>
                </Modal>

                {/* ── Single Item Restore Confirmation Dialog ── */}
                <Modal
                  show={singleRestoreDialog.show}
                  onHide={() =>
                    setSingleRestoreDialog({ show: false, data: null })
                  }
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      <i className="bi bi-arrow-counterclockwise me-2"></i>
                      Restore Item to Open
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {singleRestoreDialog.data && (
                      <>
                        <p className="mb-2">
                          <strong>{singleRestoreDialog.data.itemName}</strong>{" "}
                          will be restored to Open.
                        </p>
                        <p className="mb-1">The following will be cleared:</p>
                        <ul className="mb-2">
                          {singleRestoreDialog.data.billLine && (
                            <li>{singleRestoreDialog.data.billLine}</li>
                          )}
                          {singleRestoreDialog.data.refundLine && (
                            <li>{singleRestoreDialog.data.refundLine}</li>
                          )}
                          {singleRestoreDialog.data.dateLine && (
                            <li>{singleRestoreDialog.data.dateLine}</li>
                          )}
                        </ul>
                        <div className="alert alert-info py-2 mb-0">
                          Original Amount (₹
                          {singleRestoreDialog.data.grossValue}) will be
                          restored and count fully toward Net Amount.
                        </div>
                      </>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <button
                      className="btn btn-secondary"
                      onClick={() =>
                        setSingleRestoreDialog({ show: false, data: null })
                      }
                    >
                      <i className="bi bi-x-circle me-2"></i>Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        singleRestoreDialog.data?.onConfirm?.();
                        setSingleRestoreDialog({ show: false, data: null });
                      }}
                    >
                      <i className="bi bi-arrow-counterclockwise me-2"></i>
                      Confirm Restore
                    </button>
                  </Modal.Footer>
                </Modal>

                {/* NURSING TABLE */}
                {/* NURSING PRODUCT DETAILS */}
                {formInputs.erfType === "Nursing Service" && (
                  <>
                    <div className="accordion mb-4" id="accordionNursing">
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <Row className="accordion-button">
                            <Col xs={1} className="text-left arrowBtn">
                              <i
                                className={`bi ${isCardVisible2 ? "bi-chevron-down" : "bi-chevron-right"}`}
                                onClick={toggleCardVisibility2}
                              ></i>
                            </Col>

                            <Col
                              xs={1}
                              md={1}
                              lg={1}
                              className="text-center logoBtn"
                            >
                              <span className="iconLogo">N</span>
                            </Col>

                            <Col xs={12} md={8} lg={8}>
                              <span className="headerTitle">
                                Nursing Details
                              </span>
                            </Col>
                          </Row>
                        </h2>

                        <div
                          className={`accordion-collapse collapse show ${isCardVisible2 ? "visible" : ""}`}
                        >
                          <div className="accordion-body">
                            <div className="mb-4">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <Col
                                  xs={6}
                                  md={5}
                                  lg={7}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <h6 className="fw-bold ps-2 text-primary">
                                    Nursing Items
                                  </h6>
                                </Col>
                                {/* Restore Selected — visible during PCD edit + closure mode */}
                                {isPCDEditing &&
                                  _isClosureMode &&
                                  canEditOnThisRecord && (
                                    <Col
                                      xs={3}
                                      md={3}
                                      lg={3}
                                      className="commTopButtonRightLeftPadding"
                                    >
                                      <button
                                        type="button"
                                        className="btn btn-outline-warning btn-sm"
                                        onClick={handleRestoreSelected}
                                        disabled={
                                          restoreSelection.size === 0 || loading
                                        }
                                        title={
                                          restoreSelection.size === 0
                                            ? "Select closed items to enable restore"
                                            : "Restore selected items to Open"
                                        }
                                      >
                                        <i className="bi bi-arrow-counterclockwise me-1" />
                                        Restore Selected (
                                        {restoreSelection.size})
                                      </button>
                                    </Col>
                                  )}

                                <Col
                                  xs={4}
                                  md={3}
                                  lg={2}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <button
                                    className="btnTable"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addProductDetails("Nursing");
                                    }}
                                    disabled={disabled || !isFormEnabled}
                                  >
                                    <i className="bi bi-plus-lg"></i> Add
                                    Nursing Item
                                  </button>
                                </Col>
                              </div>

                              <DataTable
                                columns={getNursingColumns("Nursing")} //  separate columns
                                data={getDataWithTotal(nursingItems)}
                                pagination
                                paginationRowsPerPageOptions={
                                  Constants.ROW_PER_PAGE
                                }
                                noDataComponent={
                                  <div className="p-2 text-center text-muted">
                                    No Nursing items.
                                  </div>
                                }
                                conditionalRowStyles={customStyles.rows}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* NURSING ATTENDANCE ACCORDION */}

                    <div
                      className="accordion mb-4"
                      id="accordionNursingAttendance"
                    >
                      <div
                        className="accordion-item"
                        style={{ border: "none", overflow: "hidden" }}
                      >
                        <h2 className="accordion-header">
                          <Row
                            className="accordion-button"
                            style={{
                              background: "#f8f9fa",
                              color: "#2c3e50",
                              borderBottom: "1px solid #e9ecef",
                            }}
                          >
                            <Col xs={1} className="text-left arrowBtn">
                              <i
                                className={`bi ${isCardVisible ? "bi-chevron-down" : "bi-chevron-right"}`}
                                onClick={toggleCardVisibility}
                              ></i>
                            </Col>

                            <Col
                              xs={1}
                              md={1}
                              lg={1}
                              className="text-center logoBtn"
                            >
                              <span className="iconLogo">NA</span>
                            </Col>
                            <Col xs={10}>
                              <span className="headerTitle">
                                Nursing Attendance
                              </span>
                              <span className="subHeaderTitle">
                                <i className="bi bi-calendar-check me-1"></i>
                                Daily Tracker
                              </span>
                            </Col>
                          </Row>
                        </h2>

                        <div
                          className={`accordion-collapse collapse show ${isCardVisible ? "visible" : ""}`}
                        >
                          <div
                            className="accordion-body p-0"
                            style={{ background: "#ffffff" }}
                          >
                            {(() => {
                              // Unified: always use nursingData for both EQ and EXT records
                              return (nursingData || []).length === 0 ? (
                                <div
                                  className="text-center py-5"
                                  style={{ padding: "40px" }}
                                >
                                  <i
                                    className="bi bi-clipboard2-x"
                                    style={{
                                      fontSize: "40px",
                                      color: "#dee2e6",
                                    }}
                                  ></i>
                                  <p
                                    className="text-muted mt-2"
                                    style={{ fontSize: "14px" }}
                                  >
                                    No Nursing items found.
                                  </p>
                                </div>
                              ) : (
                                Object.entries(
                                  (nursingData || []).reduce(
                                    (grouped, item) => {
                                      const dateKey = `${item.fromDate}|${item.toDate}`;

                                      if (!grouped[dateKey]) {
                                        grouped[dateKey] = {
                                          fromDate: item.fromDate,
                                          toDate: item.toDate,
                                          items: [],
                                          attendance: [],
                                        };
                                      }
                                      grouped[dateKey].items.push(item);
                                      if (!grouped[dateKey].attendance.length) {
                                        grouped[dateKey].attendance =
                                          item.attendance || [];
                                      }
                                      return grouped;
                                    },
                                    {},
                                  ),
                                ).map(([dateKey, groupData]) => {
                                  const {
                                    fromDate,
                                    toDate,
                                    items,
                                    attendance,
                                  } = groupData;

                                  const present = (attendance || []).filter(
                                    (d) => d.status === "Present",
                                  ).length;
                                  const absent = (attendance || []).filter(
                                    (d) => d.status === "Absent",
                                  ).length;
                                  const replacement = (attendance || []).filter(
                                    (d) =>
                                      d.status &&
                                      d.status.includes("Replacement"),
                                  ).length;
                                  const total = (attendance || []).length;
                                  const filled = (attendance || []).filter(
                                    (d) => d.status && d.status !== "",
                                  ).length;

                                  return (
                                    <div key={dateKey} className="mb-4">
                                      {/* Header Card - Professional */}
                                      <div
                                        style={{
                                          background: "#f8f9fa",
                                          borderBottom: "1px solid #e9ecef",
                                          padding: "14px 16px",
                                        }}
                                      >
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                          <div>
                                            <h6
                                              className="mb-2"
                                              style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#495057",
                                              }}
                                            >
                                              <i
                                                className="bi bi-calendar-range me-2"
                                                style={{ color: "#6c757d" }}
                                              ></i>
                                              {fromDate} — {toDate}
                                            </h6>

                                            <div
                                              style={{ marginBottom: "8px" }}
                                            >
                                              <small
                                                style={{
                                                  color: "#6c757d",
                                                  fontSize: "12px",
                                                }}
                                              >
                                                <i className="bi bi-box me-1"></i>
                                                Items ({items.length}):{" "}
                                                {items
                                                  .map((i) => i.materialCode)
                                                  .join(", ")}
                                              </small>
                                            </div>

                                            <small
                                              style={{
                                                color: "#6c757d",
                                                fontSize: "12px",
                                              }}
                                            >
                                              <i className="bi bi-calendar3 me-1"></i>
                                              {total} days • {filled} filled
                                            </small>
                                          </div>

                                          <div className="d-flex gap-2">
                                            <span
                                              style={{
                                                background: "#e8f5e9",
                                                color: "#2e7d32",
                                                borderRadius: "16px",
                                                padding: "4px 10px",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                              }}
                                            >
                                              ✓ {present}
                                            </span>
                                            <span
                                              style={{
                                                background: "#ffebee",
                                                color: "#c62828",
                                                borderRadius: "16px",
                                                padding: "4px 10px",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                              }}
                                            >
                                              ✗ {absent}
                                            </span>
                                            <span
                                              style={{
                                                background: "#fff3e0",
                                                color: "#ef6c00",
                                                borderRadius: "16px",
                                                padding: "4px 10px",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                              }}
                                            >
                                              ↻ {replacement}
                                            </span>
                                          </div>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mt-2">
                                          <div
                                            style={{
                                              background: "#e9ecef",
                                              borderRadius: "4px",
                                              height: "4px",
                                              width: "100%",
                                            }}
                                          >
                                            <div
                                              style={{
                                                background: "#0d6efd",
                                                width: `${total > 0 ? (filled / total) * 100 : 0}%`,
                                                height: "4px",
                                                borderRadius: "4px",
                                                transition: "width 0.3s ease",
                                              }}
                                            ></div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Update Button Section */}
                                      <div
                                        style={{
                                          background: "#ffffff",
                                          borderBottom: "1px solid #e9ecef",
                                          padding: "12px 16px",
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "13px",
                                            color: "#495057",
                                          }}
                                        >
                                          <i
                                            className="bi bi-pencil me-2"
                                            style={{ color: "#6c757d" }}
                                          ></i>
                                          Update daily attendance
                                        </span>

                                        <button
                                          className="btn btn-sm"
                                          onClick={handleUpdateAttendance}
                                          disabled={
                                            isUpdatingAttendance ||
                                            isAttendanceUpdated
                                          }
                                          style={{
                                            padding: "6px 16px",
                                            fontSize: "13px",
                                            fontWeight: "500",
                                            borderRadius: "6px",
                                            background: isAttendanceUpdated
                                              ? "#e8f5e9"
                                              : "#0d6efd",
                                            border: "none",
                                            color: isAttendanceUpdated
                                              ? "#2e7d32"
                                              : "#ffffff",
                                            minWidth: "140px",
                                          }}
                                        >
                                          {isUpdatingAttendance ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm me-2"></span>
                                              Saving...
                                            </>
                                          ) : isAttendanceUpdated ? (
                                            <>
                                              <i className="bi bi-check-circle me-2"></i>
                                              Updated
                                            </>
                                          ) : (
                                            <>
                                              <i className="bi bi-save me-2"></i>
                                              Update
                                            </>
                                          )}
                                        </button>
                                      </div>

                                      {/* Table */}
                                      <div
                                        style={{
                                          border: "none",
                                          background: "#ffffff",
                                        }}
                                      >
                                        <div
                                          style={{
                                            overflowX: "auto",
                                            maxHeight: "400px",
                                          }}
                                        >
                                          <table
                                            className="table table-sm mb-0"
                                            style={{
                                              fontSize: "13px",
                                              minWidth: "900px",
                                              borderCollapse: "collapse",
                                            }}
                                          >
                                            <thead
                                              style={{
                                                position: "sticky",
                                                top: 0,
                                                background: "#f8f9fa",
                                                zIndex: 1,
                                              }}
                                            >
                                              <tr>
                                                <th
                                                  style={{
                                                    width: "40px",
                                                    padding: "10px",
                                                    textAlign: "center",
                                                    color: "#6c757d",
                                                    fontWeight: "500",
                                                    borderBottom:
                                                      "1px solid #dee2e6",
                                                  }}
                                                >
                                                  #
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px",
                                                    color: "#495057",
                                                    fontWeight: "500",
                                                    borderBottom:
                                                      "1px solid #dee2e6",
                                                  }}
                                                >
                                                  Date
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px",
                                                    color: "#495057",
                                                    fontWeight: "500",
                                                    borderBottom:
                                                      "1px solid #dee2e6",
                                                  }}
                                                >
                                                  Day
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px",
                                                    color: "#495057",
                                                    fontWeight: "500",
                                                    borderBottom:
                                                      "1px solid #dee2e6",
                                                  }}
                                                >
                                                  Select Status
                                                </th>
                                                <th
                                                  style={{
                                                    padding: "10px",
                                                    color: "#495057",
                                                    fontWeight: "500",
                                                    borderBottom:
                                                      "1px solid #dee2e6",
                                                  }}
                                                >
                                                  Updated Status
                                                </th>
                                                <th
                                                  style={{
                                                    width: "50px",
                                                    padding: "10px",
                                                    textAlign: "center",
                                                    color: "#495057",
                                                    fontWeight: "500",
                                                    borderBottom:
                                                      "1px solid #dee2e6",
                                                  }}
                                                >
                                                  View
                                                </th>
                                              </tr>
                                            </thead>

                                            <tbody>
                                              {(attendance || []).map(
                                                (dayRow, index) => {
                                                  const rowKey = `${dateKey}-${index}`;
                                                  const isRowSubmitted =
                                                    !!dayRow?.status;
                                                  const isEditing =
                                                    editingRows?.[rowKey];
                                                  const isWeekend =
                                                    dayRow.dayName ===
                                                      "Saturday" ||
                                                    dayRow.dayName === "Sunday";

                                                  const isInReplacementRange =
                                                    formInputs?.replacementStartDate &&
                                                    formInputs?.replacementEndDate &&
                                                    dayRow.date >=
                                                      formInputs?.replacementStartDate &&
                                                    dayRow.date <=
                                                      formInputs?.replacementEndDate;

                                                  const statusColor = {
                                                    Present: {
                                                      bg: "#f1f9f0",
                                                      color: "#1e7b4c",
                                                      border: "#c3e6cb",
                                                    },
                                                    Absent: {
                                                      bg: "#fef2f2",
                                                      color: "#b53b3b",
                                                      border: "#f5c6cb",
                                                    },
                                                    "Replacement Present": {
                                                      bg: "#fef6e6",
                                                      color: "#a4661e",
                                                      border: "#ffe8b5",
                                                    },
                                                    "Replacement Absent": {
                                                      bg: "#fef2f2",
                                                      color: "#b53b3b",
                                                      border: "#f5c6cb",
                                                    },
                                                  }[dayRow.status] || {
                                                    bg: "#ffffff",
                                                    color: "#6c757d",
                                                    border: "#dee2e6",
                                                  };

                                                  return (
                                                    <React.Fragment
                                                      key={rowKey}
                                                    >
                                                      <tr
                                                        style={{
                                                          background:
                                                            statusColor.bg,
                                                        }}
                                                      >
                                                        <td
                                                          style={{
                                                            padding: "8px",
                                                            textAlign: "center",
                                                            color: "#adb5bd",
                                                            borderBottom:
                                                              "1px solid #e9ecef",
                                                          }}
                                                        >
                                                          {index + 1}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "8px",
                                                            borderBottom:
                                                              "1px solid #e9ecef",
                                                          }}
                                                        >
                                                          <span
                                                            style={{
                                                              color: "#212529",
                                                            }}
                                                          >
                                                            {dayRow.date}
                                                          </span>
                                                          {isInReplacementRange && (
                                                            <span
                                                              style={{
                                                                background:
                                                                  "#fff3e0",
                                                                color:
                                                                  "#ef6c00",
                                                                fontSize:
                                                                  "10px",
                                                                padding:
                                                                  "2px 6px",
                                                                borderRadius:
                                                                  "4px",
                                                                marginLeft:
                                                                  "6px",
                                                                fontWeight:
                                                                  "500",
                                                              }}
                                                            >
                                                              ↻ Replacement
                                                            </span>
                                                          )}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "8px",
                                                            color: isWeekend
                                                              ? "#adb5bd"
                                                              : "#495057",
                                                            borderBottom:
                                                              "1px solid #e9ecef",
                                                          }}
                                                        >
                                                          {dayRow.dayName}
                                                        </td>
                                                        {/* Select Status */}
                                                        <td
                                                          style={{
                                                            padding: "4px 8px",
                                                            borderBottom:
                                                              "1px solid #e9ecef",
                                                          }}
                                                        >
                                                          {isRowSubmitted &&
                                                          !isEditing ? (
                                                            <div className="d-flex align-items-center gap-1">
                                                              <span
                                                                style={{
                                                                  background:
                                                                    "#e9ecef",
                                                                  padding:
                                                                    "4px 8px",
                                                                  borderRadius:
                                                                    "4px",
                                                                  fontSize:
                                                                    "12px",
                                                                  color:
                                                                    "#6c757d",
                                                                }}
                                                              >
                                                                <i className="bi bi-check2 me-1"></i>
                                                                Submitted
                                                              </span>
                                                              <button
                                                                className="btn btn-sm"
                                                                onClick={() =>
                                                                  handleEnableEdit?.(
                                                                    dateKey,
                                                                    index,
                                                                  )
                                                                }
                                                                style={{
                                                                  padding:
                                                                    "2px 6px",
                                                                  fontSize:
                                                                    "11px",
                                                                  border:
                                                                    "1px solid #dee2e6",
                                                                  background:
                                                                    "white",
                                                                  borderRadius:
                                                                    "4px",
                                                                }}
                                                              >
                                                                Edit
                                                              </button>
                                                            </div>
                                                          ) : (
                                                            <select
                                                              className="form-select form-select-sm"
                                                              value={
                                                                dayRow.status ||
                                                                ""
                                                              }
                                                              onChange={(e) =>
                                                                handleAttendanceChangeMultiple?.(
                                                                  items,
                                                                  index,
                                                                  "status",
                                                                  e.target
                                                                    .value,
                                                                )
                                                              }
                                                              onBlur={() =>
                                                                handleDisableEdit?.(
                                                                  dateKey,
                                                                  index,
                                                                )
                                                              }
                                                              style={{
                                                                border: `1px solid ${statusColor.border}`,
                                                                background:
                                                                  statusColor.bg,
                                                                fontSize:
                                                                  "12px",
                                                                padding:
                                                                  "4px 24px 4px 8px",
                                                                borderRadius:
                                                                  "4px",
                                                                width: "auto",
                                                                minWidth:
                                                                  "130px",
                                                              }}
                                                            >
                                                              <option value="">
                                                                Select
                                                              </option>
                                                              <option value="Present">
                                                                ✓ Present
                                                              </option>
                                                              <option value="Absent">
                                                                ✗ Absent
                                                              </option>
                                                              <option value="Replacement Present">
                                                                ↻ Replacement
                                                                Present
                                                              </option>
                                                              <option value="Replacement Absent">
                                                                ↻ Replacement
                                                                Absent
                                                              </option>
                                                            </select>
                                                          )}
                                                        </td>
                                                        {/* Updated Status */}
                                                        <td
                                                          style={{
                                                            padding: "8px",
                                                            borderBottom:
                                                              "1px solid #e9ecef",
                                                          }}
                                                        >
                                                          {dayRow.status ? (
                                                            <span
                                                              style={{
                                                                display:
                                                                  "inline-block",
                                                                padding:
                                                                  "4px 10px",
                                                                borderRadius:
                                                                  "4px",
                                                                fontSize:
                                                                  "12px",
                                                                fontWeight:
                                                                  "500",
                                                                background:
                                                                  statusColor.bg,
                                                                color:
                                                                  statusColor.color,
                                                                border: `1px solid ${statusColor.border}`,
                                                              }}
                                                            >
                                                              {dayRow.status ===
                                                              "Present"
                                                                ? "✓ Present"
                                                                : dayRow.status ===
                                                                    "Absent"
                                                                  ? "✗ Absent"
                                                                  : dayRow.status ===
                                                                      "Replacement Present"
                                                                    ? "↻ Replacement"
                                                                    : dayRow.status ===
                                                                        "Replacement Absent"
                                                                      ? "↻ Replacement Absent"
                                                                      : dayRow.status}
                                                            </span>
                                                          ) : (
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#adb5bd",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              —
                                                            </span>
                                                          )}
                                                        </td>
                                                        {/* View Button - Opens Modal */}
                                                        <td
                                                          style={{
                                                            padding: "8px",
                                                            textAlign: "center",
                                                            borderBottom:
                                                              "1px solid #e9ecef",
                                                          }}
                                                        >
                                                          <button
                                                            className="btn btn-sm"
                                                            onClick={() => {
                                                              // isReplacement: from saved day data (persists across reloads)
                                                              // also fallback: check status string for in-session marking
                                                              const isReplacement =
                                                                dayRow.isReplacement ===
                                                                  true ||
                                                                dayRow.status ===
                                                                  "Replacement Present" ||
                                                                dayRow.status ===
                                                                  "Replacement Absent";

                                                              // Read replacement nurse from the day row itself (saved by backend)
                                                              // Fall back to current form fields for in-session (not-yet-saved) replacements
                                                              const replacementDetails =
                                                                isReplacement
                                                                  ? {
                                                                      nurseName:
                                                                        dayRow.nurseName ||
                                                                        formInputs?.replacementNurseName,
                                                                      mobileNr:
                                                                        dayRow.nurseMobileNr ||
                                                                        formInputs?.replacementNurseMobileNr,
                                                                      userId:
                                                                        dayRow.nurseUserId ||
                                                                        formInputs?.replacementNurseUserId,
                                                                      AssociateNumber:
                                                                        dayRow.nurseAssociateNumber ||
                                                                        formInputs?.replacementNurseAssociateNumber,
                                                                      email:
                                                                        dayRow.nurseEmail ||
                                                                        formInputs?.replacementNurseEmail,
                                                                      address:
                                                                        dayRow.nurseAddress ||
                                                                        formInputs?.replacementNurseAddress,
                                                                    }
                                                                  : null;

                                                              setViewDetailsModal(
                                                                {
                                                                  show: true,
                                                                  data: dayRow,
                                                                  dateStr:
                                                                    dayRow.date,
                                                                  isReplacement:
                                                                    isReplacement,
                                                                  replacementNurseDetails:
                                                                    replacementDetails,
                                                                },
                                                              );
                                                            }}
                                                            style={{
                                                              padding:
                                                                "2px 8px",
                                                              fontSize: "12px",
                                                              border:
                                                                "1px solid #dee2e6",
                                                              background:
                                                                viewDetailsModal?.show &&
                                                                viewDetailsModal?.dateStr ===
                                                                  dayRow.date
                                                                  ? "#e7f1ff"
                                                                  : "white",
                                                              borderRadius:
                                                                "4px",
                                                            }}
                                                          >
                                                            <i
                                                              className="bi bi-eye"
                                                              style={{
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            ></i>
                                                          </button>
                                                        </td>
                                                      </tr>
                                                    </React.Fragment>
                                                  );
                                                },
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* PAYMENT DETAILS */}
                <div className="accordion mb-4" id="accordionPayment">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row
                        className="accordion-button"
                        onClick={() => toggleVisibility("PAY")}
                      >
                        <Col xs={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${visible.PAY ? "bi-chevron-down" : "bi-chevron-right"}`}
                          ></i>
                        </Col>
                        <Col xs={11}>
                          <span className="headerTitle">Payment Details</span>
                        </Col>
                      </Row>
                    </h2>
                    <div
                      className={`accordion-collapse collapse show ${visible.PAY ? "visible" : ""}`}
                    >
                      <div className="accordion-body">
                        <Row>
                          <Col md={6} className="pe-md-4">
                            <h6 className="text-primary mb-3 mt-3 ps-2">
                              Payment Details
                            </h6>
                            <Row className="mb-3">
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                {/* <InputField
                                  className="inputBox"
                                  label="Total Amount"
                                  value={totalPayable}
                                  disabled
                                /> */}
                                <InputField
                                  className="inputBox"
                                  label="Total Amount"
                                  value={totalPayable}
                                  disabled
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Cartage"
                                  name="cartage"
                                  onChange={handleInputChange}
                                  type="number"
                                  value={
                                    _isExtMode && isPCDEditing
                                      ? "0"
                                      : formInputs.cartage
                                  }
                                  disabled={
                                    (_isExtMode && isPCDEditing) ||
                                    disabled ||
                                    !isFormEnabled ||
                                    (
                                      PRIVILEGE_SETTINGS[
                                        formInputs.privilegeCase
                                      ] || DEFAULT_PRIVILEGE_SETTINGS
                                    ).disableCartage
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Installation Charges"
                                  name="installationCharges"
                                  onChange={handleInputChange}
                                  value={
                                    _isExtMode && isPCDEditing
                                      ? "0"
                                      : formInputs.installationCharges
                                  }
                                  type="number"
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Net Amount"
                                  value={netAmount}
                                  disabled
                                />
                                <small
                                  className="text-muted d-block mt-1"
                                  style={{ fontSize: "10px" }}
                                >
                                  <i className="bi bi-info-circle me-1" />
                                  *This includes visit charges.
                                </small>
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Enter Received Amount"
                                  name="receivedAmount"
                                  onChange={handleInputChange}
                                  value={formInputs.receivedAmount}
                                  type="number"
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Balance Amount"
                                  value={balanceAmount}
                                  disabled
                                />
                              </Col>
                            </Row>
                          </Col>
                          <Col md={6} className="ps-md-4 border-start-md">
                            <h6 className="text-primary mb-3 mt-3 ps-2">
                              Received Amt. Breakup
                            </h6>
                            <Row className="mb-3">
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <ComboboxField
                                  label="Payment Mode"
                                  data={paymentModeDropdown}
                                  name="paymentMode"
                                  textField="name"
                                  setValue={formInputs.paymentMode}
                                  getvalue={setDropdownData}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Cash"
                                  name="cashAmount"
                                  onChange={handleInputChange}
                                  value={formInputs.cashAmount}
                                  type="number"
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableCash ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <Row>
                                  <Col md={4}>
                                    <label className="mt-2">
                                      {/* Dynamic Label (Req 3) */}
                                      {formInputs.paymentMode === "Card&Cash"
                                        ? "Card"
                                        : formInputs.paymentMode ===
                                            "Cheque&Cash"
                                          ? "Cheque"
                                          : formInputs.paymentMode ===
                                              "Card&Cheque"
                                            ? "Cheque"
                                            : "Bal. Amt"}
                                    </label>
                                  </Col>
                                  <Col md={8}>
                                    <InputField
                                      className="inputBox"
                                      name="balanceAmtBreakup"
                                      value={formInputs.balanceAmtBreakup}
                                      disabled
                                    />
                                  </Col>
                                </Row>
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Cheque No."
                                  name="chequeNo"
                                  onChange={handleInputChange}
                                  value={formInputs.chequeNo}
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableCheque ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Card Trans. No."
                                  name="cardTransNo"
                                  onChange={handleInputChange}
                                  value={formInputs.cardTransNo}
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableCard ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Bank Name"
                                  name="bankName"
                                  onChange={handleInputChange}
                                  value={formInputs.bankName}
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableBank ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Receipt No."
                                  name="receiptNo"
                                  onChange={handleInputChange}
                                  value={formInputs.receiptNo}
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableReceipt ||
                                    (
                                      PAYMENT_MODE_RULES[
                                        formInputs.paymentMode
                                      ] || {}
                                    ).disableAll
                                  }
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECURITY DETAILS — hidden for Hospital ERFs */}
                {(formInputs.erfType === "Rent" ||
                  formInputs.erfType === "Sale & Rent") &&
                  erfForType !== "Hospital" &&
                  !_isExtensionRecord && (
                    <div className="accordion mb-4" id="accordionSecurity">
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <Row
                            className="accordion-button"
                            onClick={() => toggleVisibility("SEC")}
                          >
                            <Col xs={1} className="text-left arrowBtn">
                              <i
                                className={`bi ${visible.SEC ? "bi-chevron-down" : "bi-chevron-right"}`}
                              ></i>
                            </Col>
                            <Col xs={11}>
                              <span className="headerTitle">
                                Security Details
                              </span>
                            </Col>
                          </Row>
                        </h2>
                        <div
                          className={`accordion-collapse collapse show ${visible.SEC ? "visible" : ""}`}
                        >
                          <div className="accordion-body">
                            <Row>
                              <Col md={6} className="pe-md-4">
                                <Row className="mb-3">
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Standard Security Amount"
                                      value={formInputs.standardSecurityAmt}
                                      disabled
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Total Security Amount"
                                      value={totals.security.toFixed(2)}
                                      disabled={true}
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Enter Recd. Security Amount"
                                      name="receivedSecurityAmt"
                                      onChange={handleInputChange}
                                      value={formInputs.receivedSecurityAmt}
                                      type="number"
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Bal. Security Amount"
                                      value={balanceSecurityAmt}
                                      disabled
                                    />
                                  </Col>
                                </Row>
                              </Col>
                              <Col md={6} className="ps-md-4 border-start-md">
                                <Row className="mb-3">
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Enter Pincode"
                                      name="securityPincode"
                                      value={formInputs.securityPincode}
                                      onChange={handleInputChange}
                                      disabled={disabled || !isFormEnabled}
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <ComboboxField
                                      label="Security Amt. Mode"
                                      data={(() => {
                                        // Filter options based on Privilege Case settings
                                        const settings =
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS;
                                        if (settings.securityAmtModeOptions) {
                                          return paymentModeDropdown.filter(
                                            (opt) =>
                                              settings.securityAmtModeOptions.includes(
                                                opt.name,
                                              ),
                                          );
                                        }
                                        return paymentModeDropdown;
                                      })()}
                                      name="securityAmtMode"
                                      textField="name"
                                      setValue={formInputs.securityAmtMode}
                                      getvalue={setDropdownData}
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        !formInputs.securityPincode
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Cash Amt Received"
                                      name="securityCashAmt"
                                      onChange={handleInputChange}
                                      value={formInputs.securityCashAmt}
                                      type="number"
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        (
                                          PAYMENT_MODE_RULES[
                                            formInputs.securityAmtMode
                                          ] || {}
                                        ).disableCash ||
                                        (
                                          PAYMENT_MODE_RULES[
                                            formInputs.securityAmtMode
                                          ] || {}
                                        ).disableAll
                                      }
                                    />
                                  </Col>

                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <Row>
                                      <Col md={4}>
                                        <label className="mt-2">
                                          {formInputs.securityAmtMode ===
                                          "Card&Cash"
                                            ? "Card"
                                            : formInputs.securityAmtMode ===
                                                "Cheque&Cash"
                                              ? "Cheque"
                                              : formInputs.securityAmtMode ===
                                                  "Card&Cheque"
                                                ? "Cheque"
                                                : "Bal. Amt"}
                                        </label>
                                      </Col>
                                      <Col md={8}>
                                        <InputField
                                          className="inputBox"
                                          name="balanceSecurityBreakup"
                                          value={
                                            formInputs.balanceSecurityBreakup
                                          }
                                          disabled
                                        />
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Cheque No."
                                      name="securityChequeNo"
                                      onChange={handleInputChange}
                                      value={formInputs.securityChequeNo}
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        (
                                          PAYMENT_MODE_RULES[
                                            formInputs.securityAmtMode
                                          ] || {}
                                        ).disableCheque ||
                                        (
                                          PAYMENT_MODE_RULES[
                                            formInputs.securityAmtMode
                                          ] || {}
                                        ).disableAll
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Card Trans. No."
                                      name="securityCardTransNo"
                                      onChange={handleInputChange}
                                      value={formInputs.securityCardTransNo}
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        (
                                          PAYMENT_MODE_RULES[
                                            formInputs.securityAmtMode
                                          ] || {}
                                        ).disableCard ||
                                        (
                                          PAYMENT_MODE_RULES[
                                            formInputs.securityAmtMode
                                          ] || {}
                                        ).disableAll
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Bank Name"
                                      name="securityBankName"
                                      onChange={handleInputChange}
                                      value={formInputs.securityBankName}
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        (
                                          PAYMENT_MODE_RULES[
                                            formInputs.securityAmtMode
                                          ] || {}
                                        ).disableBank ||
                                        (
                                          PAYMENT_MODE_RULES[
                                            formInputs.securityAmtMode
                                          ] || {}
                                        ).disableAll
                                      }
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Security Mode Would be — hidden for Hospital ERFs and Extension records */}
                {(formInputs.erfType === "Rent" ||
                  formInputs.erfType === "Sale & Rent") &&
                  erfForType !== "Hospital" &&
                  !_isExtensionRecord && (
                    <div className="accordion mb-4" id="accordionSecurityMode">
                      <div className="accordion-item">
                        <div className="accordion-body p-4">
                          <h6 className="text-primary mb-3">
                            Security Mode Would be
                          </h6>
                          <Row className="align-items-center">
                            <Col
                              md={3}
                              className="commTopButtonRightLeftPadding"
                            >
                              <ComboboxField
                                label="Security Mode"
                                data={paymentModeDropdown.filter(
                                  (m) => m.name !== "NR",
                                )}
                                name="securityMode"
                                textField="name"
                                setValue={formInputs.securityMode}
                                getvalue={setDropdownData}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              md={3}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label="Cash Amt."
                                name="securityModeCash"
                                onChange={handleInputChange}
                                value={formInputs.securityModeCash}
                                type="number"
                                disabled={
                                  disabled ||
                                  !isFormEnabled ||
                                  !["Card&Cash", "Cheque&Cash"].includes(
                                    formInputs.securityMode,
                                  )
                                }
                              />
                            </Col>
                            <Col
                              md={3}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label="Bal. Sec. Amt."
                                value={(() => {
                                  // Point 4, 25, 26, 27
                                  if (
                                    ["Cash", "Euro", "Dollar"].includes(
                                      formInputs.securityMode,
                                    )
                                  ) {
                                    return 0;
                                  }
                                  if (
                                    [
                                      "Card",
                                      "Cheque",
                                      "Online",
                                      "Paytm",
                                      "UPI",
                                      "Card&Cheque",
                                    ].includes(formInputs.securityMode)
                                  ) {
                                    return balanceSecurityAmt;
                                  }
                                  // Else (Card&Cash, Cheque&Cash) - calculated via useEffect/State, displayed here
                                  return formInputs.securityModeBal;
                                })()}
                                disabled
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Remarks */}
                <div className="mb-4 p-4 border rounded bg-white">
                  <TextAreaField
                    className="inputBox"
                    label="Remarks"
                    name="remarks"
                    value={formInputs.remarks}
                    onChange={handleInputChange}
                    disabled={disabled || !isFormEnabled}
                  />
                </div>

                <div className="pageFooter mt-4 mb-4 p-3">
                  <div className="d-flex justify-content-end align-items-center gap-3">
                    {isPCDEditing &&
                      formInputs.extensionLifecycleStatus === "EXTENDED" &&
                      childIsLeaf && (
                        <button
                          className="btn btn-outline-secondary"
                          style={{ minWidth: "180px", padding: "10px 20px" }}
                          onClick={handleAddItemsToExtension}
                          disabled={loading || !hasReopenedItemsThisSession}
                        >
                          <i className="bi bi-patch-plus me-2" />
                          Add Items to Extension
                        </button>
                      )}
                    {isPCDEditing &&
                      ["EXT", "PEXT"].includes(formInputs.pcCallStatusCode) && (
                        <button
                          className="btn btn-primary shadow-sm"
                          style={{ minWidth: "180px", padding: "10px 20px" }}
                          onClick={() => handleUpdateExtension()}
                          disabled={disableCreateExtension}
                        >
                          <i className="bi bi-plus-circle me-2" />
                          Create Extension
                        </button>
                      )}
                  </div>
                </div>
                {/* Refund History */}
                {formInputs.refundHistory?.length > 0 && (
                  <div className="accordion mb-4" id="accordionRefundHistory">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col xs={1} className="text-left arrowBtn">
                            <i className="bi bi-chevron-down" />
                          </Col>
                          <Col xs={1} className="text-center logoBtn">
                            <span className="iconLogo">RH</span>
                          </Col>
                          <Col xs={12} md={7}>
                            <span className="headerTitle">Refund History</span>
                            <span className="subHeaderTitle">
                              All refund entries for this ERF
                            </span>
                          </Col>
                          <Col
                            xs={12}
                            md={3}
                            className="headerButtonAlign"
                          ></Col>
                        </Row>
                      </h2>
                      <div className="accordion-collapse collapse show visible">
                        <div className="accordion-body">
                          <DataTable
                            columns={refundHistoryColumns}
                            data={[
                              ...(formInputs.refundHistory || []),
                            ].reverse()}
                            pagination
                            noDataComponent={
                              <p className="text-muted p-3">
                                No refund entries yet
                              </p>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* {documentNumber && (
                  <ERFCreditNoteSection
                    documentNumber={documentNumber}
                    isExtension={
                      formInputs.documentNumber?.startsWith("EX") ||
                      formInputs.documentNumber?.includes("ERFEXT")
                    }
                  />
                )} */}

                {/* {(() => {
                  const currentDocNr =
                    formInputs.documentNumber || documentNumber || equipmentNr;
                  if (!currentDocNr) return null;

                  const isExtensionRecord =
                    currentDocNr.startsWith("EX") ||
                    currentDocNr.includes("ERFEXT");

                  if (isExtensionRecord) {
                    return (
                      <ERFExtensionPaymentSection
                        key={currentDocNr}
                        documentNumber={currentDocNr}
                        allowAddBilling={true}
                        allowAddSecurity={false}
                        allowAddCreditNote={isSalesAdmin || isPCDUser}
                        allowUpdatePayentButton={isSalesAdmin || isPCDUser}
                      />
                    );
                  }

                  // Original ERF: full payment + security
                  return (
                    <ERFPaymentSection
                      key={currentDocNr}
                      documentNumber={currentDocNr}
                      allowAddBilling={true}
                      allowAddSecurity={true}
                      allowAddCreditNote={isSalesAdmin || isPCDUser}
                      allowUpdatePayentButton={isSalesAdmin || isPCDUser}
                    />
                  );
                })()} */}

                {(() => {
                  const currentDocNr =
                    formInputs.documentNumber || documentNumber || equipmentNr;
                  if (!currentDocNr) return null;

                  const isExtensionRecord =
                    currentDocNr.startsWith("EX") ||
                    currentDocNr.includes("ERFEXT");

                  // Hide credit note for extension records and for ERFs that
                  // already have a child extension (product is at customer site)
                  const showCreditNote =
                    documentNumber &&
                    !isExtensionRecord &&
                    !formInputs.childExtensionNr;

                  return (
                    <>
                      {showCreditNote && (
                        <ERFCreditNoteSection documentNumber={documentNumber} />
                      )}

                      {isExtensionRecord ? (
                        <ERFExtensionPaymentSection
                          // key={currentDocNr}
                          key={`ERFExtensionPayment-${currentDocNr}`}
                          documentNumber={currentDocNr}
                          allowAddBilling={true}
                          allowAddSecurity={false}
                          allowAddCreditNote={isSalesAdmin || isPCDUser}
                          allowUpdatePayentButton={isSalesAdmin || isPCDUser}
                        />
                      ) : (
                        <ERFPaymentSection
                          // key={currentDocNr}
                          key={`ERFPayment-${currentDocNr}`}
                          documentNumber={currentDocNr}
                          allowAddBilling={true}
                          allowAddSecurity={true}
                          allowAddCreditNote={isSalesAdmin || isPCDUser}
                          allowUpdatePayentButton={isSalesAdmin || isPCDUser}
                        />
                      )}

                      <ERFTravelSection
                        travelHistory={travelHistory}
                        erfStatusCode={formInputs.erfStatusCode ?? ""}
                        onSave={handleTravelSave}
                        onConfirmRow={handleTravelConfirmRow}
                        disabled={false}
                      />

                      <ERFTallyStatusSection
                        key={`tally-${currentDocNr}`}
                        documentNumber={currentDocNr}
                        isExtension={isExtensionRecord}
                      />
                    </>
                  );
                })()}

                {/* {documentNumber && (
                  <ERFTallyStatusSection
                    documentNumber={documentNumber}
                    isExtension={
                      formInputs.documentNumber?.startsWith("EX") ||
                      formInputs.documentNumber?.includes("ERFEXT")
                    }
                  />
                )} */}
              </>
            )}
          </div>
        </Col>
      </Row>

      <MaterialSelectionModalV5
        show={modalShow}
        onHide={hideModal}
        onSelectedData={handleGetSelectedMaterial}
      />
      <FailureModal
        show={getShowFailureModal}
        onHide={hideFailureModal}
        failuremodaldata={failureModalData}
        requiredfields={getErrorFields}
      />
      <UpdateRemarksModal
        show={updateModalShow}
        onHide={() => updateHideModal()}
        onvaluechange={handleValueChange}
      />
      <CommonItemLevelDatatableModal
        show={itemLevelModalShow}
        onHide={() => itemLevelHideModal()}
        columns={getItemLevelColumns}
        data={getItemLevelData}
        title={getModalTitle}
      />
      <ErfExtenssionModal
        show={getModalShowExtenssionModal}
        onHide={hideModalExtenssionModal}
        data={getExtenssionData}
        onSave={handleSaveExtensions}
      />
      {/* Attendance Details Modal */}
      <AttendanceDetailsModal
        show={viewDetailsModal?.show || false}
        onHide={() => {
          setViewDetailsModal({
            show: false,
            data: null,
            dateStr: null,
            isReplacement: false,
            replacementNurseDetails: null,
          });
        }}
        data={viewDetailsModal?.data}
        dateStr={viewDetailsModal?.dateStr}
        getNurseDetailsForDate={getNurseDetailsForDate}
        formInputs={formInputs}
        isReplacement={viewDetailsModal?.isReplacement || false}
        replacementNurseDetails={viewDetailsModal?.replacementNurseDetails}
      />
    </main>
  );
};

export default ERFExtensionDetail;
